import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { readReportTaskFilter, reportDateRange, reportFilterQuery, saveReportTaskFilter } from "./reportFilters";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BellRing,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock3,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  FileDown,
  KeyRound,
  ListChecks,
  Loader2,
  MapPin,
  Pencil,
  Play,
  Power,
  PowerOff,
  Plus,
  RefreshCw,
  Route as RouteIcon,
  Search,
  ShieldAlert,
  Trash2,
  Truck,
  Upload,
  Users,
  X,
} from "lucide-react";
import { api, del, patch, post, put } from "./api";
import { DataTable } from "./DataTable";
import { selectedKey } from "./App";
import type {
  Account,
  AdminUser,
  AuditLog,
  BatchPreview,
  CurrentUser,
  DailyReport,
  DailyReportListItem,
  DailyReportDaySummary,
  DailyReportDriverDetail,
  DriverGroup,
  FeishuConfig,
  FeishuManagerConfig,
  Job,
  PagedResponse,
  ProxyBalance,
  ProxyConfig,
  ProxyResources,
  RouteAutoPlan,
  RouteLeg,
  RouteRun,
  RouteRunDetail,
  RouteTemplate,
  Waybill,
  WaybillStatusGroup,
} from "./types";
import {
  Badge,
  Button,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  IconButton,
  Input,
  LoadingState,
  Pagination,
  Select,
} from "./ui";

type Navigate = (page: "accounts" | "waybills" | "batch" | "jobs") => void;

const WAYBILL_GROUP_LABELS: Record<WaybillStatusGroup, string> = {
  all: "全部运单",
  pendingAccept: "待接单",
  pendingLoad: "待装货",
  inTransit: "运输中",
  unloaded: "已卸货",
  signed: "已签收",
  cancelled: "已取消",
  other: "其他状态",
};

function waybillStatusGroup(value: string | null): WaybillStatusGroup {
  return value && value in WAYBILL_GROUP_LABELS
    ? (value as WaybillStatusGroup)
    : "all";
}

function formatDate(value?: string) {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 19);
}

function accountOperationError(value: unknown, fallback: string) {
  const message = value instanceof Error ? value.message : String(value || fallback);
  return message.replace(/^ProxyPrecheckError:\s*/, "");
}

async function requestPermanentAccountDeletion(account: Account) {
  const confirmation = window.prompt(
    `永久删除后，司机、运单、任务、线路和定时计划记录均无法恢复。\n请输入手机号 ${account.phone} 确认删除：`,
  );
  if (confirmation === null) return undefined;
  if (confirmation.trim() !== account.phone) {
    throw new Error("手机号不匹配，未删除司机");
  }
  return del<any>(`/api/accounts/${account.id}/permanent`, {
    confirmation: confirmation.trim(),
  });
}

function AccountEditorDrawer({
  account,
  groups,
  canDeletePermanently,
  onClose,
  onChanged,
  onMessage,
}: {
  account?: Account;
  groups: DriverGroup[];
  canDeletePermanently: boolean;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
  onMessage: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [driverName, setDriverName] = useState("");
  const [plateNo, setPlateNo] = useState("");
  const [carCode, setCarCode] = useState("");
  const [plateColor, setPlateColor] = useState("1");
  const [defaultTons, setDefaultTons] = useState("30");
  const [truckLength, setTruckLength] = useState("");
  const [deviceProfile, setDeviceProfile] = useState("stable");
  const [groupId, setGroupId] = useState("group_ungrouped");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!account) return;
    setPassword("");
    setDriverName(account.driverName || "");
    setPlateNo(account.plateNo || "");
    setCarCode(account.carCode || "");
    setPlateColor(String(account.plateColor || 1));
    setDefaultTons(String(account.defaultTons || 30));
    setTruckLength(account.truckLength == null ? "" : String(account.truckLength));
    setDeviceProfile(account.deviceProfile || "stable");
    setGroupId(account.groupId || "group_ungrouped");
  }, [account]);

  async function save() {
    if (!account) return;
    setBusy(true);
    try {
      await patch(`/api/accounts/${account.id}`, {
        ...(password ? { password } : {}),
        driverName,
        plateNo,
        carCode,
        plateColor: Number(plateColor || 1),
        defaultTons: Number(defaultTons || 30),
        truckLength: truckLength ? Number(truckLength) : null,
        deviceProfile,
        groupId,
      });
      onMessage("账号资料已保存，线上状态保持待复核");
      await onChanged();
      onClose();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "保存账号失败");
    } finally {
      setBusy(false);
    }
  }

  async function recheck() {
    if (!account) return;
    setBusy(true);
    try {
      const result = await post<any>(`/api/accounts/${account.id}/recheck`);
      onMessage(result.ok ? "线上账号复核完成" : result.error || "复核失败");
      await onChanged();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "复核失败");
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled() {
    if (!account) return;
    if (account.enabled && !window.confirm(`确认停用司机 ${account.driverName || account.phone}？未执行任务将取消，且会移出未来定时计划。`)) return;
    setBusy(true);
    try {
      if (account.enabled) {
        const result = await del<any>(`/api/accounts/${account.id}`);
        onMessage(`账号已停用：取消 ${result.cancelledJobCount} 个任务，移出 ${result.removedPlanCount} 个计划`);
      } else {
        await post(`/api/accounts/${account.id}/enable`);
        onMessage("账号已恢复，需手工重新加入定时计划");
      }
      await onChanged();
      onClose();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "账号状态更新失败");
    } finally {
      setBusy(false);
    }
  }

  async function deletePermanently() {
    if (!account) return;
    setBusy(true);
    try {
      const result = await requestPermanentAccountDeletion(account);
      if (!result) return;
      onMessage(
        `司机已永久删除：移除 ${result.jobCount} 个任务、${result.routeRunCount} 条线路和 ${result.waybillCount} 张运单`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["waybills"] }),
        queryClient.invalidateQueries({ queryKey: ["jobs"] }),
        queryClient.invalidateQueries({ queryKey: ["route-runs"] }),
        queryClient.invalidateQueries({ queryKey: ["route-auto-plans"] }),
        queryClient.invalidateQueries({ queryKey: ["daily-reports"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
      await onChanged();
      onClose();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "永久删除司机失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={!!account}
      onOpenChange={(open) => !open && onClose()}
      title={`编辑账号 · ${account?.driverName || account?.phone || "司机"}`}
    >
      {account && (
        <div className="account-editor-panel">
          <div className="account-health-summary">
            <div><span>账号状态</span><strong>{account.enabled ? "启用" : "已停用"}</strong></div>
            <div><span>线上车牌</span><strong>{account.onlineVehicle.currentPlateNo || "未返回"}</strong></div>
            <div><span>车辆关系</span><strong>{account.onlineVehicle.carRelationId || "-"}</strong></div>
            <div><span>最近检查</span><strong>{formatDate(account.vehicleCheckedAt)}</strong></div>
          </div>
          {!!account.operationBlockers.length && (
            <div className="notice-bar danger-text">{account.operationBlockers.join("；")}</div>
          )}
          <div className="account-editor-grid">
            <Field label="手机号"><Input value={account.phone} disabled /></Field>
            <Field label="新密码" hint="密码未变化时留空">
              <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
            </Field>
            <Field label="司机姓名"><Input value={driverName} onChange={(event) => setDriverName(event.target.value)} /></Field>
            <Field label="所属分组">
              <Select value={groupId} onChange={(event) => setGroupId(event.target.value)}>
                {groups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}
              </Select>
            </Field>
            <Field label="车牌"><Input value={plateNo} onChange={(event) => setPlateNo(event.target.value)} /></Field>
            <Field label="车牌颜色"><Input type="number" min="1" value={plateColor} onChange={(event) => setPlateColor(event.target.value)} /></Field>
            <Field label="carCode"><Input value={carCode} onChange={(event) => setCarCode(event.target.value)} /></Field>
            <Field label="默认卸货吨数"><Input type="number" min="0.1" max="100" value={defaultTons} onChange={(event) => setDefaultTons(event.target.value)} /></Field>
            <Field label="车长"><Input type="number" min="0" step="0.1" value={truckLength} onChange={(event) => setTruckLength(event.target.value)} /></Field>
            <Field label="设备配置">
              <Select value={deviceProfile} onChange={(event) => setDeviceProfile(event.target.value)}>
                <option value="stable">稳定设备</option>
                <option value="new">新设备</option>
                <option value="app-captured">App 采集设备</option>
              </Select>
            </Field>
          </div>
          <div className="dialog-actions account-editor-actions">
            {canDeletePermanently && (
              <Button variant="danger" disabled={busy} onClick={deletePermanently}>
                <Trash2 size={15} />永久删除
              </Button>
            )}
            <Button variant="ghost" disabled={busy} onClick={recheck}><RefreshCw size={15} />线上复核</Button>
            <Button variant="secondary" disabled={busy} onClick={toggleEnabled}>
              {account.enabled ? <PowerOff size={15} /> : <Power size={15} />}
              {account.enabled ? "停用" : "恢复"}
            </Button>
            <Button disabled={busy} onClick={save}>{busy ? "处理中..." : "保存"}</Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function todayInShanghai() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function shanghaiDateAfter(days: number) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(Date.now() + days * 24 * 60 * 60 * 1000));
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function shanghaiAppointmentTime(minutesFromNow = 0) {
  const interval = 5 * 60 * 1000;
  const value = new Date(
    Math.ceil((Date.now() + minutesFromNow * 60 * 1000) / interval) * interval,
  );
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const result = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${result.year}-${result.month}-${result.day}T${result.hour}:${result.minute}`;
}

function reminderText(status?: "before" | "within" | "overdue") {
  return { before: "未到窗口", within: "窗口内", overdue: "已超时" }[status ?? "before"];
}

function autoStatusText(status?: RouteRun["autoStatus"] | string) {
  const labels: Record<string, string> = {
    manual: "手动",
    running: "自动运行中",
    waiting_order: "等待线路内新运单",
    waiting_unload: "等待安全卸货时间",
    waiting_interval: "等待下一趟接单间隔",
    waiting_schedule: "等待随机执行时间",
    waiting_retry: "等待自动重试",
    waiting_risk: "等待安全装货时间",
    needs_attention: "需要人工处理",
    paused: "自动已暂停",
    complete: "自动已完成",
    expired: "当天线路已截止",
  };
  return labels[status ?? "manual"] ?? "状态待确认";
}

function routeRunStatusText(status: string) {
  return {
    active: "运行中",
    complete: "已完成",
    expired: "已过期",
  }[status] ?? status;
}

function operationName(value: string) {
  return { accept: "接单", load: "装货", unload: "卸货" }[value] ?? value;
}

type WaybillOperation = "accept" | "load" | "unload";

function waybillOperation(waybill: Waybill): WaybillOperation | undefined {
  if (waybill.status === "100") return "accept";
  if (["120", "300", "310"].includes(waybill.status)) return "load";
  if (["410", "415"].includes(waybill.status)) return "unload";
  return undefined;
}

function statusTone(
  status: string,
): "neutral" | "success" | "warning" | "danger" | "info" {
  if (["420", "500", "520", "success", "complete"].includes(status))
    return "success";
  if (["410", "415", "running", "approved"].includes(status)) return "info";
  if (
    ["100", "120", "300", "310", "queued", "scheduled", "preview", "partial"].includes(
      status,
    )
  )
    return "warning";
  if (["failed", "blocked", "expired"].includes(status)) return "danger";
  return "neutral";
}

function checkinStatusText(status: Waybill["checkinLocationStatus"]) {
  return {
    complete: "位置已回显",
    partial: "位置不完整",
    missing: "地址缺失",
    unknown: "历史未知",
    historical_missing: "历史证据缺失",
  }[status];
}

function checkinStatusTone(
  status: Waybill["checkinLocationStatus"],
): "neutral" | "success" | "warning" | "danger" {
  if (status === "complete") return "success";
  if (status === "partial") return "warning";
  if (status === "missing") return "danger";
  return "neutral";
}

function businessTimestamp(value?: string) {
  if (!value) return undefined;
  const normalized = value.replace(" ", "T");
  const timestamp = Date.parse(
    /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized) ? normalized : `${normalized}+08:00`,
  );
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

function durationText(totalMinutes: number) {
  const minutes = Math.max(0, Math.ceil(totalMinutes));
  const hours = Math.floor(minutes / 60);
  return `${hours}小时${minutes % 60}分`;
}

function countdown(waybill: Waybill, now: number) {
  if (waybill.unloadEligible) return "可以卸货";
  if (!["410", "415"].includes(waybill.status)) return "-";
  const earliest = businessTimestamp(waybill.earliestUnloadTime);
  if (earliest != null && earliest > now) return durationText((earliest - now) / 60_000);
  if (waybill.remainingMinutes == null) return "-";
  return durationText(waybill.remainingMinutes);
}

function unloadOvertime(waybill: Waybill, now: number) {
  const windowEnd = businessTimestamp(waybill.unloadWindowEndAt);
  if (windowEnd == null) return undefined;
  const completedAt = ["420", "500", "520"].includes(waybill.status)
    ? businessTimestamp(waybill.unloadTime)
    : undefined;
  const referenceTime = completedAt ?? now;
  if (referenceTime <= windowEnd) return undefined;
  return `已超时 ${durationText((referenceTime - windowEnd) / 60_000)}`;
}

export function DashboardPage({ navigate }: { navigate: Navigate }) {
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api<any>("/api/dashboard"),
    refetchInterval: 30_000,
  });
  const metrics = [
    ["启用账号", dashboard.data?.accounts ?? 0, Users, "neutral"],
    ["待接单", dashboard.data?.waitingAccept ?? 0, ListChecks, "warning"],
    ["待装货", dashboard.data?.waitingLoad ?? 0, Upload, "warning"],
    ["运输中", dashboard.data?.inTransit ?? 0, Truck, "info"],
    ["可卸货", dashboard.data?.unloadEligible ?? 0, BellRing, "success"],
    ["异常任务", dashboard.data?.failedTasks ?? 0, ShieldAlert, "danger"],
  ] as const;
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>多账号运行态势</h1>
          <p>统一查看账号、运单、等待时间和最近业务任务。</p>
        </div>
        <div className="heading-actions">
          <Button variant="secondary" onClick={() => navigate("accounts")}>
            <RefreshCw size={16} />
            同步账号
          </Button>
          <Button onClick={() => navigate("waybills")}>
            <Truck size={16} />
            查看运单
          </Button>
        </div>
      </section>
      <section className="metric-strip">
        {metrics.map(([label, value, Icon, tone]) => (
          <button
            key={label}
            onClick={() =>
              label === "启用账号" ? navigate("accounts") : navigate("waybills")
            }
          >
            <span className={`metric-icon metric-${tone}`}>
              <Icon size={18} />
            </span>
            <span>
              <strong>{value}</strong>
              <small>{label}</small>
            </span>
          </button>
        ))}
      </section>
      <section className="split-workspace">
        <div className="panel-section">
          <div className="section-header">
            <div>
              <h2>最近任务</h2>
              <p>真实业务操作与状态复查结果</p>
            </div>
            <Button variant="ghost" onClick={() => navigate("jobs")}>
              全部任务
              <ChevronRight size={15} />
            </Button>
          </div>
          <div className="activity-list">
            {dashboard.data?.recentJobs?.length ? (
              dashboard.data.recentJobs.map((job: any) => (
                <div key={job.id} className="activity-row">
                  <span
                    className={`activity-symbol activity-${statusTone(job.status)}`}
                  >
                    {job.status === "success" ? (
                      <Check size={15} />
                    ) : job.status === "failed" ? (
                      <X size={15} />
                    ) : (
                      <Clock3 size={15} />
                    )}
                  </span>
                  <div>
                    <strong>
                      {operationName(job.operation)} · {job.waybillId}
                    </strong>
                    <span>
                      {job.phone} {job.driverName}
                    </span>
                  </div>
                  <Badge tone={statusTone(job.status)}>{job.status}</Badge>
                  <time>{formatDate(job.createdAt)}</time>
                </div>
              ))
            ) : (
              <EmptyState
                title="还没有任务"
                description="完成批量预检并批准后，任务会出现在这里。"
              />
            )}
          </div>
        </div>
        <div className="panel-section attention-panel">
          <div className="section-header">
            <div>
              <h2>需要关注</h2>
              <p>
                {dashboard.data?.autoRoutes
                  ? `${dashboard.data.autoRoutes} 条线路正在自动运行`
                  : "未开启自动线路的运单仍需人工确认"}
              </p>
            </div>
          </div>
          <button
            className="attention-row"
            onClick={() => navigate("waybills")}
          >
            <span className="attention-icon success">
              <BellRing size={17} />
            </span>
            <div>
              <strong>
                {dashboard.data?.unloadEligible ?? 0} 张运单可以卸货
              </strong>
              <span>
                {dashboard.data?.autoRoutes
                  ? "自动线路将在安全时间执行；其他运单请手动预检"
                  : "请进入运单中心预检并确认"}
              </span>
            </div>
            <ChevronRight size={16} />
          </button>
          <button
            className="attention-row"
            onClick={() => navigate("accounts")}
          >
            <span className="attention-icon danger">
              <KeyRound size={17} />
            </span>
            <div>
              <strong>{dashboard.data?.loginErrors ?? 0} 个账号同步异常</strong>
              <span>检查密码、登录状态或网络</span>
            </div>
            <ChevronRight size={16} />
          </button>
          <button className="attention-row" onClick={() => navigate("jobs")}>
            <span className="attention-icon warning">
              <AlertTriangle size={17} />
            </span>
            <div>
              <strong>
                {dashboard.data?.failedTasks ?? 0} 个任务失败或阻断
              </strong>
              <span>查看服务端状态和错误证据</span>
            </div>
            <ChevronRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}

export function AccountsPage({
  currentUser,
  openReports,
}: {
  currentUser: CurrentUser;
  openReports?: (planId?: string) => void;
}) {
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [preview, setPreview] = useState<any>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "danger" | "neutral">("neutral");
  const [accountAction, setAccountAction] = useState<{ accountId: string; type: "sync" | "recheck" }>();
  const [accountResults, setAccountResults] = useState<Record<string, { ok: boolean; label: string; detail: string }>>({});
  const [editingAccount, setEditingAccount] = useState<Account>();
  const [importGroupId, setImportGroupId] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<"" | "true" | "false">("");
  const [accountSearch, setAccountSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<50 | 100 | 200>(50);
  const initialParams = new URLSearchParams(window.location.search);
  const initialDetailAccountId = initialParams.get("accountId") || undefined;
  const [detailScope, setDetailScope] = useState<{
    accountId: string;
    statusGroup: WaybillStatusGroup;
    driverName?: string;
    phone?: string;
    count?: number;
  } | undefined>(
    initialDetailAccountId
      ? {
          accountId: initialDetailAccountId,
          statusGroup: waybillStatusGroup(initialParams.get("statusGroup")),
        }
      : undefined,
  );
  const [detailSearch, setDetailSearch] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState<50 | 100 | 200>(50);
  const [detailWaybill, setDetailWaybill] = useState<Waybill>();
  const accounts = useQuery({
    queryKey: ["accounts", "page", page, pageSize, groupFilter, enabledFilter, accountSearch],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        groupId: groupFilter,
        search: accountSearch,
      });
      if (enabledFilter) params.set("enabled", enabledFilter);
      return api<PagedResponse<Account> & { groups: Array<{ id: string; name: string }> }>(
        `/api/accounts?${params}`,
      );
    },
  });
  const accountGroups = useQuery({
    queryKey: ["admin-groups"],
    queryFn: () => api<DriverGroup[]>("/api/admin/groups"),
    enabled: currentUser.capabilities.manageAccounts,
  });
  const detailWaybills = useQuery({
    queryKey: [
      "account-waybills",
      detailScope?.accountId,
      detailScope?.statusGroup,
      detailSearch,
      detailPage,
      detailPageSize,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        accountId: detailScope!.accountId,
        statusGroup: detailScope!.statusGroup,
        search: detailSearch,
        page: String(detailPage),
        pageSize: String(detailPageSize),
      });
      return api<PagedResponse<Waybill>>(`/api/waybills?${params}`);
    },
    enabled: !!detailScope,
  });
  const detailWaybillQuery = useQuery({
    queryKey: ["waybill-detail", detailWaybill?.accountId, detailWaybill?.waybillId],
    queryFn: () => api<Waybill>(
      `/api/waybills/${detailWaybill!.waybillId}?accountId=${detailWaybill!.accountId}&includeRaw=false`,
    ),
    enabled: !!detailWaybill,
  });

  const rows = useMemo(() => parseAccounts(raw), [raw]);
  useEffect(() => {
    setPage(1);
  }, [groupFilter, enabledFilter, accountSearch, pageSize]);
  useEffect(() => {
    setDetailPage(1);
  }, [detailScope?.accountId, detailScope?.statusGroup, detailSearch, detailPageSize]);
  useEffect(() => {
    const syncDetailFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const accountId = params.get("accountId") || undefined;
      setDetailScope((current) =>
        accountId
          ? {
              ...(current?.accountId === accountId ? current : { accountId }),
              accountId,
              statusGroup: waybillStatusGroup(params.get("statusGroup")),
            }
          : undefined,
      );
      if (!accountId) setDetailWaybill(undefined);
    };
    window.addEventListener("popstate", syncDetailFromUrl);
    return () => window.removeEventListener("popstate", syncDetailFromUrl);
  }, []);

  function openAccountWaybills(
    account: Account,
    statusGroup: WaybillStatusGroup,
    count: number,
  ) {
    if (!count) return;
    setDetailScope({
      accountId: account.id,
      statusGroup,
      driverName: account.driverName,
      phone: account.phone,
      count,
    });
    setDetailWaybill(undefined);
    setDetailSearch("");
    const params = new URLSearchParams(window.location.search);
    params.set("accountId", account.id);
    params.set("statusGroup", statusGroup);
    window.history.pushState({}, "", `${window.location.pathname}?${params}`);
  }

  function closeAccountWaybills() {
    setDetailScope(undefined);
    setDetailWaybill(undefined);
    const params = new URLSearchParams(window.location.search);
    params.delete("accountId");
    params.delete("statusGroup");
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  }
  const columns: ColumnDef<Account>[] = [
    {
      header: "账号 / 司机",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.phone}</strong>
          <span>{row.original.driverName || "未识别司机"}</span>
        </div>
      ),
    },
    {
      header: "分组",
      cell: ({ row }) => (
        <Badge tone={row.original.groupId === "group_ungrouped" ? "warning" : "neutral"}>
          {row.original.groupName}
        </Badge>
      ),
    },
    {
      header: "车辆",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.plateNo || "未配置"}</strong>
          <span>{row.original.carCode || "缺少 carCode"}</span>
          {row.original.onlineVehicle.carRelationId && (
            <span>关系 {row.original.onlineVehicle.carRelationId}</span>
          )}
        </div>
      ),
    },
    { header: "默认卸货", cell: ({ row }) => `${row.original.defaultTons} 吨` },
    {
      header: "运单状态",
      cell: ({ row }) => {
        const counts = row.original.waybillCounts;
        const items = [
          ["pendingAccept", counts.pendingAccept, "warning"],
          ["pendingLoad", counts.pendingLoad, "info"],
          ["inTransit", counts.inTransit, "success"],
          ["unloaded", counts.unloaded, "neutral"],
          ["signed", counts.signed, "neutral"],
          ["cancelled", counts.cancelled, "danger"],
        ] as const;
        return (
          <div className="waybill-status-cell">
            <button
              type="button"
              className="waybill-total"
              disabled={!counts.total}
              onClick={() => openAccountWaybills(row.original, "all", counts.total)}
            >
              <span>已同步</span>
              <strong>{counts.total}</strong>
            </button>
            <div className="waybill-status-grid">
              {items.map(([group, count, tone]) => (
                <button
                  type="button"
                  className={`waybill-count waybill-count-${tone}`}
                  disabled={!count}
                  onClick={() => openAccountWaybills(row.original, group, count)}
                  key={group}
                >
                  <span>{WAYBILL_GROUP_LABELS[group]}</span>
                  <strong>{count}</strong>
                </button>
              ))}
              {counts.other > 0 && (
                <button
                  type="button"
                  className="waybill-count waybill-count-neutral"
                  onClick={() => openAccountWaybills(row.original, "other", counts.other)}
                >
                  <span>其他</span>
                  <strong>{counts.other}</strong>
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: "最近同步",
      cell: ({ row }) => formatDate(row.original.lastSyncAt),
    },
    {
      header: "状态",
      cell: ({ row }) => {
        const account = row.original;
        const credentialLabels: Record<string, string> = {
          invalid: "密码错误",
          locked: "账号锁定",
          disabled: "平台停用",
          unchecked: "待验证",
        };
        const vehicleLabels: Record<string, string> = {
          car_code_missing: "缺少 carCode",
          vehicle_missing: "未绑定车辆",
          documents_rejected: "证照异常",
          query_error: "车辆查询失败",
          unchecked: "车辆待验证",
        };
        if (!account.enabled) {
          return <Badge tone="danger">已停用</Badge>;
        }
        if (account.credentialStatus !== "ok") {
          return <Badge tone="danger">{credentialLabels[account.credentialStatus] || "登录异常"}</Badge>;
        }
        if (account.vehicleStatus !== "verified") {
          return <Badge tone="warning">{vehicleLabels[account.vehicleStatus] || "车辆异常"}</Badge>;
        }
        return <Badge tone="success">线上一致</Badge>;
      },
    },
    {
      header: "定时任务",
      cell: ({ row }) =>
        row.original.scheduledTasks.length ? (
          <div className="account-schedule-list">
            {row.original.scheduledTasks.map((task) => (
              <div key={task.id} className="account-schedule-item">
                <strong>{task.name}</strong>
                <span>
                  {task.scheduleType === "daily" ? "每日" : task.runDate}
                  {task.enabled ? " · 已启用" : " · 已暂停"}
                </span>
                {openReports && (
                  <button type="button" onClick={() => openReports(task.id)}>查看日报</button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <span className="muted-text">未绑定</span>
        ),
    },
    {
      header: "操作",
      cell: ({ row }) => {
        const account = row.original;
        const running = accountAction?.accountId === account.id ? accountAction.type : undefined;
        const result = accountResults[account.id];
        return (
          <div className="account-row-actions">
            {account.enabled && (
              <Button
                variant="ghost"
                disabled={busy}
                aria-busy={running === "sync"}
                onClick={() => sync([account.id], account.phone)}
              >
                {running === "sync" ? <Loader2 className="spin" size={15} /> : <RefreshCw size={15} />}
                {running === "sync" ? "同步中" : "同步"}
              </Button>
            )}
            {currentUser.capabilities.manageAccounts && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => setEditingAccount(account)}
              >
                <Pencil size={15} />编辑
              </Button>
            )}
            {currentUser.capabilities.manageAccounts && (
              <Button
                variant="ghost"
                disabled={busy}
                aria-busy={running === "recheck"}
                onClick={() => recheck(account)}
              >
                {running === "recheck" ? <Loader2 className="spin" size={15} /> : <RefreshCw size={15} />}
                {running === "recheck" ? "复核中" : "复核"}
              </Button>
            )}
            {currentUser.capabilities.manageAccounts && (
              <IconButton
                label={account.enabled ? "停用司机" : "恢复司机"}
                disabled={busy}
                onClick={() => toggleAccount(account)}
              >
                {account.enabled ? <PowerOff size={15} /> : <Power size={15} />}
              </IconButton>
            )}
            {currentUser.role === "super_admin" && (
              <IconButton
                label="永久删除司机"
                disabled={busy}
                onClick={() => deleteAccountPermanently(account)}
              >
                <Trash2 size={15} />
              </IconButton>
            )}
            {result && (
              <span
                className={`account-action-result ${result.ok ? "success" : "danger"}`}
                title={result.detail}
              >
                {result.label}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  async function sync(accountIds?: string[], accountLabel?: string) {
    setBusy(true);
    setMessage("");
    if (accountIds?.length === 1) {
      setAccountAction({ accountId: accountIds[0], type: "sync" });
      setAccountResults((current) => {
        const next = { ...current };
        delete next[accountIds[0]];
        return next;
      });
    }
    try {
      if (accountIds?.length === 1) {
        const accountId = accountIds[0];
        const result = await post<any>(`/api/accounts/${accountId}/sync`);
        if (!result.ok) {
          const detail = accountOperationError(result.error, "同步失败");
          setAccountResults((current) => ({ ...current, [accountId]: { ok: false, label: "同步失败", detail } }));
          setMessageTone("danger");
          setMessage(`${accountLabel || "账号"} 同步失败：${detail}`);
        } else {
          const changed = result.changedFields?.length || 0;
          const detail = `同步成功，获取 ${result.waybillCount || 0} 张运单，更新 ${changed} 项资料`;
          setAccountResults((current) => ({ ...current, [accountId]: { ok: true, label: "同步成功", detail } }));
          setMessageTone("success");
          setMessage(`${accountLabel || "账号"} ${detail}`);
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["accounts"] }),
          queryClient.invalidateQueries({ queryKey: ["waybills"] }),
          queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        ]);
        return;
      }
      const result = await post<any>("/api/accounts/sync", { accountIds });
      const failures = result.results.filter((item: any) => !item.ok);
      const failed = failures.length;
      const updated = result.results.filter((item: any) => item.changedFields?.length).length;
      setAccountResults((current) => ({
        ...current,
        ...Object.fromEntries(result.results.map((item: any) => [
          item.accountId,
          {
            ok: !!item.ok,
            label: item.ok ? "同步成功" : "同步失败",
            detail: item.ok ? `获取 ${item.waybillCount || 0} 张运单` : accountOperationError(item.error, "同步失败"),
          },
        ])),
      }));
      setMessageTone(failed ? "danger" : "success");
      setMessage(
        `同步完成：${result.results.length - failed} 成功，${updated} 个资料已更新，${failed} 失败${failures[0]?.error ? `；首个失败：${accountOperationError(failures[0].error, "同步失败")}` : ""}`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["waybills"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    } catch (error) {
      setMessageTone("danger");
      setMessage(error instanceof Error ? error.message : "同步失败");
    } finally {
      setAccountAction(undefined);
      setBusy(false);
    }
  }

  async function recheck(account: Account) {
    const accountId = account.id;
    setBusy(true);
    setMessage("");
    setAccountAction({ accountId, type: "recheck" });
    setAccountResults((current) => {
      const next = { ...current };
      delete next[accountId];
      return next;
    });
    try {
      const result = await post<any>(`/api/accounts/${accountId}/recheck`);
      const detail = result.ok
        ? `线上复核完成，获取 ${result.waybillCount || 0} 张运单`
        : accountOperationError(result.error, "复核失败");
      setAccountResults((current) => ({
        ...current,
        [accountId]: { ok: !!result.ok, label: result.ok ? "复核成功" : "复核失败", detail },
      }));
      setMessageTone(result.ok ? "success" : "danger");
      setMessage(`${account.driverName || account.phone} ${detail}`);
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    } catch (error) {
      setMessageTone("danger");
      setMessage(error instanceof Error ? error.message : "复核失败");
    } finally {
      setAccountAction(undefined);
      setBusy(false);
    }
  }

  async function toggleAccount(account: Account) {
    if (account.enabled && !window.confirm(`确认停用司机 ${account.driverName || account.phone}？未执行任务将取消，且会移出未来定时计划。`)) return;
    setBusy(true);
    setMessage("");
    try {
      if (account.enabled) {
        const result = await del<any>(`/api/accounts/${account.id}`);
        setMessage(`账号已停用：取消 ${result.cancelledJobCount} 个任务，移出 ${result.removedPlanCount} 个计划`);
      } else {
        await post(`/api/accounts/${account.id}/enable`);
        setMessage("账号已恢复，需手工重新加入定时计划");
      }
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "账号状态更新失败");
    } finally {
      setBusy(false);
    }
  }

  async function deleteAccountPermanently(account: Account) {
    setBusy(true);
    setMessage("");
    try {
      const result = await requestPermanentAccountDeletion(account);
      if (!result) return;
      setMessageTone("success");
      setMessage(
        `司机已永久删除：移除 ${result.jobCount} 个任务、${result.routeRunCount} 条线路和 ${result.waybillCount} 张运单`,
      );
      if (detailScope?.accountId === account.id) closeAccountWaybills();
      if (editingAccount?.id === account.id) setEditingAccount(undefined);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["waybills"] }),
        queryClient.invalidateQueries({ queryKey: ["jobs"] }),
        queryClient.invalidateQueries({ queryKey: ["route-runs"] }),
        queryClient.invalidateQueries({ queryKey: ["route-auto-plans"] }),
        queryClient.invalidateQueries({ queryKey: ["daily-reports"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    } catch (error) {
      setMessageTone("danger");
      setMessage(error instanceof Error ? error.message : "永久删除司机失败");
    } finally {
      setBusy(false);
    }
  }

  async function previewImport() {
    setBusy(true);
    try {
      setPreview(
        await post("/api/accounts/import/preview", {
          rows,
          replaceExisting,
          groupId: importGroupId || null,
        }),
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "预览失败");
    } finally {
      setBusy(false);
    }
  }

  async function commitImport() {
    setBusy(true);
    try {
      const result = await post<any>("/api/accounts/import/commit", {
        rows,
        replaceExisting,
        groupId: importGroupId || null,
      });
      setMessage(`已导入 ${result.imported} 个，更新 ${result.updated} 个账号`);
      setImportOpen(false);
      setRaw("");
      setPreview(undefined);
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "导入失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>账号管理</h1>
          <p>手机号和密码即可导入，其他资料可由每日线上预检回填。</p>
        </div>
        <div className="heading-actions">
          <Button
            variant="secondary"
            disabled={busy || !accounts.data?.total}
            onClick={() => sync()}
          >
            {busy ? (
              <Loader2 className="spin" size={16} />
            ) : (
              <RefreshCw size={16} />
            )}
            同步全部
          </Button>
          {currentUser.capabilities.manageAccounts && (
            <Button onClick={() => setImportOpen(true)}>
              <Plus size={16} />
              导入账号
            </Button>
          )}
        </div>
      </section>
      {message && (
        <div
          className={`notice-bar account-operation-feedback ${messageTone === "danger" ? "danger" : messageTone === "success" ? "success" : ""}`}
          role="status"
          aria-live="polite"
        >
          {message}
        </div>
      )}
      <div className="toolbar compact-toolbar">
        <label className="search-box">
          <Search size={16} />
          <input
            value={accountSearch}
            onChange={(event) => setAccountSearch(event.target.value)}
            placeholder="搜索司机、手机号或车牌"
          />
        </label>
        <Select value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)}>
          <option value="">全部分组</option>
          {accounts.data?.groups.map((group) => (
            <option value={group.id} key={group.id}>{group.name}</option>
          ))}
        </Select>
        <Select value={enabledFilter} onChange={(event) => setEnabledFilter(event.target.value as "" | "true" | "false")}>
          <option value="">全部状态</option>
          <option value="true">仅启用</option>
          <option value="false">仅停用</option>
        </Select>
        <span className="toolbar-count">
          {accounts.isFetching ? "加载中..." : `${accounts.data?.total ?? 0} 个司机账号`}
        </span>
      </div>
      <DataTable
        data={accounts.data?.items ?? []}
        columns={columns}
        loading={accounts.isLoading || accounts.isFetching}
        emptyTitle="尚未导入账号"
        emptyDescription="支持从表格直接粘贴，或导入 CSV 文件。"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={accounts.data?.total ?? 0}
        loading={accounts.isFetching}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
      <Drawer
        open={!!detailScope}
        onOpenChange={(open) => !open && closeAccountWaybills()}
        title={
          detailWaybill
            ? `运单 ${detailWaybill.waybillId}`
            : `${detailScope?.driverName || detailWaybills.data?.items[0]?.driverName || detailScope?.phone || "司机"} · ${WAYBILL_GROUP_LABELS[detailScope?.statusGroup ?? "all"]}`
        }
      >
        {detailWaybill ? (
          <>
            <Button variant="ghost" onClick={() => setDetailWaybill(undefined)}>
              <ArrowLeft size={15} />返回运单列表
            </Button>
            {detailWaybillQuery.isLoading && <LoadingState label="正在加载运单详情" />}
            <WaybillDetailContent waybill={detailWaybillQuery.data ?? detailWaybill} />
          </>
        ) : (
          <div className="account-waybill-detail">
            <label className="search-box">
              <Search size={16} />
              <input
                value={detailSearch}
                onChange={(event) => setDetailSearch(event.target.value)}
                placeholder="搜索运单号或地点"
              />
            </label>
            {detailWaybills.isLoading && <LoadingState label="正在加载司机运单" />}
            {detailWaybills.isError && (
              <EmptyState
                title="运单明细加载失败"
                description="请检查网络后重试。"
                action={<Button variant="secondary" onClick={() => detailWaybills.refetch()}>重新加载</Button>}
              />
            )}
            <div className="account-waybill-list">
              {detailWaybills.data?.items.map((waybill) => (
                <button type="button" key={waybill.waybillId} onClick={() => setDetailWaybill(waybill)}>
                  <span><strong>{waybill.waybillId}</strong><small>{waybill.departureName || "未知起点"} → {waybill.destinationName || "未知终点"}</small></span>
                  <span><Badge tone={statusTone(waybill.status)}>{waybill.statusText}</Badge><small>{formatDate(waybill.syncedAt)}</small></span>
                  <Badge tone={checkinStatusTone(waybill.checkinLocationStatus)}>{checkinStatusText(waybill.checkinLocationStatus)}</Badge>
                  <ChevronRight size={16} />
                </button>
              ))}
            </div>
            {!detailWaybills.isLoading && !detailWaybills.data?.items.length && (
              <EmptyState title="没有匹配的运单" description="该分类当前没有运单，或搜索条件没有结果。" />
            )}
            <Pagination
              page={detailPage}
              pageSize={detailPageSize}
              total={detailWaybills.data?.total ?? 0}
              loading={detailWaybills.isFetching}
              onPageChange={setDetailPage}
              onPageSizeChange={setDetailPageSize}
            />
          </div>
        )}
      </Drawer>
      <AccountEditorDrawer
        account={editingAccount}
        groups={accountGroups.data ?? []}
        canDeletePermanently={currentUser.role === "super_admin"}
        onClose={() => setEditingAccount(undefined)}
        onChanged={async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["accounts"] }),
            queryClient.invalidateQueries({ queryKey: ["admin-groups"] }),
          ]);
        }}
        onMessage={setMessage}
      />
      <Dialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="批量导入账号"
        description="每行使用制表符或逗号分隔，密码仅会加密保存。"
        wide
      >
        <div className="import-layout">
          <div>
            <Field
              label="账号数据"
              hint="仅手机号、密码必填；其后可选：司机、车牌、carCode、车牌颜色、默认卸货吨数、车长"
            >
              <textarea
                className="textarea code-input"
                value={raw}
                onChange={(event) => {
                  setRaw(event.target.value);
                  setPreview(undefined);
                }}
                placeholder="18600000001,请填写密码"
              />
            </Field>
            <label className="file-picker">
              <Upload size={15} />
              <span>选择 CSV 文件</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    setRaw(await file.text());
                    setPreview(undefined);
                  }
                }}
              />
            </label>
            <Field label="目标司机分组" hint="未选择时进入未分组">
              <Select value={importGroupId} onChange={(event) => setImportGroupId(event.target.value)}>
                <option value="">未分组</option>
                {(accountGroups.data ?? []).filter((group) => !group.isSystem).map((group) => (
                  <option value={group.id} key={group.id}>{group.name}</option>
                ))}
              </Select>
            </Field>
            <label className="check-row">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(event) => setReplaceExisting(event.target.checked)}
              />
              更新已存在账号的资料和密码
            </label>
          </div>
          <div className="import-preview">
            <div className="section-header">
              <div>
                <h3>解析预览</h3>
                <p>{rows.length} 行账号</p>
              </div>
            </div>
            {rows.length ? (
              rows.slice(0, 8).map((row, index) => (
                <div className="import-row" key={`${row.phone}-${index}`}>
                  <strong>{row.phone}</strong>
                  <span>{row.driverName || "未填写司机"}</span>
                  <span>{row.plateNo || "未填写车牌"}</span>
                </div>
              ))
            ) : (
              <EmptyState
                title="等待输入"
                description="从 Excel 复制数据或选择 CSV 文件。"
              />
            )}
          </div>
        </div>
        {preview && (
          <div className="preview-summary">
            <Badge
              tone={
                preview.validCount === preview.total ? "success" : "warning"
              }
            >
              {preview.validCount}/{preview.total} 可导入
            </Badge>
            {preview.rows
              .flatMap((row: any) => row.errors)
              .slice(0, 4)
              .map((error: string, index: number) => (
                <span key={index}>{error}</span>
              ))}
          </div>
        )}
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={previewImport}
            disabled={!rows.length || busy}
          >
            检查数据
          </Button>
          <Button
            onClick={commitImport}
            disabled={!preview || preview.validCount !== preview.total || busy}
          >
            确认导入
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function parseAccounts(raw: string) {
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^phone|^手机号/i.test(line))
    .map((line) => {
      const parts = line
        .split(line.includes("\t") ? "\t" : ",")
        .map((value) => value.trim());
      return {
        phone: parts[0] || "",
        password: parts[1] || "",
        ...(parts[2] ? { driverName: parts[2] } : {}),
        ...(parts[3] ? { plateNo: parts[3] } : {}),
        ...(parts[4] ? { carCode: parts[4] } : {}),
        ...(parts[5] ? { plateColor: Number(parts[5]) } : {}),
        ...(parts[6] ? { defaultTons: Number(parts[6]) } : {}),
        ...(parts[7] ? { truckLength: Number(parts[7]) } : {}),
      };
    });
}

export function WaybillsPage({
  selected,
  setSelected,
  openBatch,
}: {
  selected: Set<string>;
  setSelected: (value: Set<string>) => void;
  openBatch: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [groupId, setGroupId] = useState("");
  const [sortBy, setSortBy] = useState<
    "default" | "acceptTime" | "loadTime" | "unloadTime"
  >("default");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<50 | 100 | 200>(50);
  const [now, setNow] = useState(Date.now());
  const [detailTarget, setDetailTarget] = useState<Waybill>();
  const [singleAction, setSingleAction] = useState<{
    waybill: Waybill;
    operation: WaybillOperation;
  }>();
  const [singleActionOpen, setSingleActionOpen] = useState(false);
  const [singleTimeWarningConfirmed, setSingleTimeWarningConfirmed] = useState(false);
  const [singlePreview, setSinglePreview] = useState<BatchPreview>();
  const [singleScheduleMode, setSingleScheduleMode] = useState<
    "immediate" | "scheduled"
  >("immediate");
  const [singleScheduledAt, setSingleScheduledAt] = useState("");
  const [singlePreviewKey, setSinglePreviewKey] = useState("");
  const [singleBusy, setSingleBusy] = useState(false);
  const [singleError, setSingleError] = useState("");
  const [singleMessage, setSingleMessage] = useState("");
  const accounts = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api<Account[]>("/api/accounts"),
  });
  const visibleGroups = useMemo(
    () =>
      Array.from(
        new Map(
          (accounts.data ?? []).map((account) => [
            account.groupId,
            account.groupName,
          ]),
        ),
      ).sort((left, right) => left[1].localeCompare(right[1], "zh-CN")),
    [accounts.data],
  );
  const waybills = useQuery({
    queryKey: [
      "waybills",
      search,
      status,
      groupId,
      sortBy,
      sortOrder,
      page,
      pageSize,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        search,
        status,
        groupId,
        sortBy,
        sortOrder,
        page: String(page),
        pageSize: String(pageSize),
      });
      return api<PagedResponse<Waybill>>(`/api/waybills?${params}`);
    },
  });
  const detail = useQuery({
    queryKey: [
      "waybill-detail",
      detailTarget?.accountId,
      detailTarget?.waybillId,
    ],
    queryFn: () =>
      api<Waybill>(
        `/api/waybills/${detailTarget!.waybillId}?accountId=${detailTarget!.accountId}`,
      ),
    enabled: !!detailTarget,
  });
  useEffect(() => {
    setPage(1);
  }, [search, status, groupId, sortBy, sortOrder, pageSize]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  function toggle(item: Waybill) {
    const next = new Set(selected);
    const key = selectedKey(item);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  }
  function singleRequestKey(
    mode: "immediate" | "scheduled",
    scheduledAt: string,
  ) {
    return `${mode}:${mode === "scheduled" ? scheduledAt : "now"}`;
  }
  async function runSinglePreview(
    action: { waybill: Waybill; operation: WaybillOperation },
    mode: "immediate" | "scheduled",
    scheduledAt: string,
  ) {
    const requestKey = singleRequestKey(mode, scheduledAt);
    setSinglePreview(undefined);
    setSinglePreviewKey("");
    setSingleError("");
    setSingleBusy(true);
    try {
      const preview = await post<BatchPreview>("/api/batches/preview", {
        operation: action.operation,
        targets: [
          {
            accountId: action.waybill.accountId,
            waybillId: action.waybill.waybillId,
            routeRunId: action.waybill.routeRunId,
          },
        ],
        scheduleMode: mode,
        scheduledAt: mode === "scheduled" ? scheduledAt : null,
        locationMode: "auto-offset",
        offsetMeters: 150,
        jitterMinMinutes: 5,
        jitterMaxMinutes: 20,
      });
      setSinglePreview(preview);
      setSinglePreviewKey(requestKey);
    } catch (error) {
      setSingleError(error instanceof Error ? error.message : "单条操作预检失败");
    } finally {
      setSingleBusy(false);
    }
  }
  async function beginSingleAction(waybill: Waybill) {
    const operation = waybillOperation(waybill);
    if (!operation) return;
    const action = { waybill, operation };
    setDetailTarget(waybill);
    setSingleAction(action);
    setSingleTimeWarningConfirmed(false);
    setSingleScheduleMode("immediate");
    setSingleScheduledAt("");
    setSingleMessage("");
    setSingleActionOpen(true);
    await runSinglePreview(action, "immediate", "");
  }
  async function changeSingleScheduleMode(mode: "immediate" | "scheduled") {
    if (!singleAction || singleBusy) return;
    const scheduledAt =
      mode === "scheduled"
        ? singleScheduledAt || shanghaiAppointmentTime(5)
        : "";
    setSingleScheduleMode(mode);
    setSingleTimeWarningConfirmed(false);
    setSingleScheduledAt(scheduledAt);
    await runSinglePreview(singleAction, mode, scheduledAt);
  }
  function singleUnloadTimeWarning() {
    if (!singleAction || singleAction.operation !== "unload") return "";
    const waybill = singleAction.waybill;
    const reference = businessTimestamp(waybill.earliestUnloadTime || waybill.estimatedUnloadTime);
    if (reference == null) return "缺少可核实的卸货时间依据，请核对实际运输与卸货情况。";
    if (reference > Date.now()) return `尚未达到${waybill.earliestUnloadTime ? "最早" : "预估"}卸货时间 ${formatDate(waybill.earliestUnloadTime || waybill.estimatedUnloadTime)}，请再次核对实际运输与卸货情况。`;
    if (!waybill.unloadEligible) return "参考时间已过，但正式时间校验未通过；接单参考不代表实际装货或卸货完成。";
    return "";
  }
  async function approveSingleAction() {
    if (!singleAction || !singlePreview) return;
    if (singlePreview.blockedCount > 0) {
      setSingleError("预检仍有阻断项，未发送业务批准请求，请先处理预检原因。");
      return;
    }
    if (singleUnloadTimeWarning() && !singleTimeWarningConfirmed) {
      setSingleError("请先阅读并确认卸货时间警告。");
      return;
    }
    if (
      singlePreviewKey !==
      singleRequestKey(singleScheduleMode, singleScheduledAt)
    ) {
      setSingleError("执行方式或预约时间已变化，请重新预检");
      return;
    }
    setSingleBusy(true);
    setSingleError("");
    try {
      await post(`/api/batches/${singlePreview.id}/approve`, {
        previewHash: singlePreview.previewHash,
      });
      const scheduledAt = singlePreview.items[0]?.scheduledAt;
      setSingleMessage(
        singleScheduleMode === "immediate"
          ? `${operationName(singleAction.operation)}任务已进入立即执行队列。`
          : `${operationName(singleAction.operation)}任务已预约至 ${formatDate(scheduledAt)}。`,
      );
      setSingleActionOpen(false);
      setSinglePreview(undefined);
      setSinglePreviewKey("");
      queryClient.invalidateQueries({ queryKey: ["waybills"] });
      queryClient.invalidateQueries({ queryKey: ["waybill-detail"] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch (error) {
      setSingleError(error instanceof Error ? error.message : "单条操作批准失败");
    } finally {
      setSingleBusy(false);
    }
  }
  const columns: ColumnDef<Waybill>[] = [
    {
      id: "select",
      header: "",
      cell: ({ row }) => (
        <input
          aria-label={`选择 ${row.original.waybillId}`}
          type="checkbox"
          checked={selected.has(selectedKey(row.original))}
          onChange={() => toggle(row.original)}
        />
      ),
    },
    {
      header: "账号 / 运单",
      cell: ({ row }) => (
        <button
          className="table-link primary-cell"
          onClick={() => setDetailTarget(row.original)}
        >
          <strong>{row.original.phone}</strong>
          <span>
            {row.original.driverName || "未识别司机"} · {row.original.accountPlateNo || "未配置车牌"}
          </span>
          <span>{row.original.waybillId}</span>
        </button>
      ),
    },
    {
      header: "线路",
      cell: ({ row }) => (
        <button
          className="route-cell"
          onClick={() => setDetailTarget(row.original)}
        >
          <strong>{row.original.departureName || "未知起点"}</strong>
          <ArrowRight size={14} />
          <strong>{row.original.destinationName || "未知终点"}</strong>
          <span>
            {row.original.transportMileageKm
              ? `${row.original.transportMileageKm} km`
              : "里程未知"}
          </span>
          <span>装货打卡：{formatDate(row.original.loadCheckin.time)}</span>
          <span>卸货打卡：{formatDate(row.original.unloadCheckin.time)}</span>
        </button>
      ),
    },
    {
      header: "货物 / 吨数",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.goodsName || "未知货物"}</strong>
          <span>
            {row.original.dispatchWeight
              ? `${row.original.dispatchWeight} 吨`
              : "使用账号默认吨数"}
          </span>
        </div>
      ),
    },
    {
      header: "当前状态",
      cell: ({ row }) => (
        <div className="primary-cell">
          <Badge tone={statusTone(row.original.status)}>
            {row.original.statusText}
          </Badge>
          <Badge tone={checkinStatusTone(row.original.checkinLocationStatus)}>
            {checkinStatusText(row.original.checkinLocationStatus)}
          </Badge>
          <span>{row.original.action}</span>
        </div>
      ),
    },
    {
      header: "可卸货时间 / 操作",
      cell: ({ row }) => {
        const overtime = unloadOvertime(row.original, now);
        const operation = waybillOperation(row.original);
        const OperationIcon =
          operation === "accept"
            ? Check
            : operation === "load"
              ? Upload
              : Download;
        return (
          <div className="primary-cell">
            <strong className={row.original.unloadEligible ? "text-success" : ""}>
              {countdown(row.original, now)}
            </strong>
            <span>{formatDate(row.original.earliestUnloadTime)}</span>
            {!row.original.earliestUnloadTime && row.original.estimatedUnloadTime && (
              <span>预估 {formatDate(row.original.estimatedUnloadTime)}（{row.original.transportReferenceSource === "accept_reference" ? "接单时间参考" : row.original.transportReferenceSource === "system_load_confirmation" ? "系统装货确认时间" : "平台装货时间"}）</span>
            )}
            {overtime && <span className="danger-text">{overtime}</span>}
            {operation && (
              <Button
                className="row-operation-button"
                variant="secondary"
                aria-label={`${operationName(operation)} ${row.original.waybillId}`}
                title={operation === "unload" ? "打开卸货预检并核对时间与运单条件" : `操作当前运单：${operationName(operation)}`}
                onClick={() => beginSingleAction(row.original)}
              >
                <OperationIcon size={14} />
                {operationName(operation)}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      header: "同步时间",
      cell: ({ row }) => formatDate(row.original.syncedAt),
    },
  ];
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>运单中心</h1>
          <p>按账号和状态筛选，查看位置、时间校验与服务端打卡记录。</p>
        </div>
        <div className="heading-actions">
          <Button disabled={!selected.size} onClick={openBatch}>
            <ListChecks size={16} />
            批量处理 {selected.size || ""}
          </Button>
        </div>
      </section>
      {singleMessage && <div className="notice-bar">{singleMessage}</div>}
      <section className="toolbar waybill-toolbar">
        <label className="search-box">
          <Search size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="搜索账号、司机、车牌、运单或地点"
          />
        </label>
        <Select
          aria-label="运单状态"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">全部状态</option>
          <option value="100">待接单</option>
          <option value="120,300,310">待装货</option>
          <option value="410,415">运输中</option>
          <option value="420,500,520">已完成</option>
        </Select>
        <Select
          aria-label="司机组"
          value={groupId}
          onChange={(event) => setGroupId(event.target.value)}
        >
          <option value="">全部司机组</option>
          {visibleGroups.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </Select>
        <Select
          aria-label="时间排序"
          value={sortBy === "default" ? "default" : `${sortBy}:${sortOrder}`}
          onChange={(event) => {
            const [field, order = "desc"] = event.target.value.split(":");
            setSortBy(
              field as "default" | "acceptTime" | "loadTime" | "unloadTime",
            );
            setSortOrder(order as "asc" | "desc");
          }}
        >
          <option value="default">默认排序</option>
          <option value="acceptTime:desc">接单时间：最新在前</option>
          <option value="acceptTime:asc">接单时间：最早在前</option>
          <option value="loadTime:desc">装货打卡：最新在前</option>
          <option value="loadTime:asc">装货打卡：最早在前</option>
          <option value="unloadTime:desc">卸货打卡：最新在前</option>
          <option value="unloadTime:asc">卸货打卡：最早在前</option>
        </Select>
        <span className="toolbar-count">
          {waybills.isFetching ? "加载中..." : `${waybills.data?.total ?? 0} 张运单`}
        </span>
      </section>
      <DataTable
        data={waybills.data?.items ?? []}
        columns={columns}
        loading={waybills.isLoading || waybills.isFetching}
        emptyTitle="没有匹配的运单"
        emptyDescription="先到账号管理同步账号，或调整筛选条件。"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={waybills.data?.total ?? 0}
        loading={waybills.isFetching}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
      <WaybillDrawer
        open={!!detailTarget}
        onClose={() => {
          setDetailTarget(undefined);
          setSingleActionOpen(false);
        }}
        waybill={detail.data ?? detailTarget}
      />
      <Dialog
        open={singleActionOpen}
        onOpenChange={(open) => {
          setSingleActionOpen(open);
          if (!open) {
            setSinglePreview(undefined);
            setSinglePreviewKey("");
            setSingleError("");
          }
        }}
        title={
          singleAction
            ? `确认${operationName(singleAction.operation)}操作`
            : "确认单条运单操作"
        }
        description="运单详情已在右侧展开，请再次核对后确认。"
        wide
        stacked
      >
        {singleAction && (
          <div className="single-operation-summary">
            <div>
              <span>操作</span>
              <strong>{operationName(singleAction.operation)}</strong>
            </div>
            <div>
              <span>司机账号</span>
              <strong>{singleAction.waybill.phone}</strong>
            </div>
            <div>
              <span>司机 / 车牌</span>
              <strong>
                {singleAction.waybill.driverName || "未识别司机"} ·{" "}
                {singleAction.waybill.accountPlateNo ||
                  singleAction.waybill.plateNo ||
                  "未配置车牌"}
              </strong>
            </div>
            <div>
              <span>运单号</span>
              <strong>{singleAction.waybill.waybillId}</strong>
            </div>
            <div className="single-operation-route">
              <span>线路</span>
              <strong>
                {singleAction.waybill.departureName || "未知起点"} →{" "}
                {singleAction.waybill.destinationName || "未知终点"}
              </strong>
            </div>
            <div>
              <span>当前状态</span>
              <Badge tone={statusTone(singleAction.waybill.status)}>
                {singleAction.waybill.statusText}
              </Badge>
            </div>
          </div>
        )}
        <section className="single-schedule-section">
          <div className="section-header">
            <div>
              <h2>执行方式</h2>
              <p>立即执行不增加调度等待；预约执行按北京时间到点运行。</p>
            </div>
          </div>
          <div className="segmented single-schedule-options">
            <button
              type="button"
              className={singleScheduleMode === "immediate" ? "active" : ""}
              disabled={singleBusy}
              onClick={() => changeSingleScheduleMode("immediate")}
            >
              <Play size={15} />
              立即执行
            </button>
            <button
              type="button"
              className={singleScheduleMode === "scheduled" ? "active" : ""}
              disabled={singleBusy}
              onClick={() => changeSingleScheduleMode("scheduled")}
            >
              <Clock3 size={15} />
              预约执行
            </button>
          </div>
          {singleScheduleMode === "scheduled" && (
            <div className="appointment-row">
              <Field label="预约时间（北京时间）" hint="到点后仍会重新校验运单状态和安全条件">
                <Input
                  type="datetime-local"
                  min={shanghaiAppointmentTime()}
                  value={singleScheduledAt}
                  onChange={(event) => {
                    setSingleScheduledAt(event.target.value);
                    setSingleTimeWarningConfirmed(false);
                    setSinglePreview(undefined);
                    setSinglePreviewKey("");
                    setSingleError("");
                  }}
                />
              </Field>
              <Button
                variant="secondary"
                disabled={singleBusy || !singleScheduledAt}
                onClick={() =>
                  singleAction &&
                  runSinglePreview(
                    singleAction,
                    "scheduled",
                    singleScheduledAt,
                  )
                }
              >
                <RefreshCw size={15} />
                检查预约时间
              </Button>
            </div>
          )}
        </section>
        <div className="danger-confirm">
          <AlertTriangle size={20} />
          <p>
            {singleScheduleMode === "immediate"
              ? "确认后立即进入执行队列；执行前仍会重新校验权限、运单状态、定位、时间和线路上下文。"
              : "确认后任务将在预约时间到点运行；执行前仍会重新校验权限、运单状态、定位、时间和线路上下文。"}
          </p>
        </div>
        {singleUnloadTimeWarning() && (
          <div className="danger-confirm">
            <AlertTriangle size={20} />
            <div>
              <p>{singleUnloadTimeWarning()} 卸货提交会登记已完成卸货；预检阻断不能通过勾选此项解除。</p>
              <label>
                <input type="checkbox" checked={singleTimeWarningConfirmed} onChange={(event) => setSingleTimeWarningConfirmed(event.target.checked)} />
                我已阅读时间警告并再次核对实际卸货情况
              </label>
            </div>
          </div>
        )}
        {singleBusy && !singlePreview ? (
          <LoadingState label="正在预检当前运单" />
        ) : singlePreview ? (
          <PreviewPanel preview={singlePreview} />
        ) : null}
        {singleError && <div className="inline-error">{singleError}</div>}
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setSingleActionOpen(false);
              setSinglePreview(undefined);
              setSinglePreviewKey("");
            }}
          >
            取消
          </Button>
          <Button
            onClick={approveSingleAction}
            disabled={
              singleBusy ||
              !singlePreview ||
              singlePreview.blockedCount > 0 ||
              (!!singleUnloadTimeWarning() && !singleTimeWarningConfirmed) ||
              singlePreviewKey !==
                singleRequestKey(singleScheduleMode, singleScheduledAt)
            }
          >
            {singleBusy ? (
              <Loader2 className="spin" size={16} />
            ) : (
              <Play size={16} />
            )}
            {singleAction
              ? `确认${singleScheduleMode === "immediate" ? "立即" : "预约"}${operationName(singleAction.operation)}`
              : "确认操作"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function WaybillDrawer({
  open,
  onClose,
  waybill,
}: {
  open: boolean;
  onClose: () => void;
  waybill?: Waybill;
}) {
  if (!waybill) return null;
  return (
    <Drawer
      open={open}
      onOpenChange={(value) => !value && onClose()}
      title={`运单 ${waybill.waybillId}`}
    >
      <WaybillDetailContent waybill={waybill} />
    </Drawer>
  );
}

function WaybillDetailContent({ waybill }: { waybill: Waybill }) {
  return (
    <>
      <div className="detail-status">
        <Badge tone={statusTone(waybill.status)}>{waybill.statusText}</Badge>
        <span>
          {waybill.phone} · {waybill.driverName || "未识别司机"}
        </span>
      </div>
      <section className="detail-section">
        <h3>运输线路</h3>
        {waybill.confirmedRouteData && (
          <div className="detail-status">
            <span>
              已确认补充资料：
              {waybill.confirmedRouteData.loadPoint && "装货地点（高德 GCJ-02）；"}
              {waybill.confirmedRouteData.unloadPoint && "卸货地点（高德 GCJ-02）；"}
              {waybill.confirmedRouteData.declaredDistanceKm && `声明里程 ${waybill.confirmedRouteData.declaredDistanceKm} km（${waybill.confirmedRouteData.distanceSource}）；`}
              地点坐标不代表实际打卡，平台已有数据优先使用。
            </span>
          </div>
        )}
        <div className="route-timeline">
          <LocationPoint
            title={waybill.departureName}
            address={waybill.departureAddress}
            lng={waybill.departureLng}
            lat={waybill.departureLat}
            time={waybill.loadTime}
            tone="load"
          />
          <div className="route-line">
            <span>
              {waybill.transportMileageKm
                ? `${waybill.transportMileageKm} km`
                : "距离未知"}
            </span>
          </div>
          <LocationPoint
            title={waybill.destinationName}
            address={waybill.destinationAddress}
            lng={waybill.destinationLng}
            lat={waybill.destinationLat}
            time={waybill.unloadTime}
            tone="unload"
          />
        </div>
      </section>
      <section className="detail-section">
        <h3>打卡位置</h3>
        <div className="detail-status">
          <Badge tone={checkinStatusTone(waybill.checkinLocationStatus)}>
            {checkinStatusText(waybill.checkinLocationStatus)}
          </Badge>
          <span>最近校验 {formatDate(waybill.checkinLocationCheckedAt)}</span>
        </div>
        <div className="route-timeline">
          <LocationPoint
            title="装货打卡"
            address={waybill.loadCheckin.address}
            lng={waybill.loadCheckin.lng}
            lat={waybill.loadCheckin.lat}
            time={waybill.loadCheckin.time}
            tone="load"
          />
          <div className="route-line" />
          <LocationPoint
            title="卸货打卡"
            address={waybill.unloadCheckin.address}
            lng={waybill.unloadCheckin.lng}
            lat={waybill.unloadCheckin.lat}
            time={waybill.unloadCheckin.time}
            tone="unload"
          />
        </div>
      </section>
      <section className="detail-section">
        <h3>时间校验</h3>
        <dl className="detail-grid">
          <div>
            <dt>装货时间</dt>
            <dd>{formatDate(waybill.loadTime)}</dd>
          </div>
          <div>
            <dt>最早卸货</dt>
            <dd>{formatDate(waybill.earliestUnloadTime)}</dd>
          </div>
          <div>
            <dt>系统装货成功确认</dt>
            <dd>{formatDate(waybill.systemLoadConfirmedAt)}</dd>
          </div>
          <div>
            <dt>预估卸货（70公里/小时）</dt>
            <dd>{formatDate(waybill.estimatedUnloadTime)}；{waybill.transportReferenceSource === "accept_reference" ? "接单时间参考，装货可能晚于接单" : waybill.transportReferenceSource === "system_load_confirmation" ? "系统装货成功确认时间参考" : "平台装货时间参考"}。预估不代表实际到达或可提交卸货。</dd>
          </div>
          <div>
            <dt>线路计划卸货</dt>
            <dd>{formatDate(waybill.plannedUnloadAt)}</dd>
          </div>
          <div>
            <dt>卸货窗口截止</dt>
            <dd>{formatDate(waybill.unloadWindowEndAt)}</dd>
          </div>
          <div>
            <dt>线路窗口状态</dt>
            <dd className={unloadOvertime(waybill, Date.now()) ? "danger-text" : ""}>
              {unloadOvertime(waybill, Date.now()) || "未超时"}
            </dd>
          </div>
          <div>
            <dt>最终调度卸货</dt>
            <dd>{formatDate(waybill.scheduledUnloadAt)}</dd>
          </div>
          <div>
            <dt>运输随机等待</dt>
            <dd>
              {waybill.transportJitterMinutes != null
                ? `${waybill.transportJitterMinutes} 分钟`
                : "-"}
            </dd>
          </div>
          <div>
            <dt>剩余时间</dt>
            <dd>{countdown(waybill, Date.now())}</dd>
          </div>
          <div>
            <dt>是否允许</dt>
            <dd>
              {waybill.unloadEligible ? (
                <Badge tone="success">允许卸货</Badge>
              ) : (
                <Badge tone="warning">尚未允许</Badge>
              )}
            </dd>
          </div>
        </dl>
      </section>
      <section className="detail-section">
        <h3>基础信息</h3>
        <dl className="detail-grid">
          <div>
            <dt>货物</dt>
            <dd>{waybill.goodsName || "-"}</dd>
          </div>
          <div>
            <dt>接单吨数</dt>
            <dd>
              {waybill.dispatchWeight ? `${waybill.dispatchWeight} 吨` : "-"}
            </dd>
          </div>
          <div>
            <dt>车牌</dt>
            <dd>{waybill.plateNo || "-"}</dd>
          </div>
          <div>
            <dt>接单时间</dt>
            <dd>{formatDate(waybill.acceptTime)}</dd>
          </div>
        </dl>
      </section>
      {waybill.raw && (
        <section className="detail-section">
          <h3>服务端快照</h3>
          <details className="json-details">
            <summary>查看原始 JSON</summary>
            <pre>{JSON.stringify(waybill.raw, null, 2)}</pre>
          </details>
        </section>
      )}
    </>
  );
}

function LocationPoint({
  title,
  address,
  lng,
  lat,
  time,
  tone,
}: {
  title: string;
  address: string;
  lng?: number;
  lat?: number;
  time?: string;
  tone: string;
}) {
  const href =
    lng != null && lat != null
      ? `https://uri.amap.com/marker?position=${lng},${lat}&name=${encodeURIComponent(title)}`
      : undefined;
  return (
    <div className="location-point">
      <span className={`location-pin pin-${tone}`}>
        <MapPin size={15} />
      </span>
      <div>
        <strong>{title || "未知地点"}</strong>
        <p>{address || "地址未知"}</p>
        <span>
          {lng != null && lat != null
            ? `${lng.toFixed(6)}, ${lat.toFixed(6)}`
            : "坐标未知"}
        </span>
        {time && <time>{formatDate(time)}</time>}
      </div>
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          title="在高德地图中打开"
        >
          <ExternalLink size={16} />
        </a>
      )}
    </div>
  );
}

function routeLegStateText(state: string) {
  return {
    pending: "尚未开始",
    complete: "已完成",
    completed: "已完成",
    "awaiting-accept-approval": "等待接单确认",
    "awaiting-load-approval": "等待装货确认",
    "awaiting-unload-approval": "等待卸货确认",
    accepting: "正在接单",
    loading: "正在装货",
    unloading: "正在卸货",
    "waiting-trip-interval": "等待下一趟接单间隔",
  }[state] ?? state;
}

function operationStatusText(status: string) {
  return {
    success: "成功",
    failed: "失败",
    blocked: "已阻断",
    expired: "已过期",
    scheduled: "等待计划时间",
    queued: "排队中",
    running: "执行中",
    reconciling: "结果复查中",
    cancelled: "已取消",
  }[status] ?? status;
}

async function downloadEvidenceReport(itemId: string) {
  const response = await fetch(`/api/evidence/${itemId}`, { credentials: "include" });
  if (!response.ok) return;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${itemId}-report.md`;
  link.click();
  URL.revokeObjectURL(url);
}

function RouteLegDetails({
  legs,
  onWaybill,
}: {
  legs: RouteRunDetail["legs"];
  onWaybill: (waybill: Waybill) => void;
}) {
  const defaultOpen = legs.find((leg) => leg.isAnomaly)?.index ?? -1;
  const [openLeg, setOpenLeg] = useState(defaultOpen);
  useEffect(() => {
    setOpenLeg(legs.find((leg) => leg.isAnomaly)?.index ?? -1);
  }, [legs]);
  return (
    <div className="route-detail-legs">
      {legs.map((leg) => {
        const open = openLeg === leg.index;
        const attempts = Object.values(leg.operations).flat();
        return (
          <section
            className={`route-detail-leg ${leg.isAnomaly ? "is-anomaly" : ""}`}
            key={leg.index}
          >
            <button
              type="button"
              className="route-detail-leg-head"
              aria-expanded={open}
              onClick={() => setOpenLeg(open ? -1 : leg.index)}
            >
              <span className="route-detail-leg-number">{leg.number}</span>
              <span>
                <strong>{leg.fromName || "未知起点"} → {leg.toName || "未知终点"}</strong>
                <small>
                  {routeLegStateText(leg.state)} · {leg.waybillId || "未绑定运单"}
                </small>
              </span>
              <Badge tone={leg.isAnomaly ? "danger" : leg.waybill?.unloadTime ? "success" : "neutral"}>
                {leg.isAnomaly ? "需排查" : routeLegStateText(leg.state)}
              </Badge>
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {open && (
              <div className="route-detail-leg-body">
                <dl className="detail-grid compact-detail-grid">
                  <div><dt>线路</dt><dd>{leg.declaredDistanceKm ?? "-"} km</dd></div>
                  <div><dt>最低运输</dt><dd>{leg.minimumTransportHours ?? "-"} 小时</dd></div>
                  <div><dt>随机等待</dt><dd>{leg.jitterMinutes != null ? `${leg.jitterMinutes} 分钟` : "-"}</dd></div>
                  <div><dt>绑定运单</dt><dd>{leg.waybillId || "未绑定"}</dd></div>
                  <div><dt>候选数量</dt><dd>{leg.binding?.candidateCount ?? 0}</dd></div>
                  <div><dt>选择依据</dt><dd>{leg.binding?.selectionSource === "platform_dispatch_time" ? "平台派单时间" : leg.binding?.selectionSource === "waybill_id_fallback" ? "运单号倒序回退" : leg.binding?.selectionSource || "-"}</dd></div>
                  <div><dt>运单绑定</dt><dd>{leg.binding?.status === "matched" ? "已匹配" : leg.binding?.status === "invalid" ? "绑定已失效" : "等待匹配"}</dd></div>
                  <div><dt>计划装货</dt><dd>{formatDate(leg.plannedLoadAt)}</dd></div>
                  <div><dt>装货窗口</dt><dd>{formatDate(leg.plannedLoadWindowStart)} 至 {formatDate(leg.plannedLoadWindowEnd)}</dd></div>
                  <div><dt>计划卸货</dt><dd>{formatDate(leg.plannedUnloadAt)}</dd></div>
                  <div><dt>卸货窗口</dt><dd>{formatDate(leg.plannedUnloadWindowStart)} 至 {formatDate(leg.plannedUnloadWindowEnd)}</dd></div>
                  <div><dt>装货地址</dt><dd>{leg.fromAddress || "地址未知"}</dd></div>
                  <div><dt>卸货地址</dt><dd>{leg.toAddress || "地址未知"}</dd></div>
                </dl>
                <div className="route-frozen-schedules">
                  {(["accept", "load", "unload"] as const).map((operation) => {
                    const schedule = leg.operationSchedule?.[operation];
                    return (
                      <div key={operation}>
                        <strong>{operationName(operation)}冻结计划</strong>
                        <span>{formatDate(schedule?.scheduledAt)}</span>
                        <small>窗口 {formatDate(schedule?.windowStart)} 至 {formatDate(schedule?.windowEnd)}</small>
                        <small>截止 {formatDate(schedule?.deadlineAt)}</small>
                      </div>
                    );
                  })}
                </div>
                {leg.waybill ? (
                  <>
                    <button
                      type="button"
                      className="route-waybill-summary"
                      onClick={() => onWaybill(leg.waybill!)}
                    >
                      <span><strong>{leg.waybill.waybillId}</strong><small>{leg.waybill.statusText}（{leg.waybill.status}）</small></span>
                      <span><small>接单 {formatDate(leg.waybill.acceptTime)}</small><small>装货 {formatDate(leg.waybill.loadTime)}</small><small>卸货 {formatDate(leg.waybill.unloadTime)}</small></span>
                      <Badge tone={checkinStatusTone(leg.waybill.checkinLocationStatus)}>
                        {checkinStatusText(leg.waybill.checkinLocationStatus)}
                      </Badge>
                      <ChevronRight size={16} />
                    </button>
                    <div className="route-checkin-summary">
                      <span><strong>装货打卡</strong>{leg.waybill.loadCheckin.address || "地址未回显"}</span>
                      <span><strong>卸货打卡</strong>{leg.waybill.unloadCheckin.address || "地址未回显"}</span>
                      <span><strong>最早安全卸货</strong>{formatDate(leg.waybill.earliestUnloadTime)}</span>
                    </div>
                  </>
                ) : (
                  <div className="route-detail-empty">{leg.missingReason || "该路段暂无运单记录"}</div>
                )}
                <div className="route-operation-list">
                  {(["accept", "load", "unload"] as const).map((operation) => (
                    <div className="route-operation-group" key={operation}>
                      <strong>{operationName(operation)}</strong>
                      {leg.operations[operation].map((attempt) => (
                        <div className="route-operation-attempt" key={attempt.id}>
                          <span>第 {attempt.attemptNumber} 次</span>
                          <Badge tone={statusTone(attempt.status)}>{operationStatusText(attempt.status)}</Badge>
                          <small>计划 {formatDate(attempt.scheduledAt)}</small>
                          <small>开始 {formatDate(attempt.startedAt)}</small>
                          <small>完成 {formatDate(attempt.finishedAt)}</small>
                          {attempt.error && <p className="danger-text">{attempt.error}</p>}
                          {attempt.evidenceAvailable && (
                            <IconButton
                              label={`下载${operationName(operation)}证据`}
                              onClick={() => downloadEvidenceReport(attempt.id)}
                            >
                              <FileDown size={14} />
                            </IconButton>
                          )}
                        </div>
                      ))}
                      {!leg.operations[operation].length && <small className="muted-text">未执行</small>}
                    </div>
                  ))}
                </div>
                {!attempts.length && <span className="muted-text">该路段没有关联操作任务</span>}
              </div>
            )}
          </section>
        );
      })}
      {!legs.length && <EmptyState title="没有路段详情" description="该司机当天未创建线路任务，或历史记录无法恢复。" />}
    </div>
  );
}

export function BatchPage({
  selected,
  setSelected,
  targets,
  currentUser,
}: {
  selected: Set<string>;
  setSelected: (value: Set<string>) => void;
  targets: Array<{ accountId: string; waybillId: string }>;
  currentUser: CurrentUser;
}) {
  const queryClient = useQueryClient();
  const [operation, setOperation] = useState<"accept" | "load" | "unload">(
    "accept",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<50 | 100 | 200>(50);
  const operationStatuses = {
    accept: "100",
    load: "120,300,310",
    unload: "410,415",
  }[operation];
  const waybills = useQuery({
    queryKey: ["waybills", "batch", currentUser.role, operation, page, pageSize],
    queryFn: () =>
      api<PagedResponse<Waybill>>(
        `/api/waybills?status=${encodeURIComponent(operationStatuses)}&routeOnly=${currentUser.role !== "super_admin" && operation !== "accept"}&page=${page}&pageSize=${pageSize}`,
      ),
  });
  const [tons, setTons] = useState("30");
  const [offset, setOffset] = useState("150");
  const [minimumHours, setMinimumHours] = useState("");
  const [jitterMin, setJitterMin] = useState("5");
  const [jitterMax, setJitterMax] = useState("20");
  const [emergencyOverride, setEmergencyOverride] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState("");
  const [preview, setPreview] = useState<BatchPreview>();
  const [approveOpen, setApproveOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const batchWaybills = useMemo(
    () => waybills.data?.items ?? [],
    [waybills.data?.items],
  );
  const eligible = useMemo(
    () =>
      batchWaybills.filter(
        (item) =>
          ({
            accept: item.status === "100",
            load: ["120", "300", "310"].includes(item.status),
            unload: ["410", "415"].includes(item.status),
          })[operation],
      ),
    [batchWaybills, operation],
  );

  useEffect(() => {
    setPage(1);
  }, [operation, pageSize]);

  useEffect(() => {
    const selectedWaybills = batchWaybills.filter((item) =>
      selected.has(selectedKey(item)),
    );
    const operations = new Set(
      selectedWaybills.map((item) =>
        item.status === "100"
          ? "accept"
          : ["120", "300", "310"].includes(item.status)
            ? "load"
            : ["410", "415"].includes(item.status)
              ? "unload"
              : "",
      ),
    );
    operations.delete("");
    if (operations.size === 1)
      setOperation(Array.from(operations)[0] as "accept" | "load" | "unload");
  }, [selected, batchWaybills]);

  function toggle(item: Waybill) {
    const next = new Set(selected);
    const key = selectedKey(item);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
    setPreview(undefined);
  }
  async function createPreview(customTargets = targets) {
    setBusy(true);
    setMessage("");
    try {
      const enrichedTargets = await Promise.all(customTargets.map(async (target) => {
        const waybill = batchWaybills.find(
          (item) =>
            item.accountId === target.accountId &&
            item.waybillId === target.waybillId,
        );
        if (waybill) return { ...target, routeRunId: waybill.routeRunId };
        const detail = await api<Waybill>(
          `/api/waybills/${target.waybillId}?accountId=${target.accountId}`,
        );
        return { ...target, routeRunId: detail.routeRunId };
      }));
      if (
        currentUser.role !== "super_admin" &&
        operation !== "accept" &&
        enrichedTargets.some((target) => !target.routeRunId)
      ) {
        throw new Error("所选运单不属于当前可见的 active 线路，请从线路任务重新进入");
      }
      setPreview(
        await post<BatchPreview>("/api/batches/preview", {
          operation,
          targets: enrichedTargets,
          ...(operation === "unload" ? { tons: Number(tons) } : {}),
          locationMode: "auto-offset",
          offsetMeters: Number(offset),
          minimumTransportHours: minimumHours ? Number(minimumHours) : null,
          jitterMinMinutes: Number(jitterMin || 5),
          jitterMaxMinutes: Number(jitterMax || 20),
          emergencyOverride,
          emergencyReason,
        }),
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "预检失败");
    } finally {
      setBusy(false);
    }
  }
  async function approve() {
    if (!preview) return;
    setBusy(true);
    try {
      await post(`/api/batches/${preview.id}/approve`, {
        previewHash: preview.previewHash,
        ...(emergencyOverride ? { adminPassword: password } : {}),
      });
      setApproveOpen(false);
      setPassword("");
      setSelected(new Set());
      setPreview(undefined);
      setEmergencyOverride(false);
      setEmergencyReason("");
      setMessage("批次已批准，任务将按照每台车冻结的随机时间执行。");
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "审批失败");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>批量操作</h1>
          <p>先勾选目标并运行预检，核对完整明细后确认真实业务操作。</p>
        </div>
      </section>
      {message && <div className="notice-bar">{message}</div>}
      <section className="operation-layout">
        <div className="operation-config">
          <div className="segmented">
            {(["accept", "load", "unload"] as const).map((item) => (
              <button
                key={item}
                className={operation === item ? "active" : ""}
                onClick={() => {
                  setOperation(item);
                  setPreview(undefined);
                  setEmergencyOverride(false);
                  setEmergencyReason("");
                }}
              >
                {operationName(item)}
              </button>
            ))}
          </div>
          <div className="config-grid">
            {operation === "unload" && (
              <Field label="卸货吨数">
                <Input
                  type="number"
                  min="0.1"
                  max="100"
                  value={tons}
                  onChange={(event) => setTons(event.target.value)}
                />
              </Field>
            )}
            {operation !== "accept" && (
              <Field label="定位偏移（米）" hint="不得超过 500 米">
                <Input
                  type="number"
                  min="0"
                  max="500"
                  value={offset}
                  onChange={(event) => setOffset(event.target.value)}
                />
              </Field>
            )}
            {operation !== "accept" && (
              <>
                {operation === "unload" && (
                  <Field label="最低运输时长（小时）">
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={minimumHours}
                      onChange={(event) => setMinimumHours(event.target.value)}
                      placeholder="线路任务使用快照值"
                    />
                  </Field>
                )}
                <Field label="随机等待下限（分钟）">
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={jitterMin}
                    onChange={(event) => setJitterMin(event.target.value)}
                  />
                </Field>
                <Field label="随机等待上限（分钟）">
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={jitterMax}
                    onChange={(event) => setJitterMax(event.target.value)}
                  />
                </Field>
              </>
            )}
          </div>
          {currentUser.role === "super_admin" && operation === "unload" && (
            <div className="emergency-config">
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={emergencyOverride}
                  onChange={(event) => {
                    setEmergencyOverride(event.target.checked);
                    setPreview(undefined);
                  }}
                />
                跨日紧急卸货
              </label>
              {emergencyOverride && (
                <Field label="紧急原因" hint="不执行 30 分钟随机调度，仍校验运输时长、500 米定位和地址回显">
                  <textarea
                    className="textarea compact-textarea"
                    value={emergencyReason}
                    onChange={(event) => setEmergencyReason(event.target.value)}
                    placeholder="填写必须立即卸货的具体原因"
                  />
                </Field>
              )}
            </div>
          )}
          <div className="safety-note">
            <ShieldAlert size={17} />
            <span>
              执行前会重新登录并查询服务端状态。运单状态变化、位置超过 500
              米或时间不足时自动停止。
            </span>
          </div>
        </div>
        <div className="target-list">
          <div className="section-header">
            <div>
              <h2>目标运单</h2>
              <p>
                当前操作可选 {waybills.data?.total ?? 0} 张，已选 {selected.size} 张
              </p>
            </div>
            {selected.size > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSelected(new Set());
                  setPreview(undefined);
                }}
              >
                清空
              </Button>
            )}
          </div>
          {waybills.isLoading || waybills.isFetching ? (
            <LoadingState label="正在加载可操作运单" />
          ) : (
            <div className="compact-list">
              {eligible.map((item) => (
                <label key={selectedKey(item)} className="target-row">
                  <input
                    type="checkbox"
                    checked={selected.has(selectedKey(item))}
                    onChange={() => toggle(item)}
                  />
                  <div>
                    <strong>
                      {item.phone} · {item.waybillId}
                    </strong>
                    <span>
                      {item.departureName} → {item.destinationName}
                    </span>
                  </div>
                  <Badge tone={statusTone(item.status)}>{item.statusText}</Badge>
                </label>
              ))}
              {!eligible.length && (
                <EmptyState
                  title="没有可操作运单"
                  description="同步账号或切换操作类型后重试。"
                />
              )}
            </div>
          )}
          <Pagination
            page={page}
            pageSize={pageSize}
            total={waybills.data?.total ?? 0}
            loading={waybills.isFetching}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          <Button
            className="full-button"
            onClick={() => createPreview()}
            disabled={
              !targets.length ||
              busy ||
              Number(jitterMax || 0) < Number(jitterMin || 0) ||
              (emergencyOverride && !emergencyReason.trim())
            }
          >
            {busy ? (
              <Loader2 className="spin" size={16} />
            ) : (
              <ListChecks size={16} />
            )}
            运行批量预检
          </Button>
        </div>
      </section>
      {preview && (
        <PreviewPanel
          preview={preview}
          onApprove={() => setApproveOpen(true)}
        />
      )}
      <Dialog
        open={approveOpen}
        onOpenChange={(open) => {
          setApproveOpen(open);
          if (!open) setPassword("");
        }}
        title="确认真实业务操作"
        description={
          emergencyOverride
            ? `将立即执行 ${preview?.allowedCount ?? 0} 个紧急卸货任务。`
            : `将按冻结的随机时间执行 ${preview?.allowedCount ?? 0} 个${operationName(operation)}任务。`
        }
        wide
      >
        <div className="danger-confirm">
          <AlertTriangle size={20} />
          <p>
            {emergencyOverride
              ? "紧急卸货会写入独立审计记录，仍会重新校验业务状态、最低运输时长、定位和打卡地址。"
              : "系统会在每台车的计划时间到达后提交真实业务请求，执行前仍会重新校验状态、位置、业务日和截止时间。"}
          </p>
        </div>
        {preview && <PreviewPanel preview={preview} />}
        {emergencyOverride && (
          <Field label="当前用户密码" hint="跨日紧急卸货仍需要密码确认">
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoFocus
            />
          </Field>
        )}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setApproveOpen(false)}>
            取消
          </Button>
          <Button
            onClick={approve}
            disabled={busy || !!preview?.blockedCount || (emergencyOverride && password.length < 8)}
          >
            {busy ? "确认中..." : "确认并执行"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function PreviewPanel({
  preview,
  onApprove,
}: {
  preview: BatchPreview;
  onApprove?: () => void;
}) {
  const items = [...preview.items].sort((left, right) =>
    (left.scheduledAt || "").localeCompare(right.scheduledAt || ""),
  );
  return (
    <section className="preview-panel">
      <div className="section-header">
        <div>
          <h2>预检结果</h2>
          <p>有效期至 {formatDate(preview.expiresAt)}</p>
        </div>
        <div className="preview-counts">
          <Badge tone="success">{preview.allowedCount} 可执行</Badge>
          {preview.blockedCount > 0 && (
            <Badge tone="danger">{preview.blockedCount} 阻断</Badge>
          )}
        </div>
      </div>
      <div className="preview-table">
        {items.map((item) => (
          <div
            key={`${item.accountId}-${item.waybillId}`}
            className="preview-row"
          >
            <span
              className={`preview-icon ${item.allowed ? "success" : "danger"}`}
            >
              {item.allowed ? <Check size={16} /> : <X size={16} />}
            </span>
            <div>
              <strong>
                {item.phone} · {item.waybillId}
              </strong>
              <span>{item.route}</span>
              <span>
                {item.driverName || "未填写司机"} · {operationName((item.operation || preview.operation) as "accept" | "load" | "unload")}
                {item.statusText ? ` · ${item.statusText}` : ""}
              </span>
            </div>
            <div className="preview-notes">
              {item.scheduledAt && (
                <span className="schedule-primary">
                  第 {item.scheduleSequence ?? "-"}/{item.scheduleCount ?? "-"} 位 · 计划 {formatDate(item.scheduledAt)}
                </span>
              )}
              {item.scheduleWindowStart && item.scheduleWindowEnd && (
                <span>
                  窗口 {formatDate(item.scheduleWindowStart)} 至 {formatDate(item.scheduleWindowEnd)}
                </span>
              )}
              {item.scheduleDeadlineAt && (
                <span>硬截止 {formatDate(item.scheduleDeadlineAt)}</span>
              )}
              {item.transportJitterMinutes != null && item.operation !== "accept" && (
                <span>本单运输随机等待 {item.transportJitterMinutes} 分钟</span>
              )}
              {item.emergencyReason && <span>紧急原因：{item.emergencyReason}</span>}
              {item.blockers.map((note) => (
                <span className="error-text" key={note}>
                  {note}
                </span>
              ))}
              {item.warnings.map((note) => (
                <span key={note}>{note}</span>
              ))}
            </div>
            <strong>
              {item.emergencyOverride
                ? "紧急"
                : item.operation === "unload" && item.tons != null
                  ? `${item.tons} 吨`
                  : "运单值"}
            </strong>
          </div>
        ))}
      </div>
      <div className="preview-footer">
        <span>预检快照哈希：{preview.previewHash.slice(0, 16)}...</span>
        {onApprove && (
          <Button onClick={onApprove} disabled={preview.blockedCount > 0}>
            <Play size={16} />
            查看并确认
          </Button>
        )}
      </div>
    </section>
  );
}

export function SchedulesPage({
  currentUser,
  openReports,
}: {
  currentUser: CurrentUser;
  openReports?: (planId?: string) => void;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<50 | 100 | 200>(50);
  const plans = useQuery({
    queryKey: ["route-auto-plans", page, pageSize],
    queryFn: () => api<PagedResponse<RouteAutoPlan>>(`/api/route-auto-plans?page=${page}&pageSize=${pageSize}`),
    refetchInterval: 15_000,
  });
  const templates = useQuery({
    queryKey: ["route-templates", "schedule-management"],
    queryFn: () => api<RouteTemplate[]>(
      currentUser.role === "super_admin"
        ? "/api/route-templates?includeArchived=true"
        : "/api/route-templates",
    ),
    enabled: currentUser.capabilities.manageRouteTemplates,
  });
  const groups = useQuery({
    queryKey: ["admin-groups", "schedule-management"],
    queryFn: () => api<DriverGroup[]>("/api/admin/groups"),
    enabled: currentUser.capabilities.manageAccounts,
  });
  const users = useQuery({
    queryKey: ["admin-users", "schedule-management"],
    queryFn: () => api<AdminUser[]>("/api/admin/users"),
    enabled: currentUser.role === "super_admin",
  });
  const accounts = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api<Account[]>("/api/accounts"),
    enabled: currentUser.capabilities.manageAccounts,
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RouteAutoPlan>();
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [authorizedUserId, setAuthorizedUserId] = useState("");
  const [scheduleType, setScheduleType] = useState<"daily" | "once">("daily");
  const [runDate, setRunDate] = useState(shanghaiDateAfter(1));
  const [startDate, setStartDate] = useState("");
  const [executionMode, setExecutionMode] = useState<"continuous" | "scheduled">("continuous");
  const [prepareTime, setPrepareTime] = useState("07:45");
  const [deadlineTime, setDeadlineTime] = useState("09:00");
  const [tripIntervalMinMinutes, setTripIntervalMinMinutes] = useState(30);
  const [tripIntervalMaxMinutes, setTripIntervalMaxMinutes] = useState(60);
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
  const [driverSearch, setDriverSearch] = useState("");
  const [conflicts, setConflicts] = useState<Record<string, Array<{ reason: string; planName?: string; templateName?: string }>>>({});
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  function openForm(plan?: RouteAutoPlan) {
    const defaultTemplate = templates.data?.find((item) => item.status === "active");
    const defaultGroup = groups.data?.find((item) => !item.isSystem);
    const defaultUser = currentUser.role === "super_admin"
      ? users.data?.find((item) => item.role === "sub_account" && item.enabled)
      : currentUser;
    setEditing(plan);
    setName(plan?.name ?? "");
    setTemplateId(plan?.templateId ?? defaultTemplate?.id ?? "");
    setGroupId(plan?.groupId ?? defaultGroup?.id ?? "");
    setAuthorizedUserId(plan?.authorizedUserId ?? defaultUser?.id ?? "");
    setScheduleType(plan?.scheduleType ?? "daily");
    setRunDate(plan?.runDate ?? shanghaiDateAfter(1));
    setStartDate(plan?.startDate ?? "");
    setExecutionMode(plan?.executionMode ?? "continuous");
    setPrepareTime(plan?.prepareTime ?? "07:45");
    setDeadlineTime(plan?.catchupDeadlineTime ?? "09:00");
    setTripIntervalMinMinutes(plan?.tripIntervalMinMinutes ?? 30);
    setTripIntervalMaxMinutes(plan?.tripIntervalMaxMinutes ?? 60);
    setSelectedAccountIds(new Set(plan?.accountIds ?? []));
    setDriverSearch("");
    setConflicts({});
    setEnabled(plan?.enabled ?? true);
    setFormOpen(true);
  }

  const groupAccounts = useMemo(
    () => {
      const actor = currentUser.role === "super_admin"
        ? users.data?.find((user) => user.id === authorizedUserId)
        : currentUser;
      return (accounts.data ?? []).filter((account) => actor?.groupIds.includes(account.groupId));
    },
    [accounts.data, authorizedUserId, currentUser, users.data],
  );
  const visibleGroupAccounts = useMemo(() => {
    const keyword = driverSearch.trim().toLowerCase();
    if (!keyword) return groupAccounts;
    return groupAccounts.filter((account) =>
      [account.phone, account.driverName, account.plateNo, account.groupName]
        .some((value) => value.toLowerCase().includes(keyword)),
    );
  }, [driverSearch, groupAccounts]);
  const availableAccountIds = groupAccounts
    .filter((account) => account.enabled && !conflicts[account.id]?.length)
    .map((account) => account.id);
  const allAvailableSelected = availableAccountIds.length > 0
    && availableAccountIds.every((accountId) => selectedAccountIds.has(accountId));
  const selectedConflictCount = Array.from(selectedAccountIds).filter(
    (accountId) => conflicts[accountId]?.length,
  ).length;

  useEffect(() => {
    if (!formOpen || !groupId || !groupAccounts.length) {
      setConflicts({});
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setConflictsLoading(true);
      try {
        const result = await post<{ conflicts: Record<string, Array<{ reason: string; planName?: string; templateName?: string }>> }>(
          "/api/route-auto-plans/conflicts",
          {
            planId: editing?.id ?? null,
            scheduleType,
            runDate: scheduleType === "once" ? runDate : null,
            accountIds: groupAccounts.map((account) => account.id),
          },
        );
        if (!cancelled) setConflicts(result.conflicts);
      } catch (error) {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "读取司机冲突失败");
      } finally {
        if (!cancelled) setConflictsLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentUser.role, editing?.id, formOpen, groupAccounts, groupId, runDate, scheduleType]);

  async function savePlan() {
    setBusy(true);
    setMessage("");
    const payload = {
      name,
      enabled,
      groupId,
      authorizedUserId,
      scheduleType,
      runDate: scheduleType === "once" ? runDate : null,
      startDate: scheduleType === "daily" ? startDate || null : null,
      executionMode,
      prepareTime,
      catchupDeadlineTime: deadlineTime,
      tripIntervalMinMinutes,
      tripIntervalMaxMinutes,
      accountIds: Array.from(selectedAccountIds),
    };
    try {
      if (editing) await put(`/api/route-auto-plans/${editing.id}`, payload);
      else await post("/api/route-auto-plans", { templateId, ...payload });
      setFormOpen(false);
      setMessage(editing ? "定时任务已更新" : "定时任务已创建");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["route-auto-plans"] }),
        queryClient.invalidateQueries({ queryKey: ["accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["route-daily-automation"] }),
      ]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存定时任务失败");
    } finally {
      setBusy(false);
    }
  }

  async function deletePlan(plan: RouteAutoPlan) {
    if (!window.confirm(`确认删除定时任务“${plan.name}”？已生成的线路和运行记录会保留。`)) return;
    try {
      await del(`/api/route-auto-plans/${plan.id}`);
      setMessage("定时任务已删除，历史线路和运行记录已保留");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["route-auto-plans"] }),
        queryClient.invalidateQueries({ queryKey: ["accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["route-daily-automation"] }),
      ]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "删除定时任务失败");
    }
  }

  const columns: ColumnDef<RouteAutoPlan>[] = [
    {
      header: "任务 / 线路",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.name}</strong>
          <span>{row.original.templateName}</span>
        </div>
      ),
    },
    {
      header: "执行方式",
      cell: ({ row }) => (
        <div className="primary-cell">
          <Badge tone={row.original.scheduleType === "daily" ? "success" : "info"}>
            {row.original.scheduleType === "daily" ? "每日" : "指定日期"}
          </Badge>
          <span>{row.original.scheduleType === "daily" ? (row.original.startDate ? `${row.original.startDate} 起每日运行` : "每天运行") : row.original.runDate}</span>
          {row.original.executionMode === "scheduled" && <span>参考计划时间逐段执行</span>}
        </div>
      ),
    },
    {
      header: "司机范围",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.groupName}</strong>
          <span>{row.original.boundAccountCount} 名司机 · {row.original.authorizedDisplayName || row.original.authorizedUsername}</span>
        </div>
      ),
    },
    {
      header: "准备 / 截止",
      cell: ({ row }) => `${row.original.prepareTime} / ${row.original.catchupDeadlineTime}`,
    },
    {
      header: "趟间随机等待",
      cell: ({ row }) => `${row.original.tripIntervalMinMinutes}–${row.original.tripIntervalMaxMinutes} 分钟`,
    },
    {
      header: "状态",
      cell: ({ row }) => (
        <div className="primary-cell">
          <Badge tone={row.original.enabled ? "success" : "neutral"}>{row.original.enabled ? "已启用" : "已暂停"}</Badge>
          {row.original.lastError && <span className="danger-text">{row.original.lastError}</span>}
        </div>
      ),
    },
    {
      header: "操作",
      cell: ({ row }) => currentUser.capabilities.manageRouteTemplates ? (
        <div className="inline-actions">
          {openReports && <Button variant="ghost" onClick={() => openReports(row.original.id)}>日报</Button>}
          <Button variant="ghost" onClick={() => openForm(row.original)}>编辑</Button>
          <Button variant="ghost" onClick={() => deletePlan(row.original)}><Trash2 size={14} />删除</Button>
        </div>
      ) : openReports ? <Button variant="ghost" onClick={() => openReports(row.original.id)}>查看日报</Button> : <span className="muted-text">只读</span>,
    },
  ];

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>定时任务</h1>
          <p>管理线路的每日或指定日期自动任务，并查看绑定的司机范围。</p>
        </div>
        {currentUser.capabilities.manageRouteTemplates && (
          <Button onClick={() => openForm()}><Plus size={16} />创建定时任务</Button>
        )}
      </section>
      {message && <div className="notice-bar">{message}</div>}
      <DataTable
        data={plans.data?.items ?? []}
        columns={columns}
        loading={plans.isLoading}
        emptyTitle="尚未创建定时任务"
        emptyDescription="创建后，系统会在准备时间为绑定司机生成当天线路。"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={plans.data?.total ?? 0}
        loading={plans.isFetching}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
      <Dialog
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editing ? "编辑定时任务" : "创建定时任务"}
        description="任务按北京时间准备；上一趟卸货完成后，独立随机等待设定时长，再接下一趟。"
        wide
      >
        <div className="config-grid">
          <Field label="任务名称">
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：肇庆怡宝每日运输" />
          </Field>
          <Field label="线路">
            <Select value={templateId} disabled={!!editing} onChange={(event) => setTemplateId(event.target.value)}>
              <option value="">请选择</option>
              {templates.data?.filter((item) => item.status === "active").map((item) => (
                <option value={item.id} key={item.id}>{item.name}</option>
              ))}
              {editing && !templates.data?.some((item) => item.id === editing.templateId && item.status === "active") && (
                <option value={editing.templateId}>{editing.templateName}</option>
              )}
            </Select>
          </Field>
          <Field label="执行方式">
            <div className="segmented-control">
              <Button type="button" variant={scheduleType === "daily" ? "primary" : "secondary"} onClick={() => setScheduleType("daily")}>每日</Button>
              <Button type="button" variant={scheduleType === "once" ? "primary" : "secondary"} onClick={() => setScheduleType("once")}>指定日期</Button>
            </div>
          </Field>
          {scheduleType === "once" && (
            <Field label="执行日期" hint="Asia/Shanghai">
              <Input type="date" min={todayInShanghai()} value={runDate} onChange={(event) => setRunDate(event.target.value)} />
            </Field>
          )}
          {scheduleType === "daily" && (
            <Field label="开始日期" hint="北京时间；留空表示立即生效。">
              <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </Field>
          )}
          <Field label="线路推进方式" hint="参考计划时间：超过计划窗口后顺延，缺单时当天继续等待；仍须完成上一段并满足趟间等待及运输时长。">
            <Select value={executionMode} onChange={(event) => setExecutionMode(event.target.value as "continuous" | "scheduled")}>
              <option value="continuous">完成后连续推进</option>
              <option value="scheduled">参考计划时间逐段执行</option>
            </Select>
          </Field>
          <Field label="计划所属组" hint="仅用于计划归类，可选择授权范围内其他分组的司机。">
            <Select
              value={groupId}
              onChange={(event) => {
                setGroupId(event.target.value);
                setDriverSearch("");
              }}
            >
              <option value="">请选择</option>
              {groups.data?.filter((group) => !group.isSystem).map((group) => (
                <option value={group.id} key={group.id}>{group.name} · {group.accountCount} 人</option>
              ))}
            </Select>
          </Field>
          <Field label="授权管理员">
            {currentUser.role === "super_admin" ? (
              <Select value={authorizedUserId} onChange={(event) => { setAuthorizedUserId(event.target.value); setSelectedAccountIds(new Set()); }}>
                <option value="">请选择</option>
                {users.data?.filter((user) => user.role === "sub_account" && user.enabled).map((user) => (
                  <option value={user.id} key={user.id}>{user.displayName || user.username} · {user.username}</option>
                ))}
              </Select>
            ) : (
              <Select value={currentUser.id} disabled>
                <option value={currentUser.id}>{currentUser.displayName || currentUser.username} · {currentUser.username}</option>
              </Select>
            )}
          </Field>
          <Field label="准备时间" hint="到点读取司机并同步账号">
            <Input type="time" value={prepareTime} onChange={(event) => setPrepareTime(event.target.value)} />
          </Field>
          <Field label="首段补偿截止" hint={executionMode === "scheduled" ? "参考计划时间模式允许顺延至当天结束，此截止仅用于连续推进模式。" : "超过后不再新开当天线路"}>
            <Input type="time" value={deadlineTime} onChange={(event) => setDeadlineTime(event.target.value)} />
          </Field>
          <Field label="趟间最短等待（分钟）" hint="0–1440；首次接单无需等待。">
            <Input type="number" min={0} max={1440} step={1} value={tripIntervalMinMinutes} onChange={(event) => setTripIntervalMinMinutes(Number(event.target.value))} />
          </Field>
          <Field label="趟间最长等待（分钟）" hint="不得小于最短等待；每趟独立随机，保存后后续接单生效。">
            <Input type="number" min={tripIntervalMinMinutes} max={1440} step={1} value={tripIntervalMaxMinutes} onChange={(event) => setTripIntervalMaxMinutes(Number(event.target.value))} />
          </Field>
        </div>
        <div className="schedule-driver-picker">
          <div className="section-header schedule-driver-heading">
            <div>
              <h3>执行司机</h3>
              <p>已选择 {selectedAccountIds.size} / {groupAccounts.length} 人；名单保存后不会因手机号排序或新增司机而变化。</p>
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={!availableAccountIds.length}
              onClick={() => {
                if (allAvailableSelected) setSelectedAccountIds(new Set());
                else setSelectedAccountIds(new Set(availableAccountIds));
              }}
            >
              {allAvailableSelected ? "清空选择" : `全选可用司机（${availableAccountIds.length}）`}
            </Button>
          </div>
          <div className="schedule-driver-toolbar">
            <div className="search-box">
              <Search size={16} />
              <Input
                value={driverSearch}
                onChange={(event) => setDriverSearch(event.target.value)}
                placeholder="搜索司机、手机号或车牌"
              />
            </div>
            {conflictsLoading && <span className="schedule-conflict-loading"><Loader2 className="spin" size={14} />正在检查冲突</span>}
          </div>
          {groupId ? (
            <div className="schedule-driver-list">
              {visibleGroupAccounts.map((account) => {
                const conflict = conflicts[account.id]?.[0];
                const unavailableReason = !account.enabled ? "司机账号已停用" : conflict?.reason;
                return (
                  <label className={`schedule-driver-row${unavailableReason ? " is-conflict" : ""}`} key={account.id}>
                    <input
                      type="checkbox"
                      checked={selectedAccountIds.has(account.id)}
                      disabled={!!unavailableReason && !selectedAccountIds.has(account.id)}
                      onChange={() => {
                        const next = new Set(selectedAccountIds);
                        next.has(account.id) ? next.delete(account.id) : next.add(account.id);
                        setSelectedAccountIds(next);
                      }}
                    />
                    <div className="schedule-driver-identity">
                      <strong>{account.driverName || "未识别司机"}</strong>
                      <span>{account.phone} · {account.plateNo || "未配置车牌"} · {account.groupName}</span>
                      {account.groupId !== groupId && <span className="schedule-conflict-text">跨组司机，仅提示，不限制执行</span>}
                    </div>
                    {unavailableReason ? (
                      <span className="schedule-conflict-text">
                        {unavailableReason}{conflict && ` · ${conflict.planName || conflict.templateName}`}
                      </span>
                    ) : (
                      <Badge tone={selectedAccountIds.has(account.id) ? "success" : "neutral"}>
                        {selectedAccountIds.has(account.id) ? "已选择" : "可选择"}
                      </Badge>
                    )}
                  </label>
                );
              })}
              {!visibleGroupAccounts.length && (
                <EmptyState title="没有匹配司机" description="请调整搜索条件或重新选择司机分组。" />
              )}
            </div>
          ) : (
            <EmptyState title="请先选择司机分组" description="选择后可从授权范围内跨组选择司机，并检查定时任务冲突。" />
          )}
        </div>
        {selectedConflictCount > 0 && enabled && (
          <div className="notice-bar danger-text">已选司机中有 {selectedConflictCount} 人与其他定时任务冲突，请先调整名单。</div>
        )}
        <label className="target-row auto-run-option">
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          <div><strong>启用定时任务</strong><span>暂停后不会新建线路，已经生成的线路继续按原计划运行。</span></div>
        </label>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setFormOpen(false)}>取消</Button>
          <Button
            disabled={busy || conflictsLoading || !templateId || !groupId || !authorizedUserId || !selectedAccountIds.size || (enabled && selectedConflictCount > 0) || (scheduleType === "once" && !runDate)}
            onClick={savePlan}
          >
            {busy ? <Loader2 className="spin" size={16} /> : null}保存
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export function RoutesPage({ currentUser, openSchedules }: { currentUser: CurrentUser; openSchedules: () => void }) {
  const queryClient = useQueryClient();
  const [runPage, setRunPage] = useState(1);
  const [runPageSize, setRunPageSize] = useState<50 | 100 | 200>(50);
  const templates = useQuery({
    queryKey: ["route-templates", currentUser.role],
    queryFn: () =>
      api<RouteTemplate[]>(
        currentUser.role === "super_admin"
          ? "/api/route-templates?includeArchived=true"
          : "/api/route-templates",
      ),
  });
  const proxyResources = useQuery({
    queryKey: ["proxy-resources"],
    queryFn: () => api<ProxyResources>("/api/system/proxy/resources"),
    enabled: currentUser.role === "super_admin",
    retry: false,
    staleTime: 5 * 60_000,
  });
  const runs = useQuery({
    queryKey: ["route-runs", runPage, runPageSize],
    queryFn: () =>
      api<PagedResponse<RouteRun> & { activeAccountIds: string[] }>(
        `/api/route-runs?page=${runPage}&pageSize=${runPageSize}`,
      ),
    refetchInterval: 15_000,
  });
  const accounts = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api<Account[]>("/api/accounts"),
  });
  const users = useQuery({
    queryKey: ["admin-users", "route-grants"],
    queryFn: () => api<AdminUser[]>("/api/admin/users"),
    enabled: currentUser.role === "super_admin",
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RouteTemplate>();
  const [runOpen, setRunOpen] = useState(false);
  const [grantTemplate, setGrantTemplate] = useState<RouteTemplate>();
  const [grantedUsers, setGrantedUsers] = useState<Set<string>>(new Set());
  const [originalGrantedUsers, setOriginalGrantedUsers] = useState<Set<string>>(new Set());
  const [name, setName] = useState("新运输线路");
  const [description, setDescription] = useState("");
  const [tons, setTons] = useState("30");
  const [proxyAreaCode, setProxyAreaCode] = useState("");
  const [proxyAreaName, setProxyAreaName] = useState("");
  const [legs, setLegs] = useState<RouteLeg[]>([
    {
      fromName: "",
      fromAddress: "",
      fromAliases: [],
      toName: "",
      toAddress: "",
      toAliases: [],
      declaredDistanceKm: 80,
      minimumTransportHours: 2,
      jitterMinMinutes: 5,
      jitterMaxMinutes: 20,
      plannedLoadTime: "08:30",
      plannedUnloadTime: "10:30",
      reminderToleranceMinutes: 30,
    },
  ]);
  const [templateId, setTemplateId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [businessDate, setBusinessDate] = useState(todayInShanghai());
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [routePreview, setRoutePreview] = useState<BatchPreview>();
  const [deletingRun, setDeletingRun] = useState<RouteRun>();
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>(
    new URLSearchParams(window.location.search).get("routeRunId") || undefined,
  );
  const [routeWaybill, setRouteWaybill] = useState<Waybill>();
  const routeDetail = useQuery({
    queryKey: ["route-run-detail", selectedRunId],
    queryFn: () => api<RouteRunDetail>(`/api/route-runs/${selectedRunId}`),
    enabled: !!selectedRunId,
    refetchInterval: selectedRunId ? 15_000 : false,
  });
  const routeWaybillDetail = useQuery({
    queryKey: ["waybill-detail", routeWaybill?.accountId, routeWaybill?.waybillId],
    queryFn: () => api<Waybill>(
      `/api/waybills/${routeWaybill!.waybillId}?accountId=${routeWaybill!.accountId}&includeRaw=false`,
    ),
    enabled: !!routeWaybill,
  });
  useEffect(() => {
    const syncRouteFromUrl = () => {
      const runId = new URLSearchParams(window.location.search).get("routeRunId") || undefined;
      setSelectedRunId(runId);
      if (!runId) setRouteWaybill(undefined);
    };
    window.addEventListener("popstate", syncRouteFromUrl);
    return () => window.removeEventListener("popstate", syncRouteFromUrl);
  }, []);

  const activeTemplates = (templates.data ?? []).filter(
    (template) => template.status === "active",
  );
  const activeAccountIds = new Set(
    runs.data?.activeAccountIds ?? [],
  );
  const selectedTemplate = activeTemplates.find(
    (template) => template.id === templateId,
  );

  function openRouteDetail(runId: string) {
    setSelectedRunId(runId);
    setRouteWaybill(undefined);
    const params = new URLSearchParams(window.location.search);
    params.set("routeRunId", runId);
    window.history.pushState({}, "", `${window.location.pathname}?${params}`);
  }

  function closeRouteDetail() {
    setSelectedRunId(undefined);
    setRouteWaybill(undefined);
    const params = new URLSearchParams(window.location.search);
    params.delete("routeRunId");
    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  }

  function emptyLeg(): RouteLeg {
    return {
      fromName: "",
      fromAddress: "",
      fromAliases: [],
      toName: "",
      toAddress: "",
      toAliases: [],
      declaredDistanceKm: 100,
      minimumTransportHours: 2,
      jitterMinMinutes: 5,
      jitterMaxMinutes: 20,
      plannedLoadTime: "08:30",
      plannedUnloadTime: "10:30",
      reminderToleranceMinutes: 30,
    };
  }

  function openTemplate(template?: RouteTemplate) {
    setEditingTemplate(template);
    setName(template?.name ?? "新线路模板");
    setDescription(template?.description ?? "");
    setTons(String(template?.defaultTons ?? 30));
    setProxyAreaCode(template?.proxyAreaCode ?? "");
    setProxyAreaName(template?.proxyAreaName ?? "");
    setLegs(template?.legs.map((leg) => ({ ...emptyLeg(), ...leg })) ?? [emptyLeg()]);
    setCreateOpen(true);
  }

  async function saveTemplate() {
    try {
      const payload = {
        name,
        description,
        defaultTons: Number(tons),
        proxyAreaCode,
        proxyAreaName,
        legs,
      };
      if (editingTemplate) {
        await put(`/api/route-templates/${editingTemplate.id}`, payload);
      } else {
        await post("/api/route-templates", payload);
      }
      setCreateOpen(false);
      setMessage(editingTemplate ? "线路模板已更新；运行中任务继续使用旧快照" : "线路模板已创建");
      queryClient.invalidateQueries({ queryKey: ["route-templates"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存失败");
    }
  }
  async function createRun() {
    try {
      await post("/api/route-runs", { templateId, accountId, businessDate, autoEnabled });
      setRunOpen(false);
      setMessage(
        autoEnabled
          ? "线路任务和自动运行已启动；系统会在安全时间卸货并衔接下一段，全部路段完成后停止"
          : "线路任务已启动，模板、计划时刻和随机等待已经冻结为快照",
      );
      queryClient.invalidateQueries({ queryKey: ["route-runs"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "创建失败");
    }
  }
  async function openGrants(template: RouteTemplate) {
    try {
      const result = await api<{ userIds: string[] }>(
        `/api/route-templates/${template.id}/grants`,
      );
      const next = new Set(result.userIds);
      setGrantedUsers(next);
      setOriginalGrantedUsers(new Set(result.userIds));
      setGrantTemplate(template);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "读取线路授权失败");
    }
  }
  async function saveGrants() {
    if (!grantTemplate) return;
    try {
      await put(`/api/route-templates/${grantTemplate.id}/grants`, {
        userIds: Array.from(grantedUsers),
      });
      setGrantTemplate(undefined);
      setMessage("线路授权已更新；被移除用户将立即看不到对应运行任务");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存线路授权失败");
    }
  }
  async function advance(runId: string) {
    try {
      const result = await post<any>(`/api/route-runs/${runId}/advance`);
      if (result.batch) setRoutePreview(result.batch);
      else setMessage("线路任务已全部完成。");
      queryClient.invalidateQueries({ queryKey: ["route-runs"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "推进失败");
    }
  }
  async function toggleAutomation(run: RouteRun) {
    try {
      await put(`/api/route-runs/${run.id}/automation`, { enabled: !run.autoEnabled });
      setMessage(run.autoEnabled ? "自动运行已暂停，可改为手动推进" : "自动运行已开启，将从当前线路段继续");
      queryClient.invalidateQueries({ queryKey: ["route-runs"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "更新自动运行失败");
    }
  }
  async function deleteRouteRun() {
    if (!deletingRun || deleteReason.trim().length < 2) return;
    setDeleteBusy(true);
    try {
      await del(`/api/route-runs/${deletingRun.id}`, { reason: deleteReason.trim() });
      setDeletingRun(undefined);
      setDeleteReason("");
      setMessage("线路任务已从列表移除；历史快照、审计和司机每日一次限制仍然保留");
      if ((runs.data?.items.length ?? 0) === 1 && runPage > 1) setRunPage(runPage - 1);
      await queryClient.invalidateQueries({ queryKey: ["route-runs"] });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "删除线路任务失败");
    } finally {
      setDeleteBusy(false);
    }
  }
  async function approveRoutePreview() {
    if (!routePreview) return;
    try {
      await post(`/api/batches/${routePreview.id}/approve`, {
        previewHash: routePreview.previewHash,
      });
      setRoutePreview(undefined);
      setMessage("线路步骤已确认执行。完成后再次点击推进。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "审批失败");
    }
  }
  function updateLeg(index: number, field: keyof RouteLeg, value: string) {
    setLegs((current) =>
      current.map((leg, legIndex) =>
        legIndex === index
          ? {
              ...leg,
              [field]: ["fromAliases", "toAliases"].includes(field)
                ? value.split(/[,，]/).map((item) => item.trim()).filter(Boolean)
                : [
                      "declaredDistanceKm",
                      "minimumTransportHours",
                      "jitterMinMinutes",
                      "jitterMaxMinutes",
                      "reminderToleranceMinutes",
                    ].includes(field)
                  ? Number(value)
                  : value,
            }
          : leg,
      ),
    );
  }
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>线路任务</h1>
          <p>支持手动推进，也支持当天自动接单、装货、到安全时间卸货并连续完成整条线路。</p>
        </div>
        <div className="heading-actions">
          {currentUser.capabilities.manageRouteTemplates && (
            <Button variant="secondary" onClick={() => openTemplate()}>
              <Plus size={16} />
              新建模板
            </Button>
          )}
          <Button
            onClick={() => setRunOpen(true)}
            disabled={!activeTemplates.length || !accounts.data?.length}
          >
            <Play size={16} />
            启动线路
          </Button>
        </div>
      </section>
      {message && <div className="notice-bar">{message}</div>}
      <section className="route-template-grid">
        {templates.data?.map((template) => (
          <article className="route-template" key={template.id}>
            <div className="route-template-head">
              <div>
                <strong>{template.name}</strong>
                <span>
                  {template.defaultTons} 吨 · {template.legs.length} 段 · 代理 {template.proxyAreaName || "不限地区"}
                </span>
              </div>
              <Badge tone={template.status === "active" ? "success" : "neutral"}>
                {template.status === "active" ? "启用" : "已归档"}
              </Badge>
            </div>
            <p>{template.description || "未填写说明"}</p>
            <div className="leg-list">
              {template.legs.map((leg, index) => (
                <div key={index}>
                  <span>{index + 1}</span>
                  <strong>{leg.fromName}</strong>
                  <ArrowRight size={13} />
                  <strong>{leg.toName}</strong>
                  <small>
                    {leg.declaredDistanceKm} km · {leg.minimumTransportHours}h +{" "}
                    {leg.jitterMinMinutes}-{leg.jitterMaxMinutes}m · {leg.plannedLoadTime || "--:--"} / {leg.plannedUnloadTime || "--:--"}
                  </small>
                </div>
              ))}
            </div>
            <div className="inline-actions route-template-actions">
              <Button variant="ghost" onClick={openSchedules}>
                <Clock3 size={14} />定时任务
              </Button>
              {currentUser.capabilities.manageRouteTemplates && (
                <>
                {currentUser.role === "super_admin" && (
                  <Button variant="ghost" onClick={() => openGrants(template)}>
                    <Users size={14} />授权用户
                  </Button>
                )}
                <Button variant="ghost" onClick={() => openTemplate(template)}>
                  编辑
                </Button>
                {template.status === "active" && (
                  <Button
                    variant="ghost"
                    onClick={async () => {
                      if (!window.confirm(`确认归档“${template.name}”？历史任务不会受影响。`)) return;
                      await del(`/api/route-templates/${template.id}`);
                      queryClient.invalidateQueries({ queryKey: ["route-templates"] });
                    }}
                  >
                    <Trash2 size={14} />归档
                  </Button>
                )}
                </>
              )}
            </div>
          </article>
        ))}
        {!templates.data?.length && (
          <EmptyState
            title={currentUser.capabilities.manageRouteTemplates ? "还没有线路模板" : "尚未分配线路，请联系超级管理员"}
            description={currentUser.capabilities.manageRouteTemplates ? "创建固定线路后，可为司机启动可恢复的多段任务。" : "获得线路授权后，才可查看、启动和推进对应线路。"}
          />
        )}
      </section>
      <section className="panel-section">
        <div className="section-header">
          <div>
            <h2>线路任务记录</h2>
            <p>自动模式会保留每一步批次、证据和审计记录；过期或完成任务可由超级管理员移出列表</p>
          </div>
        </div>
        <div className="run-list">
          {runs.isLoading && <LoadingState label="正在加载线路运行记录" />}
          {runs.data?.items.map((run) => (
            <div className="run-row" key={run.id}>
              <span className="route-run-icon">
                <RouteIcon size={17} />
              </span>
              <button
                type="button"
                className="route-run-identity"
                onClick={() => openRouteDetail(run.id)}
                aria-label={`查看 ${run.driverName || run.phone} 的线路详情`}
              >
                <strong>{run.templateName || run.templateId}</strong>
                <span>
                  {run.phone} {run.driverName}
                </span>
                <ChevronRight size={15} />
              </button>
              <div className="inline-actions">
                <Badge tone={statusTone(run.status)}>{routeRunStatusText(run.status)}</Badge>
                {run.createdSource === "daily_auto" && (
                  <Badge tone="info">每日计划</Badge>
                )}
                <Badge
                  tone={
                    run.autoStatus === "needs_attention"
                      ? "danger"
                      : run.autoEnabled
                        ? "success"
                        : "neutral"
                  }
                >
                  {autoStatusText(run.autoStatus)}
                </Badge>
              </div>
              <span>
                第{" "}
                {Math.min(
                  (run.currentLegIndex ?? 0) + 1,
                  run.state?.legs?.length ?? 0,
                )}{" "}
                / {run.state?.legs?.length ?? 0} 段
              </span>
              {run.status === "active" && run.state.legs[run.currentLegIndex] && (
                <div className="route-run-schedule">
                  <span>
                    业务日 {run.businessDate} · 装货 {run.state.legs[run.currentLegIndex].plannedLoadTime || "-"}
                    （{reminderText(run.state.legs[run.currentLegIndex].plannedLoadWindowStatus)}）
                  </span>
                  <span>
                    卸货 {run.state.legs[run.currentLegIndex].plannedUnloadTime || "-"}
                    （{reminderText(run.state.legs[run.currentLegIndex].plannedUnloadWindowStatus)}）
                    · 随机等待 {run.state.legs[run.currentLegIndex].jitterMinutes ?? 0} 分钟
                  </span>
                  <span>
                    服务端基础最早可卸货：{formatDate(run.currentWaybill?.earliestUnloadTime)}
                  </span>
                  {run.nextAutoActionAt && (
                    <span>下一次自动检查/操作：{formatDate(run.nextAutoActionAt)}</span>
                  )}
                  {Object.entries(
                    run.state.legs[run.currentLegIndex].operationSchedule || {},
                  ).map(([operation, schedule]) => (
                    <span key={operation}>
                      {operationName(operation)}随机时间：{formatDate(schedule.scheduledAt)}
                      {schedule.count
                        ? ` · 第 ${schedule.sequence ?? "-"}/${schedule.count} 位`
                        : ""}
                      {schedule.deadlineAt
                        ? ` · 截止 ${formatDate(schedule.deadlineAt)}`
                        : ""}
                    </span>
                  ))}
                  {run.lastAutoError && <span className="danger-text">异常：{run.lastAutoError}</span>}
                </div>
              )}
              <div className="inline-actions route-run-actions">
                {run.status === "active" && (
                  <Button variant="ghost" onClick={() => toggleAutomation(run)}>
                    {run.autoEnabled ? "暂停自动" : "开启自动"}
                  </Button>
                )}
                {currentUser.role === "super_admin" && ["complete", "expired"].includes(run.status) && (
                  <IconButton
                    label="删除线路任务"
                    className="danger-icon-button"
                    onClick={() => {
                      setDeletingRun(run);
                      setDeleteReason("");
                    }}
                  >
                    <Trash2 size={15} />
                  </IconButton>
                )}
                <Button
                  variant="secondary"
                  onClick={() => advance(run.id)}
                  disabled={run.status !== "active" || run.autoEnabled}
                >
                  推进
                  <ChevronRight size={15} />
                </Button>
              </div>
            </div>
          ))}
          {!runs.isLoading && !runs.data?.items.length && (
            <EmptyState
              title="没有线路任务"
              description="选择模板和账号后启动。"
            />
          )}
        </div>
        <Pagination
          page={runPage}
          pageSize={runPageSize}
          total={runs.data?.total ?? 0}
          loading={runs.isFetching}
          onPageChange={setRunPage}
          onPageSizeChange={(value) => {
            setRunPageSize(value);
            setRunPage(1);
          }}
        />
      </section>
      <Drawer
        open={!!selectedRunId}
        onOpenChange={(open) => !open && closeRouteDetail()}
        title={
          routeWaybill
            ? `运单 ${routeWaybill.waybillId}`
            : `${routeDetail.data?.driverName || routeDetail.data?.phone || "司机"} · 线路详情`
        }
      >
        {routeWaybill ? (
          <>
            <Button variant="ghost" onClick={() => setRouteWaybill(undefined)}>
              <ArrowLeft size={15} />返回线路详情
            </Button>
            {routeWaybillDetail.isLoading && <LoadingState label="正在加载运单详情" />}
            <WaybillDetailContent waybill={routeWaybillDetail.data ?? routeWaybill} />
          </>
        ) : (
          <div className="route-run-detail">
            {routeDetail.isLoading && <LoadingState label="正在加载线路详情" />}
            {routeDetail.isError && (
              <EmptyState
                title="线路详情加载失败"
                description="任务可能已删除、授权已撤销或网络暂时不可用。"
                action={<Button variant="secondary" onClick={() => routeDetail.refetch()}>重新加载</Button>}
              />
            )}
            {routeDetail.data && (
              <>
                <section className="route-detail-summary">
                  <div><span>{routeDetail.data.businessDate || "无业务日"}</span><strong>{routeDetail.data.templateName || routeDetail.data.templateId}</strong><small>{routeDetail.data.driverName || "未知司机"} · {routeDetail.data.phone || "-"}</small></div>
                  <div className="inline-actions"><Badge tone={statusTone(routeDetail.data.status)}>{routeRunStatusText(routeDetail.data.status)}</Badge><Badge tone={routeDetail.data.autoStatus === "needs_attention" ? "danger" : "neutral"}>{autoStatusText(routeDetail.data.autoStatus)}</Badge></div>
                </section>
                <dl className="detail-grid compact-detail-grid">
                  <div><dt>完成进度</dt><dd>{routeDetail.data.completedLegs} / {routeDetail.data.legs.length} 段</dd></div>
                  <div><dt>创建来源</dt><dd>{routeDetail.data.createdSource === "daily_auto" ? "每日定时任务" : "手动创建"}</dd></div>
                  <div><dt>下一次检查</dt><dd>{formatDate(routeDetail.data.nextAutoActionAt)}</dd></div>
                  <div><dt>最近更新</dt><dd>{formatDate(routeDetail.data.updatedAt)}</dd></div>
                </dl>
                {routeDetail.data.lastAutoError && <div className="notice-bar danger-text">{routeDetail.data.lastAutoError}</div>}
                <RouteLegDetails legs={routeDetail.data.legs} onWaybill={setRouteWaybill} />
              </>
            )}
          </div>
        )}
      </Drawer>
      <Dialog
        open={!!deletingRun}
        onOpenChange={(open) => {
          if (!open && !deleteBusy) {
            setDeletingRun(undefined);
            setDeleteReason("");
          }
        }}
        title="删除线路任务"
        description="任务将从列表隐藏，但不会物理清除历史记录。"
      >
        {deletingRun && (
          <div className="route-delete-dialog">
            <div className="single-operation-summary">
              <div><span>司机</span><strong>{deletingRun.driverName || "-"} · {deletingRun.phone || "-"}</strong></div>
              <div><span>业务日</span><strong>{deletingRun.businessDate || "-"}</strong></div>
              <div><span>当前状态</span><strong>{deletingRun.status}</strong></div>
              <div className="single-operation-route"><span>线路</span><strong>{deletingRun.templateName || deletingRun.templateId}</strong></div>
              <div><span>完成进度</span><strong>{Math.min(deletingRun.currentLegIndex + 1, deletingRun.state.legs.length)} / {deletingRun.state.legs.length} 段</strong></div>
            </div>
            <div className="danger-confirm">
              <AlertTriangle size={18} />
              <p>删除后页面不再显示该任务，且不能通过页面恢复；司机、运单、线路快照、操作证据和审计记录不会删除，同一司机当天仍不能重复启动线路。</p>
            </div>
            <Field label="删除原因" hint="原因会写入操作日志和飞书通知。">
              <textarea
                className="input compact-textarea"
                value={deleteReason}
                maxLength={200}
                placeholder="例如：任务已过期且无需继续处理"
                onChange={(event) => setDeleteReason(event.target.value)}
              />
            </Field>
            <div className="dialog-actions">
              <Button variant="secondary" disabled={deleteBusy} onClick={() => setDeletingRun(undefined)}>取消</Button>
              <Button variant="danger" disabled={deleteBusy || deleteReason.trim().length < 2} onClick={deleteRouteRun}>
                {deleteBusy ? <Loader2 className="spin" size={16} /> : <Trash2 size={16} />}
                确认删除
              </Button>
            </div>
          </div>
        )}
      </Dialog>
      <Dialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={editingTemplate ? "编辑线路模板" : "新建线路模板"}
        wide
      >
        <div className="config-grid">
          <Field label="模板名称">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="默认吨数">
            <Input
              type="number"
              value={tons}
              onChange={(event) => setTons(event.target.value)}
            />
          </Field>
          <Field
            label="代理地区"
            hint={proxyResources.error ? "地区资源读取失败，现有选择仍可保存" : "定时预检和业务操作会优先提取该地区 IP"}
          >
            <Select
              value={proxyAreaCode}
              onChange={(event) => {
                const selected = proxyResources.data?.items.find((item) => item.areaCode === event.target.value);
                setProxyAreaCode(event.target.value);
                setProxyAreaName(selected?.areaName ?? "");
              }}
            >
              <option value="">不限定地区</option>
              {proxyResources.data?.items.map((item) => (
                <option key={item.areaCode} value={item.areaCode} disabled={!item.available}>
                  {item.areaName}（{item.available ? `${item.ispCount} 个运营商可用` : "暂无资源"}）
                </option>
              ))}
              {proxyAreaCode && !proxyResources.data?.items.some((item) => item.areaCode === proxyAreaCode) && (
                <option value={proxyAreaCode}>{proxyAreaName || proxyAreaCode}（当前配置）</option>
              )}
            </Select>
          </Field>
        </div>
        <Field label="说明">
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <div className="leg-editor">
          <div className="section-header">
            <h3>线路分段</h3>
            <Button
              variant="ghost"
              onClick={() => setLegs([...legs, emptyLeg()])}
            >
              <Plus size={15} />
              增加一段
            </Button>
          </div>
          {legs.map((leg, index) => (
            <div className="leg-editor-row" key={index}>
              <span>{index + 1}</span>
              <Input
                value={leg.fromName}
                onChange={(event) =>
                  updateLeg(index, "fromName", event.target.value)
                }
                placeholder="装货点"
              />
              <Input
                value={leg.fromAddress}
                onChange={(event) => updateLeg(index, "fromAddress", event.target.value)}
                placeholder="装货地址"
              />
              <Input
                value={leg.fromAliases.join("，")}
                onChange={(event) => updateLeg(index, "fromAliases", event.target.value)}
                placeholder="装货点别名，逗号分隔"
              />
              <ArrowRight size={15} />
              <Input
                value={leg.toName}
                onChange={(event) =>
                  updateLeg(index, "toName", event.target.value)
                }
                placeholder="卸货点"
              />
              <Input
                value={leg.toAddress}
                onChange={(event) => updateLeg(index, "toAddress", event.target.value)}
                placeholder="卸货地址"
              />
              <Input
                value={leg.toAliases.join("，")}
                onChange={(event) => updateLeg(index, "toAliases", event.target.value)}
                placeholder="卸货点别名，逗号分隔"
              />
              <Input
                type="number"
                value={leg.declaredDistanceKm}
                onChange={(event) =>
                  updateLeg(index, "declaredDistanceKm", event.target.value)
                }
                title="距离 km"
              />
              <Input
                type="number"
                value={leg.minimumTransportHours}
                onChange={(event) =>
                  updateLeg(index, "minimumTransportHours", event.target.value)
                }
                title="最低小时"
              />
              <Input
                type="time"
                value={leg.plannedLoadTime || ""}
                onChange={(event) => updateLeg(index, "plannedLoadTime", event.target.value)}
                title="计划装货时间"
              />
              <Input
                type="time"
                value={leg.plannedUnloadTime || ""}
                onChange={(event) => updateLeg(index, "plannedUnloadTime", event.target.value)}
                title="计划卸货时间"
              />
              <Input
                type="number"
                value={leg.reminderToleranceMinutes}
                onChange={(event) => updateLeg(index, "reminderToleranceMinutes", event.target.value)}
                title="提醒容差（分钟）"
              />
              <IconButton
                label="删除这一段"
                onClick={() =>
                  setLegs(legs.filter((_, legIndex) => legIndex !== index))
                }
              >
                <Trash2 size={15} />
              </IconButton>
            </div>
          ))}
        </div>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setCreateOpen(false)}>
            取消
          </Button>
          <Button
            onClick={saveTemplate}
            disabled={!name || legs.some((leg) => !leg.fromName || !leg.toName)}
          >
            保存模板
          </Button>
        </div>
      </Dialog>
      <Dialog open={runOpen} onOpenChange={setRunOpen} title="启动线路任务">
        <Field label="1. 选择司机">
          <Select
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            <option value="">请选择</option>
            {accounts.data?.map((account) => (
              <option value={account.id} key={account.id} disabled={activeAccountIds.has(account.id)}>
                {account.phone} · {account.driverName}{activeAccountIds.has(account.id) ? "（已有 active 线路）" : ""}
              </option>
            ))}
          </Select>
        </Field>
        {accountId && activeAccountIds.has(accountId) && (
          <div className="notice-bar">该司机已有运行中的线路，请关闭窗口后在运行列表中继续推进。</div>
        )}
        <Field label="2. 选择业务日期" hint="时区：Asia/Shanghai">
          <Input
            type="date"
            value={businessDate}
            onChange={(event) => setBusinessDate(event.target.value)}
          />
        </Field>
        <Field label="3. 明确选择一条线路">
          <Select
            value={templateId}
            onChange={(event) => setTemplateId(event.target.value)}
          >
            <option value="">请选择</option>
            {activeTemplates.map((template) => (
              <option value={template.id} key={template.id}>
                {template.name}
              </option>
            ))}
          </Select>
        </Field>
        {selectedTemplate && (
          <div className="route-start-summary">
            <strong>4. 确认线路：{selectedTemplate.name}</strong>
            {selectedTemplate.legs.map((leg, index) => (
              <span key={index}>
                {index + 1}. {leg.fromName} → {leg.toName} · {leg.plannedLoadTime || "-"}/{leg.plannedUnloadTime || "-"}
              </span>
            ))}
          </div>
        )}
        <label className="target-row auto-run-option">
          <input
            type="checkbox"
            checked={autoEnabled}
            onChange={(event) => setAutoEnabled(event.target.checked)}
          />
          <div>
            <strong>自动运行到线路完成</strong>
            <span>自动接单、装货、到安全时间卸货并进入下一段；当天全部路段完成后停止</span>
          </div>
        </label>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setRunOpen(false)}>
            取消
          </Button>
          <Button
            onClick={createRun}
            disabled={!templateId || !accountId || !businessDate || activeAccountIds.has(accountId)}
          >
            确认并启动
          </Button>
        </div>
      </Dialog>
      <Dialog
        open={!!grantTemplate}
        onOpenChange={(open) => !open && setGrantTemplate(undefined)}
        title={`授权用户 · ${grantTemplate?.name ?? ""}`}
        description="普通用户只有获配后才能查看、启动和推进该线路。"
      >
        <div className="grant-user-list">
          {(users.data ?? [])
            .filter((user) => user.role === "sub_account")
            .map((user) => (
              <label key={user.id} className="target-row">
                <input
                  type="checkbox"
                  checked={grantedUsers.has(user.id)}
                  disabled={!user.enabled}
                  onChange={() => {
                    const next = new Set(grantedUsers);
                    next.has(user.id) ? next.delete(user.id) : next.add(user.id);
                    setGrantedUsers(next);
                  }}
                />
                <div>
                  <strong>{user.displayName || user.username}</strong>
                  <span>{user.username}{user.enabled ? "" : " · 已停用"}</span>
                </div>
              </label>
            ))}
        </div>
        <div className="grant-change-summary">
          新增 {Array.from(grantedUsers).filter((id) => !originalGrantedUsers.has(id)).length} 个，
          移除 {Array.from(originalGrantedUsers).filter((id) => !grantedUsers.has(id)).length} 个
        </div>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setGrantTemplate(undefined)}>取消</Button>
          <Button onClick={saveGrants}>保存授权</Button>
        </div>
      </Dialog>
      <Dialog
        open={!!routePreview}
        onOpenChange={(open) => !open && setRoutePreview(undefined)}
        title="确认线路步骤"
        description={`核对 ${routePreview?.allowedCount ?? 0} 个任务的运单、线路和计划时间后确认执行。`}
        wide
      >
        <PreviewPanel preview={routePreview!} />
        <div className="danger-confirm">
          <AlertTriangle size={20} />
          <p>确认后任务会按照上方冻结的计划时间提交真实业务请求，执行前仍会复核权限、状态、定位和截止时间。</p>
        </div>
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={() => setRoutePreview(undefined)}
          >
            取消
          </Button>
          <Button
            onClick={approveRoutePreview}
            disabled={!!routePreview?.blockedCount}
          >
            确认并执行
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function reportStatusTone(status: string): "neutral" | "success" | "warning" | "danger" | "info" {
  if (status === "complete") return "success";
  if (status === "partial") return "warning";
  if (status === "missed") return "danger";
  return status === "running" ? "info" : "neutral";
}

function reportStatusText(status: string) {
  return {
    preparing: "准备中",
    running: "运行中",
    complete: "已完成",
    partial: "部分完成",
    missed: "未启动",
  }[status] ?? status;
}

function operationReportText(report: DailyReportListItem, operation: string) {
  const counts = report.operations?.[operation] ?? {};
  const failed = (counts.failed ?? 0) + (counts.blocked ?? 0) + (counts.expired ?? 0);
  return `${counts.success ?? 0} 成功 / ${failed} 异常`;
}

export function DailyReportsPage({ currentUser }: { currentUser: CurrentUser }) {
  const queryClient = useQueryClient();
  const params = new URLSearchParams(window.location.search);
  const storageKey = `daily-report-task-filter:${currentUser.id}`;
  const [savedTaskFilter] = useState(() => readReportTaskFilter(storageKey, window.location.search));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<50 | 100 | 200>(50);
  const [dateFrom, setDateFrom] = useState(todayInShanghai);
  const [dateTo, setDateTo] = useState(todayInShanghai);
  const [status, setStatus] = useState("");
  const [planIds, setPlanIds] = useState<string[]>(savedTaskFilter.planIds);
  const [authorizedUserId, setAuthorizedUserId] = useState(savedTaskFilter.authorizedUserId);
  const [selectedId, setSelectedId] = useState<string | undefined>(params.get("reportId") ?? undefined);
  const [expandedDriverId, setExpandedDriverId] = useState<string | undefined>(params.get("driverId") ?? undefined);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverFilter, setDriverFilter] = useState<"all" | "anomaly" | "incomplete" | "complete">("all");
  const [reportWaybill, setReportWaybill] = useState<Waybill>();
  const [message, setMessage] = useState("");
  const businessToday = todayInShanghai();
  useEffect(() => {
    saveReportTaskFilter(storageKey, { authorizedUserId, planIds });
  }, [storageKey, authorizedUserId, planIds]);

  function changePlans(ids: string[]) {
    setPlanIds(ids);
    setPage(1);
    const next = new URLSearchParams(window.location.search);
    next.delete("planId");
    next.delete("planIds");
    window.history.replaceState(null, "", `${window.location.pathname}?${next}`);
  }
  const administrators = useQuery({
    queryKey: ["admin-users", "report-filter"],
    queryFn: () => api<AdminUser[]>("/api/admin/users"),
    enabled: currentUser.role === "super_admin",
    staleTime: 60_000,
  });
  const plans = useQuery({
    queryKey: ["route-auto-plans", "report-filter", authorizedUserId],
    queryFn: async () => {
      const items: RouteAutoPlan[] = [];
      let page = 1;
      let result: PagedResponse<RouteAutoPlan>;
      do {
        const query = new URLSearchParams({ page: String(page), pageSize: "200" });
        if (authorizedUserId) query.set("authorizedUserId", authorizedUserId);
        result = await api<PagedResponse<RouteAutoPlan>>(`/api/route-auto-plans?${query}`);
        items.push(...result.items);
        page += 1;
      } while (items.length < result.total && result.items.length > 0);
      return { items };
    },
    staleTime: 60_000,
  });
  const reports = useQuery({
    queryKey: ["daily-reports", page, pageSize, dateFrom, dateTo, status, planIds, authorizedUserId],
    queryFn: ({ signal }) => {
      const query = reportFilterQuery({ dateFrom, dateTo, status, planIds, authorizedUserId });
      query.set("page", String(page));
      query.set("pageSize", String(pageSize));
      query.set("summaryOnly", "true");
      return api<PagedResponse<DailyReportListItem>>(`/api/daily-reports?${query.toString()}`, { signal });
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const waybillSummary = useQuery({
    queryKey: ["daily-reports", "waybill-summary", dateFrom, dateTo, status, planIds, authorizedUserId],
    queryFn: ({ signal }) => api<DailyReportDaySummary>(
      `/api/daily-reports/summary?${reportFilterQuery({ dateFrom, dateTo, status, planIds, authorizedUserId })}`,
      { signal },
    ),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const detail = useQuery({
    queryKey: ["daily-report", selectedId],
    queryFn: () => api<DailyReport>(`/api/daily-reports/${selectedId}`),
    enabled: !!selectedId,
    refetchInterval: (query) => query.state.data?.finalizedAt ? false : 30_000,
  });
  const driverDetail = useQuery({
    queryKey: ["daily-report-driver", selectedId, expandedDriverId],
    queryFn: () => api<DailyReportDriverDetail>(
      `/api/daily-reports/${selectedId}/drivers/${expandedDriverId}`,
    ),
    enabled: !!selectedId && !!expandedDriverId,
    refetchInterval: (query) => query.state.data?.finalized ? false : 30_000,
  });
  const reportWaybillDetail = useQuery({
    queryKey: ["waybill-detail", reportWaybill?.accountId, reportWaybill?.waybillId],
    queryFn: () => api<Waybill>(
      `/api/waybills/${reportWaybill!.waybillId}?accountId=${reportWaybill!.accountId}&includeRaw=false`,
    ),
    enabled: !!reportWaybill,
  });
  useEffect(() => {
    const syncReportFromUrl = () => {
      const next = new URLSearchParams(window.location.search);
      const reportId = next.get("reportId") || undefined;
      setSelectedId(reportId);
      setExpandedDriverId(reportId ? next.get("driverId") || undefined : undefined);
      if (!reportId) setReportWaybill(undefined);
    };
    window.addEventListener("popstate", syncReportFromUrl);
    return () => window.removeEventListener("popstate", syncReportFromUrl);
  }, []);

  const current = detail.data;
  const visibleDrivers = useMemo(() => {
    const keyword = driverSearch.trim().toLowerCase();
    const category = (driver: DailyReport["drivers"][number]) => {
      if (driver.error || ["needs_attention", "paused"].includes(driver.autoStatus || "")) return "anomaly";
      if (driver.status === "complete") return "complete";
      return "incomplete";
    };
    const rank = { anomaly: 0, incomplete: 1, complete: 2 };
    return [...(current?.drivers ?? [])]
      .filter((driver) =>
        (!keyword || [driver.driverName, driver.phone, driver.plateNo].some((value) => value.toLowerCase().includes(keyword)))
        && (driverFilter === "all" || category(driver) === driverFilter),
      )
      .sort((left, right) => rank[category(left)] - rank[category(right)] || left.phone.localeCompare(right.phone));
  }, [current?.drivers, driverFilter, driverSearch]);

  const totals = (reports.data?.items ?? []).reduce(
    (value, report) => ({
      expected: value.expected + (report.metrics?.expected ?? 0),
      complete: value.complete + (report.metrics?.complete ?? 0),
      errors: value.errors + (report.metrics?.driverErrors ?? 0) + (report.metrics?.needsAttention ?? 0),
      waybills: value.waybills + (report.metrics?.uniqueWaybills ?? 0),
    }),
    { expected: 0, complete: 0, errors: 0, waybills: 0 },
  );

  function selectReport(id?: string) {
    setSelectedId(id);
    setExpandedDriverId(undefined);
    setReportWaybill(undefined);
    const next = new URLSearchParams(window.location.search);
    if (id) next.set("reportId", id); else next.delete("reportId");
    next.delete("driverId");
    const url = `${window.location.pathname}?${next.toString()}`;
    if (id) window.history.pushState(null, "", url);
    else window.history.replaceState(null, "", url);
  }

  function selectDriver(accountId?: string) {
    setExpandedDriverId(accountId);
    setReportWaybill(undefined);
    const next = new URLSearchParams(window.location.search);
    if (accountId) next.set("driverId", accountId); else next.delete("driverId");
    const url = `${window.location.pathname}?${next.toString()}`;
    if (accountId) window.history.pushState(null, "", url);
    else window.history.replaceState(null, "", url);
  }

  async function resend() {
    if (!selectedId) return;
    try {
      await post(`/api/daily-reports/${selectedId}/resend`);
      setMessage("日报已加入飞书发送队列");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["daily-reports"] }),
        queryClient.invalidateQueries({ queryKey: ["daily-report", selectedId] }),
      ]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "重发失败");
    }
  }

  const columns: ColumnDef<DailyReportListItem>[] = [
    {
      header: "业务日 / 定时任务",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.businessDate}</strong>
          <span>{row.original.planName}</span>
        </div>
      ),
    },
    {
      header: "线路 / 分组",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.templateName}</strong>
          <span>{row.original.groupName}</span>
        </div>
      ),
    },
    {
      header: "司机结果",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.metrics?.complete ?? 0} / {row.original.metrics?.expected ?? 0} 完成</strong>
          <span>过期 {row.original.metrics?.expired ?? 0} · 人工处理 {row.original.metrics?.needsAttention ?? 0} · 异常 {row.original.metrics?.driverErrors ?? 0}</span>
        </div>
      ),
    },
    {
      header: "业务操作",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>接单 {operationReportText(row.original, "accept")}</strong>
          <span>装货 {operationReportText(row.original, "load")} · 卸货 {operationReportText(row.original, "unload")}</span>
          <span>绑定 {row.original.bindings?.matched ?? 0}/{row.original.bindings?.total ?? 0} · 多候选 {row.original.bindings?.multipleCandidates ?? 0} · 阻断 {row.original.bindings?.acceptConflictBlocks ?? 0}</span>
        </div>
      ),
    },
    {
      header: "状态 / 飞书",
      cell: ({ row }) => (
        <div className="primary-cell">
          <Badge tone={reportStatusTone(row.original.status)}>{reportStatusText(row.original.status)}</Badge>
          <span>启动 {row.original.feishu?.startStatus ?? "-"} · 最终 {row.original.feishu?.finalStatus ?? "-"}</span>
        </div>
      ),
    },
    {
      header: "操作",
      cell: ({ row }) => <Button variant="ghost" onClick={() => selectReport(row.original.id)}>查看详情</Button>,
    },
  ];

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>每日汇总</h1>
          <p>查看每日定时任务的启动、司机执行结果、运单变化和飞书送达状态。</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => Promise.all([reports.refetch(), waybillSummary.refetch()])}
        >
          <RefreshCw size={16} />刷新
        </Button>
      </section>
      {message && <div className="notice-bar">{message}</div>}
      {waybillSummary.isError && <div className="notice-bar">运单汇总加载失败，请点击刷新重试。</div>}
      <section className="report-metric-group">
        <div className="report-metric-heading">
          <div>
            <h2>运单汇总</h2>
            <span>{dateFrom && dateFrom === dateTo ? dateFrom : `${dateFrom || "不限开始日期"} 至 ${dateTo || "不限结束日期"}`} · 按当前筛选条件实时统计</span>
          </div>
          <small>进行中运单 {waybillSummary.data?.inProgressWaybills ?? "-"}</small>
        </div>
        <section className="report-metrics report-today-metrics">
          <div className="metric-success"><span>成功运单</span><strong>{waybillSummary.data?.successfulWaybills ?? "-"}</strong></div>
          <div className={waybillSummary.data?.failedWaybills ? "metric-alert" : ""}><span>失败运单</span><strong>{waybillSummary.data?.failedWaybills ?? "-"}</strong></div>
          <div><span>成功率</span><strong>{waybillSummary.data ? `${waybillSummary.data.successRate.toFixed(1)}%` : "-"}</strong></div>
          <div><span>参与运输司机</span><strong>{waybillSummary.data?.participatingDrivers ?? "-"}</strong></div>
        </section>
      </section>
      <section className="report-metric-group">
        <div className="report-metric-heading">
          <div>
            <h2>筛选汇总</h2>
            <span>当前筛选条件下的报告数据</span>
          </div>
        </div>
        <section className="report-metrics">
          <div><span>计划司机</span><strong>{totals.expected}</strong></div>
          <div><span>完成线路</span><strong>{totals.complete}</strong></div>
          <div><span>涉及运单</span><strong>{totals.waybills}</strong></div>
          <div className={totals.errors ? "metric-alert" : ""}><span>异常待处理</span><strong>{totals.errors}</strong></div>
        </section>
      </section>
      <div className="report-date-presets" role="group" aria-label="日期快捷筛选">
        <span>快捷日期</span>
        {([1, "yesterday", 7, 14, 30] as const).map((days) => {
          const range = reportDateRange(days, businessToday);
          const active = dateFrom === range.dateFrom && dateTo === range.dateTo;
          return <Button key={days} variant={active ? "primary" : "secondary"} aria-pressed={active}
            onClick={() => { setDateFrom(range.dateFrom); setDateTo(range.dateTo); setPage(1); }}>
            {days === 1 ? "今日" : days === "yesterday" ? "昨日" : `近 ${days} 天`}
          </Button>;
        })}
      </div>
      <section className="toolbar report-toolbar">
        <Field label="开始日期"><Input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setPage(1); }} /></Field>
        <Field label="结束日期"><Input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setPage(1); }} /></Field>
        <Field label="管理员">
          <Select value={authorizedUserId} onChange={(event) => {
            setAuthorizedUserId(event.target.value);
            changePlans([]);
            setPage(1);
          }}>
            <option value="">全部管理员</option>
            {(currentUser.role === "super_admin" ? administrators.data ?? [] : [currentUser]).map((user) => (
              <option key={user.id} value={user.id}>{user.displayName || user.username}</option>
            ))}
          </Select>
        </Field>
        <div className="field">
          <span className="field-label" id="report-task-filter-label">定时任务</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button type="button" className="input report-task-trigger" disabled={plans.isLoading}
                aria-labelledby="report-task-filter-label report-task-filter-value">
                <span id="report-task-filter-value">{planIds.length ? `已选 ${planIds.length} 个任务` : "全部任务"}</span>
                <ChevronDown size={15} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="report-task-menu" align="start" sideOffset={4}>
                <DropdownMenu.CheckboxItem className="report-task-option" checked={!planIds.length}
                  onSelect={(event) => event.preventDefault()} onCheckedChange={() => changePlans([])}>
                  <span className="report-task-check"><DropdownMenu.ItemIndicator><Check size={14} /></DropdownMenu.ItemIndicator></span>
                  全部任务
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.Separator className="report-task-separator" />
                {plans.data?.items.map((plan) => (
                  <DropdownMenu.CheckboxItem key={plan.id} className="report-task-option" checked={planIds.includes(plan.id)}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(checked) => changePlans(checked ? [...planIds, plan.id] : planIds.filter((id) => id !== plan.id))}>
                    <span className="report-task-check"><DropdownMenu.ItemIndicator><Check size={14} /></DropdownMenu.ItemIndicator></span>
                    {plan.name}
                  </DropdownMenu.CheckboxItem>
                ))}
                {!plans.isLoading && !plans.data?.items.length && <div className="report-task-empty">{plans.isError ? "任务加载失败，请刷新重试" : "暂无定时任务"}</div>}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
        <Field label="结果状态">
          <Select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
            <option value="">全部状态</option>
            <option value="preparing">准备中</option><option value="running">运行中</option>
            <option value="complete">完成</option><option value="partial">部分完成</option><option value="missed">未启动</option>
          </Select>
        </Field>
      </section>
      <DataTable
        data={reports.data?.items ?? []}
        columns={columns}
        loading={reports.isLoading}
        emptyTitle="暂无每日汇总"
        emptyDescription="定时任务到达准备时间后，会在这里生成当天报告。"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={reports.data?.total ?? 0}
        loading={reports.isFetching}
        onPageChange={setPage}
        onPageSizeChange={(value) => { setPageSize(value); setPage(1); }}
      />
      <Drawer
        open={!!selectedId}
        onOpenChange={(open) => !open && selectReport(undefined)}
        title={reportWaybill ? `运单 ${reportWaybill.waybillId}` : "每日任务执行报告"}
      >
        {reportWaybill ? (
          <>
            <Button variant="ghost" onClick={() => setReportWaybill(undefined)}>
              <ArrowLeft size={15} />返回司机线路详情
            </Button>
            {reportWaybillDetail.isLoading && <LoadingState label="正在加载运单详情" />}
            <WaybillDetailContent waybill={reportWaybillDetail.data ?? reportWaybill} />
          </>
        ) : (
          <>
            {detail.isLoading && <LoadingState label="正在加载日报详情" />}
            {current && (
              <div className="report-detail">
                <section className="report-summary-head">
                  <div><span>{current.businessDate}</span><strong>{current.templateName}</strong><small>{current.planName} · {current.groupName}</small></div>
                  <Badge tone={reportStatusTone(current.status)}>{reportStatusText(current.status)}</Badge>
                </section>
                <section className="report-detail-metrics">
                  <div><span>计划</span><strong>{current.metrics.expected}</strong></div>
                  <div><span>完成</span><strong>{current.metrics.complete}</strong></div>
                  <div><span>过期</span><strong>{current.metrics.expired}</strong></div>
                  <div><span>需处理</span><strong>{current.metrics.needsAttention}</strong></div>
                  <div><span>完成率</span><strong>{current.metrics.completionRate}%</strong></div>
                </section>
                <section className="report-detail-metrics">
                  <div><span>运单绑定</span><strong>{current.bindings?.matched ?? 0}/{current.bindings?.total ?? 0}</strong></div>
                  <div><span>多候选</span><strong>{current.bindings?.multipleCandidates ?? 0}</strong></div>
                  <div><span>绑定失效</span><strong>{current.bindings?.invalid ?? 0}</strong></div>
                  <div><span>接单阻断</span><strong>{current.bindings?.acceptConflictBlocks ?? 0}</strong></div>
                </section>
                {!!current.routePreflight?.legs?.length && <section className="report-section">
                  <h3>司机分段覆盖</h3>
                  <p>检查时间 {formatDate(current.routePreflight.checkedAt)}。按每名司机统计；查询未完成的单独列出。</p>
                  {current.routePreflight.legs.map((leg) => <details key={leg.index}>
                    <summary>第 {leg.index} 段 · {leg.route} · 已覆盖 {leg.matchedAccountCount}/{leg.checkedAccountCount} 人 · 未匹配 {leg.missingAccountCount ?? 0} 人 · 待查 {leg.unknownAccountCount ?? 0} 人</summary>
                    {!!leg.missingDrivers?.length && <p>{leg.missingDrivers.map((driver) => `${driver.driverName} ${driver.plateNo}`).join("；")}</p>}
                  </details>)}
                </section>}
                <section className="report-section">
                  <div className="section-header report-driver-heading">
                    <div><h3>司机明细</h3><p>异常司机优先显示，点击司机展开线路执行情况。</p></div>
                  </div>
                  <div className="report-driver-toolbar">
                    <label className="search-box"><Search size={15} /><input value={driverSearch} onChange={(event) => setDriverSearch(event.target.value)} placeholder="搜索司机、手机号或车牌" /></label>
                    <Select value={driverFilter} onChange={(event) => setDriverFilter(event.target.value as typeof driverFilter)}>
                      <option value="all">全部司机</option>
                      <option value="anomaly">仅异常</option>
                      <option value="incomplete">未完成</option>
                      <option value="complete">已完成</option>
                    </Select>
                  </div>
                  <div className="report-driver-list">
                    {visibleDrivers.map((driver) => {
                      const expanded = expandedDriverId === driver.accountId;
                      return (
                        <div className={`report-driver-item ${expanded ? "is-expanded" : ""}`} key={driver.accountId}>
                          <button type="button" aria-expanded={expanded} onClick={() => selectDriver(expanded ? undefined : driver.accountId)}>
                            <span><strong>{driver.driverName || "未知司机"} · {driver.phone}</strong><small>{driver.plateNo || "未配置车牌"} · 完成 {driver.completedLegs}/{driver.totalLegs} 段</small></span>
                            <span><Badge tone={driver.error ? "danger" : driver.status === "complete" ? "success" : "warning"}>{driver.autoStatus ? autoStatusText(driver.autoStatus as RouteRun["autoStatus"]) : routeRunStatusText(driver.status)}</Badge>{driver.error && <small>{driver.error}</small>}</span>
                            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                          {expanded && (
                            <div className="report-driver-detail">
                              {driverDetail.isLoading && <LoadingState label="正在加载司机线路详情" />}
                              {driverDetail.isError && <EmptyState title="司机详情加载失败" description="请检查授权或网络后重试。" action={<Button variant="secondary" onClick={() => driverDetail.refetch()}>重新加载</Button>} />}
                              {driverDetail.data && (
                                <>
                                  {driverDetail.data.detailSource === "historical_reconstructed" && <div className="notice-bar">该详情由现有本地历史记录重建，缺失字段会标记为历史未知。</div>}
                                  {driver.timingSummary && <p>{driver.timingSummary}</p>}
                                  {driver.completedLegs < driver.totalLegs && driver.scheduleHealth && (
                                    <div className={`notice-bar${driver.scheduleHealth.warnings.length ? " notice-warning" : ""}`}>
                                      <p>参考完成时间：{formatDate(driver.scheduleHealth.estimatedFinishAt)}{driver.scheduleHealth.unknownTransferCount ? "（部分转场耗时待核实）" : ""}</p>
                                      {!!driver.scheduleHealth.warnings.length && <p>{driver.scheduleHealth.warnings.join("；")}</p>}
                                      <small>估算依据：{driver.scheduleHealth.basis}</small>
                                    </div>
                                  )}
                                  {!!driver.orderDiagnostics?.length && <div className="notice-bar">
                                    <strong>发单及匹配诊断</strong>
                                    {driver.orderDiagnostics.map((item) => <p key={item.legIndex}>第 {item.legIndex} 段：{item.reason || item.status} · 待接 {item.pendingCount ?? "-"} · 方向匹配 {item.endpointMatchCount ?? "-"} · 已占用 {item.reservedCount ?? 0} · {formatDate(item.checkedAt)}</p>)}
                                  </div>}
                                  <RouteLegDetails legs={driverDetail.data.legs} onWaybill={setReportWaybill} />
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {!visibleDrivers.length && <EmptyState title="没有匹配的司机" description="请调整搜索或状态筛选。" />}
                  </div>
                </section>
                <section className="report-section">
                  <h3>通知时间线</h3>
                  <div className="report-event-list">
                    {current.events?.map((event) => (
                      <div key={event.id}><span className={`notice-level notice-${event.severity}`} /><div><strong>{event.title}</strong><p>{event.message}</p><small>{formatDate(event.created_at)} · {event.status === "suppressed" ? "仅 Web 记录" : `飞书 ${event.status}`}</small></div></div>
                    ))}
                    {!current.events?.length && <span className="muted-text">暂无事件</span>}
                  </div>
                </section>
                {currentUser.role === "super_admin" && <Button onClick={resend}><BellRing size={16} />重发飞书汇总</Button>}
              </div>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}

export function JobsPage({ currentUser }: { currentUser: CurrentUser }) {
  const [tab, setTab] = useState<"jobs" | "audit">("jobs");
  const [auditSearch, setAuditSearch] = useState("");
  const [jobPage, setJobPage] = useState(1);
  const [jobPageSize, setJobPageSize] = useState<50 | 100 | 200>(50);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState<50 | 100 | 200>(50);
  const jobs = useQuery({
    queryKey: ["jobs", jobPage, jobPageSize],
    queryFn: () =>
      api<PagedResponse<Job>>(`/api/jobs?page=${jobPage}&pageSize=${jobPageSize}`),
    refetchInterval: 3_000,
  });
  const audit = useQuery({
    queryKey: ["audit", currentUser.id, auditSearch, auditPage, auditPageSize],
    queryFn: () =>
      api<PagedResponse<AuditLog>>(
        `/api/audit?search=${encodeURIComponent(auditSearch)}&page=${auditPage}&pageSize=${auditPageSize}`,
      ),
    enabled: tab === "audit",
  });
  useEffect(() => {
    setAuditPage(1);
  }, [auditSearch, auditPageSize]);
  async function download(itemId: string) {
    const response = await fetch(`/api/evidence/${itemId}`, {
      credentials: "include",
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${itemId}-report.md`;
    link.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>任务与操作日志</h1>
          <p>
            {currentUser.role === "super_admin"
              ? "查看全部管理员的写操作、业务结果和系统任务。"
              : "查看自己的写操作、业务结果和系统任务。"}
          </p>
        </div>
      </section>
      <div className="segmented compact">
        <button
          className={tab === "jobs" ? "active" : ""}
          onClick={() => setTab("jobs")}
        >
          任务
        </button>
        <button
          className={tab === "audit" ? "active" : ""}
          onClick={() => setTab("audit")}
        >
          操作日志
        </button>
      </div>
      {tab === "jobs" ? (
        <div className="page-stack compact-stack">
          <div className="job-list">
            {jobs.isLoading && <LoadingState label="正在加载任务记录" />}
            {jobs.data?.items.map((job) => (
              <section className="job-group" key={job.id}>
              <div className="job-header">
                <div>
                  <strong>{operationName(job.operation)}批次</strong>
                  <span>{job.id}</span>
                </div>
                <Badge tone={statusTone(job.status)}>{job.status}</Badge>
                <time>{formatDate(job.createdAt)}</time>
              </div>
              {job.items.map((item) => (
                <div className="job-item" key={item.id}>
                  <span
                    className={`activity-symbol activity-${statusTone(item.status)}`}
                  >
                    {item.status === "running" ? (
                      <Loader2 className="spin" size={15} />
                    ) : item.status === "success" ? (
                      <Check size={15} />
                    ) : item.status === "failed" ? (
                      <X size={15} />
                    ) : (
                      <Clock3 size={15} />
                    )}
                  </span>
                  <div>
                    <strong>
                      {item.phone} · {item.waybillId}
                    </strong>
                    <span>
                      {operationName(item.operation)} ·{" "}
                      {item.error ||
                        (item.result?.reason as string) ||
                        item.status}
                    </span>
                    {item.scheduledAt && (
                      <span>
                        计划 {formatDate(item.scheduledAt)}
                        {item.scheduleSequence
                          ? ` · 第 ${item.scheduleSequence} 位`
                          : ""}
                        {item.scheduleDeadlineAt
                          ? ` · 截止 ${formatDate(item.scheduleDeadlineAt)}`
                          : ""}
                      </span>
                    )}
                    {item.rescheduledAt && (
                      <span className="warning-text">
                        已于 {formatDate(item.rescheduledAt)} 重新排程
                      </span>
                    )}
                  </div>
                  <Badge tone={statusTone(item.status)}>{item.status}</Badge>
                  {item.evidenceAvailable && (
                    <IconButton
                      label="下载摘要报告"
                      onClick={() => download(item.id)}
                    >
                      <Download size={16} />
                    </IconButton>
                  )}
                </div>
              ))}
              </section>
            ))}
            {!jobs.isLoading && !jobs.data?.items.length && (
              <EmptyState
                title="还没有任务"
                description="批量预检和批准后，执行状态会实时显示。"
              />
            )}
          </div>
          <Pagination
            page={jobPage}
            pageSize={jobPageSize}
            total={jobs.data?.total ?? 0}
            loading={jobs.isFetching}
            onPageChange={setJobPage}
            onPageSizeChange={(value) => {
              setJobPageSize(value);
              setJobPage(1);
            }}
          />
        </div>
      ) : (
        <div className="page-stack compact-stack">
          <section className="toolbar">
            <label className="search-box">
              <Search size={16} />
              <input
                value={auditSearch}
                onChange={(event) => setAuditSearch(event.target.value)}
                placeholder="搜索管理员、事件、目标或 IP"
              />
            </label>
            <Button variant="secondary" onClick={() => audit.refetch()}>
              <RefreshCw size={16} />
              刷新
            </Button>
            <span className="toolbar-count">
              {audit.isFetching ? "加载中..." : `${audit.data?.total ?? 0} 条记录`}
            </span>
          </section>
          <div className="audit-list">
            {audit.isLoading && <LoadingState label="正在加载操作日志" />}
            {audit.data?.items.map((item) => {
              const statusCode = Number(item.detail.statusCode);
              return (
                <div className="audit-row" key={item.id}>
                  <span>{item.event_type}</span>
                  <div>
                    <strong>{item.summary}</strong>
                    <small>
                      {item.actor} · {item.target_type || "system"}{" "}
                      {item.target_id || ""}
                      {item.remote_addr ? ` · ${item.remote_addr}` : ""}
                    </small>
                    {Object.keys(item.detail).length > 0 && (
                      <details className="audit-details">
                        <summary>查看详情</summary>
                        <pre>{JSON.stringify(item.detail, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                  <div className="audit-result">
                    {Number.isFinite(statusCode) && (
                      <Badge tone={statusCode < 400 ? "success" : "danger"}>
                        HTTP {statusCode}
                      </Badge>
                    )}
                    <time>{formatDate(item.created_at)}</time>
                  </div>
                </div>
              );
            })}
            {!audit.isLoading && !audit.data?.items.length && (
              <EmptyState
                title="没有匹配的操作日志"
                description="执行写操作后，管理员、结果和时间会显示在这里。"
              />
            )}
          </div>
          <Pagination
            page={auditPage}
            pageSize={auditPageSize}
            total={audit.data?.total ?? 0}
            loading={audit.isFetching}
            onPageChange={setAuditPage}
            onPageSizeChange={setAuditPageSize}
          />
        </div>
      )}
    </div>
  );
}

export function AccessPage() {
  const queryClient = useQueryClient();
  const groups = useQuery({
    queryKey: ["admin-groups"],
    queryFn: () => api<DriverGroup[]>("/api/admin/groups"),
  });
  const users = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api<AdminUser[]>("/api/admin/users"),
  });
  const [message, setMessage] = useState("");
  const [groupOpen, setGroupOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DriverGroup>();
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [userOpen, setUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser>();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userEnabled, setUserEnabled] = useState(true);
  const [grantedGroups, setGrantedGroups] = useState<Set<string>>(new Set());
  const [resetUser, setResetUser] = useState<AdminUser>();
  const [resetPassword, setResetPassword] = useState("");
  const [membersGroup, setMembersGroup] = useState<DriverGroup>();
  const [memberSearch, setMemberSearch] = useState("");
  const [memberEnabled, setMemberEnabled] = useState<"" | "true" | "false">("");
  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState<50 | 100 | 200>(50);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [moveGroupId, setMoveGroupId] = useState("");
  const [memberEditor, setMemberEditor] = useState<Account>();
  const [memberBusy, setMemberBusy] = useState(false);

  const members = useQuery({
    queryKey: ["group-members", membersGroup?.id, memberSearch, memberEnabled, memberPage, memberPageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        groupId: membersGroup!.id,
        search: memberSearch,
        page: String(memberPage),
        pageSize: String(memberPageSize),
      });
      if (memberEnabled) params.set("enabled", memberEnabled);
      return api<PagedResponse<Account>>(`/api/accounts?${params}`);
    },
    enabled: !!membersGroup,
  });

  const assignableGroups = (groups.data ?? []).filter((group) => !group.isSystem);
  async function refreshAccessData() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-groups"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
      queryClient.invalidateQueries({ queryKey: ["accounts"] }),
      queryClient.invalidateQueries({ queryKey: ["group-members"] }),
    ]);
  }

  function openGroup(group?: DriverGroup) {
    setEditingGroup(group);
    setGroupName(group?.name ?? "");
    setGroupDescription(group?.description ?? "");
    setGroupOpen(true);
  }

  async function saveGroup() {
    try {
      if (editingGroup) {
        await put(`/api/admin/groups/${editingGroup.id}`, {
          name: groupName,
          description: groupDescription,
        });
      } else {
        await post("/api/admin/groups", {
          name: groupName,
          description: groupDescription,
        });
      }
      setGroupOpen(false);
      setMessage(editingGroup ? "分组已更新" : "分组已创建");
      await refreshAccessData();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存分组失败");
    }
  }

  function openUser(user?: AdminUser) {
    setEditingUser(user);
    setUsername(user?.username ?? "");
    setDisplayName(user?.displayName ?? "");
    setUserPassword("");
    setUserEnabled(user?.enabled ?? true);
    setGrantedGroups(new Set(user?.groupIds ?? []));
    setUserOpen(true);
  }

  async function saveUser() {
    try {
      const groupIds = Array.from(grantedGroups);
      if (editingUser) {
        await put(`/api/admin/users/${editingUser.id}`, {
          displayName,
          enabled: userEnabled,
        });
        await put(`/api/admin/users/${editingUser.id}/group-grants`, {
          groupIds,
        });
      } else {
        await post("/api/admin/users", {
          username,
          displayName,
          password: userPassword,
          groupIds,
        });
      }
      setUserOpen(false);
      setMessage(editingUser ? "子账号已更新" : "子账号已创建");
      await refreshAccessData();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存子账号失败");
    }
  }

  async function submitPasswordReset() {
    if (!resetUser) return;
    try {
      await post(`/api/admin/users/${resetUser.id}/reset-password`, {
        password: resetPassword,
      });
      setResetUser(undefined);
      setResetPassword("");
      setMessage(`已重置 ${resetUser.username} 的密码，现有会话已失效`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "重置密码失败");
    }
  }

  function openMembers(group: DriverGroup) {
    setMembersGroup(group);
    setMemberSearch("");
    setMemberEnabled("");
    setMemberPage(1);
    setSelectedMembers(new Set());
    setMoveGroupId("");
  }

  async function assignGroup() {
    try {
      const result = await post<any>("/api/admin/accounts/group-assignment", {
        accountIds: Array.from(selectedMembers),
        groupId: moveGroupId,
      });
      setSelectedMembers(new Set());
      setMessage(`已移动 ${result.updated} 名司机，取消 ${result.cancelledJobs} 个未执行任务`);
      await refreshAccessData();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "分组分配失败");
    }
  }

  async function recheckMember(account: Account) {
    setMemberBusy(true);
    try {
      const result = await post<any>(`/api/accounts/${account.id}/recheck`);
      setMessage(result.ok ? `${account.driverName || account.phone} 线上复核完成` : result.error || "复核失败");
      await refreshAccessData();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "复核失败");
    } finally {
      setMemberBusy(false);
    }
  }

  async function toggleMember(account: Account) {
    if (account.enabled && !window.confirm(`确认停用司机 ${account.driverName || account.phone}？`)) return;
    setMemberBusy(true);
    try {
      if (account.enabled) await del(`/api/accounts/${account.id}`);
      else await post(`/api/accounts/${account.id}/enable`);
      setMessage(account.enabled ? "司机账号已停用" : "司机账号已恢复，未重新加入旧计划");
      await refreshAccessData();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "账号状态更新失败");
    } finally {
      setMemberBusy(false);
    }
  }

  const groupColumns: ColumnDef<DriverGroup>[] = [
    {
      header: "分组",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.name}</strong>
          <span>{row.original.description || "未填写说明"}</span>
        </div>
      ),
    },
    {
      header: "司机",
      cell: ({ row }) => (
        <div className="group-account-counts">
          <strong>{row.original.accountCount}</strong>
          <span>启用 {row.original.activeAccountCount} · 停用 {row.original.disabledAccountCount}</span>
        </div>
      ),
    },
    { header: "授权用户", cell: ({ row }) => `${row.original.userCount} 个` },
    {
      header: "类型",
      cell: ({ row }) => (
        <Badge tone={row.original.isSystem ? "warning" : "neutral"}>
          {row.original.isSystem ? "系统保留" : "普通分组"}
        </Badge>
      ),
    },
    {
      header: "操作",
      cell: ({ row }) => (
          <div className="inline-actions">
            <Button variant="ghost" onClick={() => openMembers(row.original)}><Users size={14} />成员</Button>
            {!row.original.isSystem && <>
            <Button variant="ghost" onClick={() => openGroup(row.original)}>编辑</Button>
            <IconButton
              label="删除分组"
              disabled={row.original.accountCount > 0 || row.original.userCount > 0}
              onClick={async () => {
                await del(`/api/admin/groups/${row.original.id}`);
                await refreshAccessData();
              }}
            >
              <Trash2 size={15} />
            </IconButton>
            </>}
          </div>
        ),
    },
  ];

  const userColumns: ColumnDef<AdminUser>[] = [
    {
      header: "用户",
      cell: ({ row }) => (
        <div className="primary-cell">
          <strong>{row.original.username}</strong>
          <span>{row.original.displayName || "未设置显示名称"}</span>
        </div>
      ),
    },
    {
      header: "角色",
      cell: ({ row }) => row.original.role === "super_admin" ? "超级管理员" : "子账号",
    },
    {
      header: "授权分组",
      cell: ({ row }) => row.original.role === "super_admin" ? "全部" : row.original.groupNames.join("、") || "未授权",
    },
    {
      header: "获配线路",
      cell: ({ row }) => row.original.role === "super_admin" ? "全部" : row.original.routeTemplateNames.join("、") || "未分配",
    },
    {
      header: "状态",
      cell: ({ row }) => <Badge tone={row.original.enabled ? "success" : "danger"}>{row.original.enabled ? "启用" : "停用"}</Badge>,
    },
    { header: "最近登录", cell: ({ row }) => formatDate(row.original.lastLoginAt) },
    {
      header: "操作",
      cell: ({ row }) => row.original.role === "super_admin" ? null : (
        <div className="inline-actions">
          <Button variant="ghost" onClick={() => openUser(row.original)}>编辑</Button>
          <Button variant="ghost" onClick={() => setResetUser(row.original)}>
            <KeyRound size={14} />重置密码
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>用户与分组</h1>
          <p>按司机分组授权子账号，未授权的数据不会出现在查询或业务操作中。</p>
        </div>
        <div className="heading-actions">
          <Button variant="secondary" onClick={() => openGroup()}><Plus size={16} />新建分组</Button>
          <Button onClick={() => openUser()}><Plus size={16} />新建子账号</Button>
        </div>
      </section>
      {message && <div className="notice-bar">{message}</div>}
      <section className="access-grid">
        <div className="access-panel">
          <div className="section-header"><div><h2>司机分组</h2><p>一个司机只能属于一个分组</p></div></div>
          <DataTable data={groups.data ?? []} columns={groupColumns} emptyTitle="暂无司机分组" />
        </div>
        <div className="access-panel">
          <div className="section-header"><div><h2>管理用户</h2><p>子账号可授权多个司机分组和多条线路；每次任务只选择一条线路</p></div></div>
          <DataTable data={users.data ?? []} columns={userColumns} emptyTitle="暂无管理用户" />
        </div>
      </section>
      <Drawer
        open={!!membersGroup}
        onOpenChange={(open) => !open && setMembersGroup(undefined)}
        title={`${membersGroup?.name || "司机分组"} · 成员管理`}
      >
        <div className="group-members-panel">
          <div className="group-members-toolbar">
            <label className="search-box">
              <Search size={16} />
              <input value={memberSearch} onChange={(event) => { setMemberSearch(event.target.value); setMemberPage(1); }} placeholder="搜索姓名、手机号或车牌" />
            </label>
            <Select value={memberEnabled} onChange={(event) => { setMemberEnabled(event.target.value as "" | "true" | "false"); setMemberPage(1); }}>
              <option value="">全部状态</option>
              <option value="true">仅启用</option>
              <option value="false">仅停用</option>
            </Select>
          </div>
          <div className="member-selection-bar">
            <label className="check-row">
              <input
                type="checkbox"
                checked={!!members.data?.items.length && members.data.items.every((item) => selectedMembers.has(item.id))}
                onChange={(event) => {
                  const next = new Set(selectedMembers);
                  for (const item of members.data?.items ?? []) event.target.checked ? next.add(item.id) : next.delete(item.id);
                  setSelectedMembers(next);
                }}
              />
              选择本页
            </label>
            <span>已选 {selectedMembers.size} 名</span>
            <Select value={moveGroupId} onChange={(event) => setMoveGroupId(event.target.value)}>
              <option value="">移动到...</option>
              {(groups.data ?? []).filter((group) => group.id !== membersGroup?.id).map((group) => (
                <option value={group.id} key={group.id}>{group.name}</option>
              ))}
            </Select>
            <Button disabled={!selectedMembers.size || !moveGroupId || memberBusy} onClick={assignGroup}>移动</Button>
          </div>
          <div className="group-member-list">
            {(members.data?.items ?? []).map((account) => (
              <div className="group-member-row" key={account.id}>
                <input
                  type="checkbox"
                  checked={selectedMembers.has(account.id)}
                  onChange={(event) => {
                    const next = new Set(selectedMembers);
                    event.target.checked ? next.add(account.id) : next.delete(account.id);
                    setSelectedMembers(next);
                  }}
                />
                <div className="primary-cell">
                  <strong>{account.driverName || "未识别司机"}</strong>
                  <span>{account.phone} · {account.plateNo || "未回填车辆"}</span>
                </div>
                <div className="member-statuses">
                  <Badge tone={account.enabled ? "success" : "danger"}>{account.enabled ? "启用" : "停用"}</Badge>
                  <Badge tone={account.credentialStatus === "ok" ? "success" : "warning"}>登录{account.credentialStatus === "ok" ? "正常" : "待处理"}</Badge>
                  <Badge tone={account.vehicleStatus === "verified" ? "success" : "warning"}>三证{account.vehicleStatus === "verified" ? "正常" : "待处理"}</Badge>
                  <Badge tone={account.scheduledTasks.length ? "info" : "neutral"}>{account.scheduledTasks.length ? `${account.scheduledTasks.length} 个计划` : "无计划"}</Badge>
                </div>
                <div className="inline-actions">
                  <IconButton label="编辑司机" onClick={() => setMemberEditor(account)}><Pencil size={15} /></IconButton>
                  <IconButton label="线上复核" disabled={memberBusy} onClick={() => recheckMember(account)}><RefreshCw size={15} /></IconButton>
                  <IconButton label={account.enabled ? "停用司机" : "恢复司机"} disabled={memberBusy} onClick={() => toggleMember(account)}>
                    {account.enabled ? <PowerOff size={15} /> : <Power size={15} />}
                  </IconButton>
                </div>
              </div>
            ))}
            {!members.isLoading && !members.data?.items.length && <EmptyState title="没有匹配的司机" description="该分组为空，或当前筛选没有结果。" />}
          </div>
          <Pagination
            page={memberPage}
            pageSize={memberPageSize}
            total={members.data?.total ?? 0}
            loading={members.isFetching}
            onPageChange={setMemberPage}
            onPageSizeChange={(value) => { setMemberPageSize(value); setMemberPage(1); }}
          />
        </div>
      </Drawer>
      <AccountEditorDrawer
        account={memberEditor}
        groups={groups.data ?? []}
        canDeletePermanently
        onClose={() => setMemberEditor(undefined)}
        onChanged={refreshAccessData}
        onMessage={setMessage}
      />
      <Dialog open={groupOpen} onOpenChange={setGroupOpen} title={editingGroup ? "编辑分组" : "新建分组"}>
        <Field label="分组名称"><Input value={groupName} onChange={(event) => setGroupName(event.target.value)} /></Field>
        <Field label="说明"><Input value={groupDescription} onChange={(event) => setGroupDescription(event.target.value)} /></Field>
        <div className="dialog-actions"><Button variant="secondary" onClick={() => setGroupOpen(false)}>取消</Button><Button disabled={!groupName} onClick={saveGroup}>保存</Button></div>
      </Dialog>
      <Dialog open={userOpen} onOpenChange={setUserOpen} title={editingUser ? "编辑子账号" : "新建子账号"} wide>
        <div className="config-grid">
          <Field label="用户名"><Input value={username} disabled={!!editingUser} onChange={(event) => setUsername(event.target.value)} /></Field>
          <Field label="显示名称"><Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></Field>
          {!editingUser && <Field label="初始密码"><Input type="password" value={userPassword} onChange={(event) => setUserPassword(event.target.value)} /></Field>}
          {editingUser && <label className="check-row"><input type="checkbox" checked={userEnabled} onChange={(event) => setUserEnabled(event.target.checked)} />启用账号</label>}
        </div>
        <Field label="授权分组" hint="系统未分组不能授权">
          <div className="grant-grid">
            {assignableGroups.map((group) => (
              <label key={group.id}>
                <input
                  type="checkbox"
                  checked={grantedGroups.has(group.id)}
                  onChange={(event) => {
                    const next = new Set(grantedGroups);
                    event.target.checked ? next.add(group.id) : next.delete(group.id);
                    setGrantedGroups(next);
                  }}
                />
                <span>{group.name}<small>{group.accountCount} 个司机</small></span>
              </label>
            ))}
          </div>
        </Field>
        <div className="dialog-actions"><Button variant="secondary" onClick={() => setUserOpen(false)}>取消</Button><Button disabled={!username || (!editingUser && userPassword.length < 8)} onClick={saveUser}>保存</Button></div>
      </Dialog>
      <Dialog open={!!resetUser} onOpenChange={(open) => !open && setResetUser(undefined)} title="重置子账号密码">
        <p className="dialog-description">重置后，{resetUser?.username} 的现有登录会话将立即失效。</p>
        <Field label="新密码"><Input type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} /></Field>
        <div className="dialog-actions"><Button variant="secondary" onClick={() => setResetUser(undefined)}>取消</Button><Button disabled={resetPassword.length < 8} onClick={submitPasswordReset}>确认重置</Button></div>
      </Dialog>
    </div>
  );
}

export function ProxyPage() {
  const queryClient = useQueryClient();
  const config = useQuery({
    queryKey: ["proxy-config"],
    queryFn: () => api<ProxyConfig>("/api/system/proxy"),
  });
  const balance = useQuery({
    queryKey: ["proxy-balance"],
    queryFn: () => api<ProxyBalance>("/api/system/proxy/balance"),
    enabled: !!config.data?.authKeyMasked,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [poolTarget, setPoolTarget] = useState("1");
  const [authKey, setAuthKey] = useState("");
  const [authPwd, setAuthPwd] = useState("");
  const [showAuthKey, setShowAuthKey] = useState(false);
  const [showAuthPwd, setShowAuthPwd] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "warning" | "danger">("success");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!config.data) return;
    setPoolTarget(String(config.data.poolTarget));
  }, [config.data]);

  async function saveProxy() {
    setBusy(true);
    setMessage("");
    try {
      const updated = await put<ProxyConfig>("/api/system/proxy", {
        mode: "all",
        poolTarget: Number(poolTarget),
        authKey: authKey || null,
        authPwd: authPwd || null,
      });
      setAuthKey("");
      setAuthPwd("");
      setShowAuthKey(false);
      setShowAuthPwd(false);
      setMessageTone("success");
      setMessage("代理配置已保存；余额、地区资源和弹性提取已统一切换到当前 Authkey");
      queryClient.removeQueries({ queryKey: ["proxy-resources"] });
      await config.refetch();
      if (updated.authKeyMasked) await balance.refetch();
    } catch (error) {
      setMessageTone("danger");
      setMessage(error instanceof Error ? error.message : "保存代理配置失败");
    } finally {
      setBusy(false);
    }
  }

  async function refreshBalance() {
    setMessage("");
    const result = await balance.refetch();
    if (result.error) {
      setMessageTone("danger");
      setMessage(result.error instanceof Error ? result.error.message : "读取剩余额度失败");
      return;
    }
    setMessageTone("success");
    setMessage("青果可提取额度已刷新");
  }

  const balanceError = balance.error instanceof Error ? balance.error.message : "";

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>代理 IP 池</h1>
          <p>青果短效代理 · 弹性提取</p>
        </div>
        <div className="heading-actions">
          <Button
            variant="secondary"
            onClick={refreshBalance}
            disabled={!config.data?.authKeyMasked || balance.isFetching}
          >
            {balance.isFetching ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />}
            刷新余量
          </Button>
        </div>
      </section>
      {message && <div className={`notice-bar ${messageTone === "danger" ? "notice-danger" : messageTone === "warning" ? "notice-warning" : ""}`}>{message}</div>}
      <section className="proxy-metric-strip">
        <div className="proxy-balance-metric">
          <span>青果剩余</span>
          <strong>{balance.data?.balance ?? "-"}</strong>
          <small>可提取 IP</small>
        </div>
        <div>
          <span>代理覆盖</span>
          <strong>全部请求</strong>
          <small>共用一套认证账密</small>
        </div>
        <div>
          <span>池内可用</span>
          <strong>{config.data?.pool.available ?? 0}</strong>
          <small>
            {config.data?.pool.extractBlocked
              ? `提取暂停：${config.data.pool.blockedCode || "请检查配置"}`
              : `当前窗口可用 ${config.data?.pool.remainingRequests ?? 0} 次请求`}
          </small>
        </div>
        <div>
          <span>系统今日提取</span>
          <strong>{config.data?.pool.extractedToday ?? 0}</strong>
          <small>
            参考预算 {config.data?.pool.dailyIpBudget ?? 1900}；按青果实际余量继续提取
          </small>
        </div>
      </section>
      {(balanceError || config.error) && (
        <div className="inline-error">
          {balanceError || (config.error instanceof Error ? config.error.message : "读取代理配置失败")}
        </div>
      )}
      {config.data?.pool.extractBlocked && (
        <div className="notice-bar notice-danger">
          {config.data.pool.blockedReason || "代理提取已暂停"}
          {config.data.pool.blockedUntil ? `；${formatDate(config.data.pool.blockedUntil)} 后自动重试` : ""}
        </div>
      )}
      {balance.data && balance.data.balance < 1000 && (
        <div className="notice-bar notice-warning">青果当日可提取余量不足 1,000 个，请结合剩余任务核对用量。</div>
      )}
      {!!config.data?.pool.providerErrorsToday?.length && (
        <section className="settings-section">
          <h2>今日提取异常</h2>
          <table><thead><tr><th>地区</th><th>原因</th><th>次数</th></tr></thead><tbody>
            {config.data.pool.providerErrorsToday.map((item) => <tr key={`${item.areaCode}:${item.code}`}>
              <td>{item.areaName || item.areaCode || "不限地区"}</td>
              <td>{item.code === "NO_RESOURCE_FOUND" ? "地区资源不足" : item.code === "EXTRACT_LIMIT_EXCEEDED" ? "服务商提取配额耗尽" : item.code}</td>
              <td>{item.count}</td>
            </tr>)}
          </tbody></table>
        </section>
      )}
      {!!config.data?.pool.usageHistory?.length && (
        <section className="settings-section">
          <h2>每日用量（北京时间）</h2>
          <table><thead><tr><th>业务日</th><th>提取 IP</th><th>代理请求</th></tr></thead><tbody>
            {[...config.data.pool.usageHistory].reverse().map((item) => <tr key={item.date}>
              <td>{item.date}</td><td>{item.extractedIPs}</td><td>{item.proxiedRequests}</td>
            </tr>)}
          </tbody></table>
        </section>
      )}
      <section className="settings-grid proxy-settings-grid">
        <div className="settings-section wide">
          <div className="settings-section-heading">
            <div>
              <h2>当前配置</h2>
              <p>{config.data?.extractUrlMasked || "尚未配置提取 API"}</p>
            </div>
            <Badge tone={config.data?.mode === "off" ? "neutral" : config.data?.configured ? "success" : "danger"}>
              {config.data?.mode === "off" ? "已关闭" : config.data?.configured ? "运行中" : "待完善"}
            </Badge>
          </div>
          <dl className="settings-list proxy-current-list">
            <div><dt>单 IP 频率</dt><dd>{config.data?.pool.requestsPerMinute ?? 20} 次/分钟</dd></div>
            <div><dt>请求间隔</dt><dd>{config.data?.pool.minIntervalSeconds ?? 2.5}-{config.data?.pool.maxIntervalSeconds ?? 3} 秒</dd></div>
            <div><dt>今日代理请求</dt><dd>{config.data?.pool.proxiedRequestsToday ?? 0} 次</dd></div>
            <div><dt>Authkey / 业务 Key</dt><dd>{config.data?.authKeyMasked || "未配置"}</dd></div>
            <div><dt>Authpwd</dt><dd>{config.data?.authPwdMasked || "未配置"}</dd></div>
            <div><dt>配置来源</dt><dd>{config.data?.source === "database" ? "运营台配置" : "服务器环境变量"}</dd></div>
            <div><dt>最近切换</dt><dd>{formatDate(config.data?.updatedAt)}</dd></div>
            <div><dt>余额请求 ID</dt><dd>{balance.data?.requestId || "-"}</dd></div>
            <div><dt>余额查询时间</dt><dd>{formatDate(balance.data?.checkedAt)}</dd></div>
          </dl>
        </div>
        <div className="settings-section wide">
          <div className="settings-section-heading">
            <div><h2>切换配置</h2><p>敏感字段留空时保持当前值，保存后立即清空输入。</p></div>
          </div>
          <div className="proxy-form-grid">
            <Field label="单次池目标" hint="建议保持 1，减少短效 IP 到期浪费">
              <Input type="number" min="1" max="100" value={poolTarget} onChange={(event) => setPoolTarget(event.target.value)} />
            </Field>
            <Field label="Authkey" hint={config.data?.authKeyMasked || "未配置"}>
              <div className="secret-input">
                <Input type={showAuthKey ? "text" : "password"} value={authKey} onChange={(event) => setAuthKey(event.target.value)} placeholder={config.data?.authKeyMasked ? "留空表示保持不变" : "请输入 Authkey"} autoComplete="off" />
                <IconButton className="secret-input-toggle" label={showAuthKey ? "隐藏 Authkey" : "显示 Authkey"} onClick={() => setShowAuthKey((value) => !value)}>
                  {showAuthKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </IconButton>
              </div>
            </Field>
            <Field label="Authpwd" hint={config.data?.authPwdMasked || "未配置"}>
              <div className="secret-input">
                <Input type={showAuthPwd ? "text" : "password"} value={authPwd} onChange={(event) => setAuthPwd(event.target.value)} placeholder={config.data?.authPwdConfigured ? "留空表示保持不变" : "请输入 Authpwd"} autoComplete="new-password" />
                <IconButton className="secret-input-toggle" label={showAuthPwd ? "隐藏 Authpwd" : "显示 Authpwd"} onClick={() => setShowAuthPwd((value) => !value)}>
                  {showAuthPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </IconButton>
              </div>
            </Field>
          </div>
          <div className="settings-actions">
            <Button onClick={saveProxy} disabled={busy || !poolTarget || Number(poolTarget) < 1 || Number(poolTarget) > 100}>
              {busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />}
              {busy ? "保存中" : "保存配置"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeishuManagerEditor({
  config,
  onSaved,
  onMessage,
}: {
  config: FeishuManagerConfig;
  onSaved: () => Promise<unknown>;
  onMessage: (message: string) => void;
}) {
  const [mode, setMode] = useState(config.mode);
  const [groupName, setGroupName] = useState(config.groupName);
  const [webhook, setWebhook] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMode(config.mode);
    setGroupName(config.groupName);
    setWebhook("");
    setSecret("");
  }, [config]);

  async function save() {
    setBusy(true);
    try {
      await put(`/api/system/feishu/users/${config.userId}`, {
        mode,
        groupName,
        webhook: webhook || null,
        secret: secret || null,
      });
      setWebhook("");
      setSecret("");
      onMessage(`${config.username} 的飞书配置已保存`);
      await onSaved();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "保存管理员飞书配置失败");
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      await post(`/api/system/feishu/users/${config.userId}/test`);
      onMessage(`${config.username} 的飞书测试通知发送成功`);
      await onSaved();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "发送管理员飞书测试通知失败");
    } finally {
      setBusy(false);
    }
  }

  const status = mode === "disabled"
    ? { tone: "neutral" as const, label: "不发送" }
    : mode === "inherit"
      ? { tone: config.effectiveEnabled ? "success" as const : "warning" as const, label: "使用默认" }
      : { tone: config.effectiveEnabled ? "success" as const : "warning" as const, label: config.effectiveEnabled ? "独立机器人" : "待配置" };

  return (
    <div className="feishu-manager-row">
      <div className="feishu-manager-summary">
        <div>
          <strong>{config.displayName || config.username}</strong>
          <span>{config.username} · {config.userEnabled ? "账号启用" : "账号停用"}</span>
        </div>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      <div className="feishu-plan-scope">
        <span>管理线路任务 <strong>{config.planCount}</strong></span>
        <p>{config.planNames.length ? config.planNames.join("、") : "暂无每日线路任务"}</p>
      </div>
      <div className="feishu-manager-controls">
        <Field label="发送方式">
          <Select value={mode} onChange={(event) => setMode(event.target.value as FeishuManagerConfig["mode"])}>
            <option value="inherit">使用系统默认机器人</option>
            <option value="custom">使用独立机器人</option>
            <option value="disabled">不发送飞书</option>
          </Select>
        </Field>
        {mode === "custom" && (
          <>
            <Field label="飞书群名称"><Input value={groupName} onChange={(event) => setGroupName(event.target.value)} /></Field>
            <Field label="机器人 Webhook" hint={config.webhookMasked || "保存后显示脱敏值"}><Input type="password" value={webhook} onChange={(event) => setWebhook(event.target.value)} placeholder={config.configured ? "留空表示保持不变" : "请输入 Webhook"} /></Field>
            <Field label="签名密钥"><Input type="password" value={secret} onChange={(event) => setSecret(event.target.value)} placeholder={config.configured ? "留空表示保持不变" : "请输入签名密钥"} /></Field>
          </>
        )}
      </div>
      <div className="feishu-manager-footer">
        <div className="feishu-manager-health">
          <span>待发送 {config.health.pending}</span>
          <span>死信 {config.health.dead}</span>
          <span>最近发送 {formatDate(config.health.lastSentAt)}</span>
        </div>
        <div className="settings-actions">
          <Button onClick={save} disabled={busy}>{busy ? "处理中..." : "保存"}</Button>
          <Button variant="secondary" onClick={test} disabled={busy || mode !== "custom" || !config.configured}>发送测试通知</Button>
        </div>
      </div>
      {config.health.channelHealth?.status === "unavailable" && <div className="notice-bar notice-warning">
        {config.health.channelHealth.reason}。已暂停该通道发送，更新凭据后恢复。
      </div>}
      {config.lastTestError && <div className="inline-error">最近测试失败：{config.lastTestError}</div>}
    </div>
  );
}

export function SettingsPage() {
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => api<any>("/api/health"),
  });
  const feishu = useQuery({
    queryKey: ["feishu-config"],
    queryFn: () => api<FeishuConfig>("/api/system/feishu"),
  });
  const [password, setPassword] = useState("");
  const [retention, setRetention] = useState("30");
  const [message, setMessage] = useState("");
  const [feishuEnabled, setFeishuEnabled] = useState(false);
  const [feishuGroup, setFeishuGroup] = useState("");
  const [feishuWebhook, setFeishuWebhook] = useState("");
  const [feishuSecret, setFeishuSecret] = useState("");
  const [feishuBusy, setFeishuBusy] = useState(false);
  const [feishuCreateOpen, setFeishuCreateOpen] = useState(false);
  const [newFeishuUserId, setNewFeishuUserId] = useState("");
  const [newFeishuGroup, setNewFeishuGroup] = useState("");
  const [newFeishuWebhook, setNewFeishuWebhook] = useState("");
  const [newFeishuSecret, setNewFeishuSecret] = useState("");
  const [newFeishuBusy, setNewFeishuBusy] = useState(false);
  useEffect(() => {
    if (!feishu.data) return;
    setFeishuEnabled(feishu.data.enabled);
    setFeishuGroup(feishu.data.groupName);
  }, [feishu.data]);
  async function backup() {
    const response = await fetch("/api/system/backup", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      setMessage("备份失败，请检查管理员密码");
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ydb-web-backup.zip";
    link.click();
    URL.revokeObjectURL(url);
    setMessage("备份已生成并下载");
  }
  async function cleanup() {
    try {
      const result = await post<any>("/api/system/cleanup", {
        adminPassword: password,
        retentionDays: Number(retention),
      });
      setMessage(`已清理 ${result.deletedDirectories} 个证据目录`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "清理失败");
    }
  }
  async function saveFeishu() {
    setFeishuBusy(true);
    try {
      await put("/api/system/feishu", {
        enabled: feishuEnabled,
        groupName: feishuGroup,
        webhook: feishuWebhook || null,
        secret: feishuSecret || null,
      });
      setFeishuWebhook("");
      setFeishuSecret("");
      setMessage(feishuEnabled ? "飞书机器人配置已保存并启用" : "飞书机器人配置已保存，当前未启用");
      await Promise.all([feishu.refetch(), health.refetch()]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存飞书配置失败");
    } finally {
      setFeishuBusy(false);
    }
  }
  async function testFeishu() {
    setFeishuBusy(true);
    try {
      await post("/api/system/feishu/test");
      setMessage("飞书测试通知发送成功");
      await feishu.refetch();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "飞书测试通知失败");
    } finally {
      setFeishuBusy(false);
    }
  }
  const managers = feishu.data?.managers ?? [];
  const independentManagers = managers.filter((manager) => manager.mode === "custom");
  const availableManagers = managers.filter((manager) => manager.mode !== "custom");
  const selectedNewManager = managers.find((manager) => manager.userId === newFeishuUserId);

  function openFeishuCreate() {
    setNewFeishuUserId(availableManagers[0]?.userId ?? "");
    setNewFeishuGroup("");
    setNewFeishuWebhook("");
    setNewFeishuSecret("");
    setFeishuCreateOpen(true);
  }

  async function createManagerFeishu() {
    if (!newFeishuUserId) return;
    setNewFeishuBusy(true);
    try {
      await put(`/api/system/feishu/users/${newFeishuUserId}`, {
        mode: "custom",
        groupName: newFeishuGroup,
        webhook: newFeishuWebhook,
        secret: newFeishuSecret,
      });
      const manager = managers.find((item) => item.userId === newFeishuUserId);
      setFeishuCreateOpen(false);
      setMessage(`${manager?.username || "管理员"} 的独立飞书机器人已新增`);
      await feishu.refetch();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "新增飞书机器人失败");
    } finally {
      setNewFeishuBusy(false);
    }
  }
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <h1>系统设置</h1>
          <p>查看本机运行参数，备份数据库与清理历史证据。</p>
        </div>
      </section>
      {message && <div className="notice-bar">{message}</div>}
      <section className="settings-grid">
        <div className="settings-section">
          <h2>运行状态</h2>
          <dl className="settings-list">
            <div>
              <dt>后端版本</dt>
              <dd>{health.data?.version ?? "-"}</dd>
            </div>
            <div>
              <dt>业务时区</dt>
              <dd>{health.data?.businessTimezone ?? "-"}</dd>
            </div>
            {health.data?.storage && <div>
              <dt>可用磁盘 / 业务预留</dt>
              <dd>{(health.data.storage.availableBytes / 1024**3).toFixed(1)} / {(health.data.storage.reserveBytes / 1024**3).toFixed(1)} GiB
                <Badge tone={health.data.storage.status === "ok" ? "success" : "warning"}>{health.data.storage.status === "ok" ? "充足" : health.data.storage.status === "critical" ? "暂停新接单" : "容量预警"}</Badge>
              </dd>
            </div>}
            {health.data?.storage?.hoursUntilReserve != null && <div>
              <dt>预计触及预留</dt><dd>约 {health.data.storage.hoursUntilReserve} 小时（按近期净消耗估计）</dd>
            </div>}
            <div>
              <dt>写操作并发</dt>
              <dd>{health.data?.writeConcurrency ?? "-"}</dd>
            </div>
            <div>
              <dt>服务状态</dt>
              <dd>
                <Badge tone={health.data?.ok ? "success" : "danger"}>
                  {health.data?.ok ? "正常" : "异常"}
                </Badge>
              </dd>
            </div>
          </dl>
        </div>
        <div className="settings-section">
          <h2>证据与备份</h2>
          <Field label="管理员密码">
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          <div className="settings-actions">
            <Button
              variant="secondary"
              onClick={backup}
              disabled={password.length < 8}
            >
              <FileDown size={16} />
              导出完整备份
            </Button>
          </div>
          <div className="retention-row">
            <Field label="证据保留天数">
              <Input
                type="number"
                min="1"
                value={retention}
                onChange={(event) => setRetention(event.target.value)}
              />
            </Field>
            <Button
              variant="danger"
              onClick={cleanup}
              disabled={password.length < 8}
            >
              <Trash2 size={16} />
              清理历史证据
            </Button>
          </div>
        </div>
        <div className="settings-section wide feishu-settings">
          <div className="settings-section-heading">
            <div><h2>系统默认机器人</h2><p>负责系统告警，以及选择“使用系统默认”的管理员线路任务。</p></div>
            <Badge tone={feishu.data?.enabled ? "success" : feishu.data?.configured ? "warning" : "neutral"}>
              {feishu.data?.enabled ? "已启用" : feishu.data?.configured ? "已配置未启用" : "未配置"}
            </Badge>
          </div>
          <div className="feishu-status-strip">
            <span>待发送 <strong>{feishu.data?.health?.pending ?? 0}</strong></span>
            <span>死信 <strong>{feishu.data?.health?.dead ?? 0}</strong></span>
            <span>未收口日报 <strong>{feishu.data?.health?.openDailyReports ?? 0}</strong></span>
            <span>最近发送 <strong>{formatDate(feishu.data?.health?.lastSentAt)}</strong></span>
          </div>
          {feishu.data?.health?.channelHealth?.status === "unavailable" && <div className="notice-bar notice-warning">
            {feishu.data.health.channelHealth.reason}。通道已暂停自动发送，更新凭据后立即恢复；下次探测 {formatDate(feishu.data.health.channelHealth.retryAt)}。
          </div>}
          <div className="feishu-form-grid">
            <Field label="飞书群名称"><Input value={feishuGroup} onChange={(event) => setFeishuGroup(event.target.value)} placeholder="例如：肇庆怡宝运营通知" /></Field>
            <Field label="机器人 Webhook" hint={feishu.data?.webhookMasked || "保存后仅显示脱敏值"}><Input type="password" value={feishuWebhook} onChange={(event) => setFeishuWebhook(event.target.value)} placeholder={feishu.data?.configured ? "留空表示保持不变" : "https://open.feishu.cn/open-apis/bot/..."} /></Field>
            <Field label="签名密钥"><Input type="password" value={feishuSecret} onChange={(event) => setFeishuSecret(event.target.value)} placeholder={feishu.data?.configured ? "留空表示保持不变" : "请输入机器人签名密钥"} /></Field>
          </div>
          <label className="check-row"><input type="checkbox" checked={feishuEnabled} onChange={(event) => setFeishuEnabled(event.target.checked)} /><span>启用飞书通知</span></label>
          {feishu.data?.lastTestError && <div className="inline-error">最近测试失败：{feishu.data.lastTestError}</div>}
          <div className="settings-actions">
            <Button onClick={saveFeishu} disabled={feishuBusy}>{feishuBusy ? "处理中..." : "保存配置"}</Button>
            <Button variant="secondary" onClick={testFeishu} disabled={feishuBusy || !feishu.data?.configured}>发送测试通知</Button>
          </div>
          <div className="feishu-manager-heading">
            <div>
              <h2>管理员线路机器人</h2>
              <p>通知按每日计划的授权管理员独立聚合和发送。</p>
            </div>
            <div className="feishu-manager-heading-actions">
              <Badge tone="info">{independentManagers.length} 个独立机器人</Badge>
              <Button onClick={openFeishuCreate} disabled={!availableManagers.length}>
                <Plus size={16} />新增机器人
              </Button>
            </div>
          </div>
          <div className="feishu-manager-list">
            {managers.map((manager) => (
              <FeishuManagerEditor
                key={manager.userId}
                config={manager}
                onSaved={() => feishu.refetch()}
                onMessage={setMessage}
              />
            ))}
          </div>
        </div>
        <div className="settings-section wide">
          <h2>安全边界</h2>
          <div className="security-list">
            <div>
              <ShieldAlert size={18} />
              <span>
                <strong>真实业务操作需要二次确认</strong>
                <small>
                  预检快照 5 分钟后自动失效，确认弹窗会完整展示运单和计划明细；跨日紧急卸货仍验证密码。
                </small>
              </span>
            </div>
            <div>
              <KeyRound size={18} />
              <span>
                <strong>账号凭据加密保存</strong>
                <small>
                  优先使用 macOS Keychain 主密钥，数据库不保存明文密码。
                </small>
              </span>
            </div>
            <div>
              <CircleHelp size={18} />
              <span>
                <strong>等待中的运单不会自动卸货</strong>
                <small>达到最早时间后仅生成通知，需要重新预检和批准。</small>
              </span>
            </div>
          </div>
        </div>
      </section>
      <Dialog
        open={feishuCreateOpen}
        onOpenChange={setFeishuCreateOpen}
        title="新增飞书机器人"
        description="选择管理员后，该管理员负责的线路通知将发送到此机器人。"
      >
        <Field label="关联管理员">
          <Select value={newFeishuUserId} onChange={(event) => setNewFeishuUserId(event.target.value)}>
            {availableManagers.map((manager) => (
              <option value={manager.userId} key={manager.userId}>
                {manager.displayName || manager.username}（{manager.username}）
              </option>
            ))}
          </Select>
        </Field>
        {selectedNewManager && (
          <div className="feishu-create-scope">
            <span>管理线路任务 <strong>{selectedNewManager.planCount}</strong></span>
            <p>{selectedNewManager.planNames.length ? selectedNewManager.planNames.join("、") : "暂无每日线路任务"}</p>
          </div>
        )}
        <Field label="飞书群名称">
          <Input value={newFeishuGroup} onChange={(event) => setNewFeishuGroup(event.target.value)} placeholder="例如：南宁线路通知群" />
        </Field>
        <Field label="机器人 Webhook">
          <Input type="password" value={newFeishuWebhook} onChange={(event) => setNewFeishuWebhook(event.target.value)} placeholder="https://open.feishu.cn/open-apis/bot/..." />
        </Field>
        <Field label="签名密钥">
          <Input type="password" value={newFeishuSecret} onChange={(event) => setNewFeishuSecret(event.target.value)} placeholder="请输入机器人签名密钥" />
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setFeishuCreateOpen(false)}>取消</Button>
          <Button
            onClick={createManagerFeishu}
            disabled={newFeishuBusy || !newFeishuUserId || !newFeishuWebhook.trim() || !newFeishuSecret.trim()}
          >
            {newFeishuBusy ? "新增中..." : "新增并启用"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
